import { goodbyeFailureDescription, readGoodbyeSendResult, type GoodbyeSendResult } from '@/utils/goodbye-letter/sendResult';
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  AlertTriangle,
  Trash2,
  FileDown,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { LoanAutocomplete, EncompassLoan } from '@/components/broker-recon/LoanAutocomplete';
import { ServicingTab, type ServicerSelection } from './ServicingTab';
import { useAuth } from '@/contexts/useAuth';
import { createComponentLogger } from '@/utils/logger';
import { bankerReconService } from '@/services/bankerReconService';
import { refreshBankerReconFees, type ReconFeeContext } from '@/services/bankerReconFeeService';
import { readReconFeeAccounting, reconcileFeePaymentLedger, type ReconFeeAccountingSnapshot } from '@/utils/banker-recon/feePaymentLedger';
import { useVolumeBonusPreview } from './useVolumeBonusPreview';
import { UserService } from '@/services/userService';
import { getPricingItemsForRecon } from '@/services/loanPricingService';
import {
  fetchConcessions,
  sumActiveConcessionPoints,
  sumActiveConcessionDollars
} from '@/services/lendwiseConcessionService';
import {
  type LineItemCategory,
  type BankerReconStatus,
  type BankerRecon,
  type LineItemSource,
  type DraftOverrideEntry,
  getConfiguredOverrides,
  getActiveOverrides
} from '@/types/banker-recon';
import type { User } from '@/types/user';
import { showBankerReconToast } from './bankerReconToast';
import { supabase } from '@/utils/supabase';
import { downloadBankerReconPDF } from '@/utils/banker-recon/pdfGenerator';
import {
  calculatePaymentCount,
  calculateMonthlyPI,
  generatePaymentSchedule,
  calculateInvestorWirePrincipalReduction
} from '@/types/servicing';
import { buildLoPayoutLedger } from '@/utils/banker-recon/loPayoutLedger';
import {
  computeBankedRecon,
  getLoanRevenueReimbursementBasis,
  type BankedReconResult
} from '@/utils/banker-recon/bankedNetProfit';
import { calculateInvestorWireAudit } from '@/utils/banker-recon/wireReconciliation';
import {
  buildFhaUfmipBridge,
  EMPTY_FHA_UFMIP_SOURCE,
  fhaUfmipSourceFromRecon,
  type FhaUfmipBridge,
  type FhaUfmipFlowMode,
  type FhaUfmipSourceSnapshot
} from '@/utils/banker-recon/fhaUfmip';
import {
  computeReconLoProfitability,
  isAllowedProfitabilityRoutingTarget,
  ROUTABLE_CREDIT_LABELS,
  ROUTABLE_CREDIT_TYPES,
  type ProfitabilityRouting,
  type ProfitabilityRoutingTarget,
  type ReconLoProfitabilityResult,
  type RoutableCreditType
} from '@/utils/banker-recon/loProfitability';
import { getLoAvailableProfitability } from '@/services/myCommissionService';
import {
  deriveProfitabilityRouting,
  stripProfitabilityRoutedLoCompReduction
} from './profitabilityRoutingDerive';
import { extractEncompassLoCompBps } from '@/utils/loCompDerived';
import {
  deriveLendwiseProcessingUwScalars,
  findConventionalMiPremiumFee,
  isMiPremiumLineType,
  mergeAutoCaptureRows,
  MI_PREMIUM_REMITTANCE_LINE_TYPE,
  miPremiumExpenseDescription,
  syncMiPremiumRow
} from '@/utils/banker-recon/leFeeCanonical';
import {
  isManualAdjustmentLineType,
  manualAdjustmentFromLineItem,
  manualAdjustmentToLineItem,
  type ManualReconAdjustment
} from '@/utils/banker-recon/manualAdjustments';
import { normalizeRevenueStateForDraft } from '@/utils/banker-recon/retainedRevenueNormalization';
import {
  canonicalInvestorCostBaseType,
  extractBuydownPassThroughAmount,
  isBuydownLineType,
  normalizeBuydownPassThroughCostsForDraft,
  normalizeExtractedFieldsForBuydown
} from '@/utils/banker-recon/buydownPassThrough';
import {
  buildFinalizationToast,
  formatLetterNotice,
  letterNoticeHeadline,
  type LetterNotice
} from './buildFinalizationToast';
import {
  describeGoodbyeSendResult,
  shouldReportGoodbyeOutcome,
  type GoodbyeOutcome
} from '@/components/servicing/goodbyeLetterErrors';
import { replaceDocOrderReconRows } from './docOrderReconFeed';
import { buildBankerReconFinalizationFieldWrites, type EncompassFieldWrite } from './finalizationFieldWrites';
import {
  applyDeliveryLockToLoan,
  buildDeliveryLockSourceSnapshot,
  getActiveDeliveryLockSelection,
  getPricingSourceType,
  type DeliveryLockSelection
} from '@/services/deliveryLockService';
import {
  applyLoCompReductionToCompensation,
  buildLoCompReductionImportMetadata,
  LO_COMP_REDUCTION_TYPE,
  normalizeLoCompReductionForRecon,
  type LoCompReductionImportMetadata
} from './loCompReduction';

// Tab components
import { BankerReconSummary } from './BankerReconSummary';
import { WarehouseReconTab } from './WarehouseReconTab';
import { InvestorAdviceTab } from './InvestorAdviceTab';
import { FinalizedReconsTable } from './FinalizedReconsTable';

// Shared types
import {
  type KeyDates,
  type CostsState,
  type RevenueState,
  type PricingState,
  type CompensationState,
  type CompensationDeductionType,
  type UploadedDocument,
  type BankerDocumentType,
  type PurchaseAdjustment,
  type AutoFillExtractionCache,
  type AutoFillReviewMark,
  type ReconDocumentAutoFill,
  toNumber,
  computeInvestorLockPrice,
  sumProfitMargin,
  BANKER_EXTRACTOR_VERSION,
  DEFAULT_KEY_DATES,
  DEFAULT_COSTS,
  DEFAULT_REVENUE,
  DEFAULT_COMPENSATION,
  DEFAULT_PRICING
} from './types';
import { effectiveWarehouseDays } from '@/utils/banker-recon/warehouseDays';
import { AutoFillReviewDialog } from './AutoFillReviewDialog';
import {
  buildAutoFillRows,
  applyAutoFillRows,
  type AutoFillCurrentState,
  type AutoFillRow
} from './autoFillFromDocuments';

const log = createComponentLogger('BankerRecon', 'banker-recon');

// ============================================================================
// LOCAL TYPES (not shared)
// ============================================================================

interface ValidationIssue {
  type: 'warning' | 'error' | 'info';
  message: string;
}

// Save state enum for cleaner state management
type SaveState = 'idle' | 'saving' | 'success';

type PendingLoanAction = { type: 'clear' } | { type: 'switch'; loan: EncompassLoan };

// 'stale' = a newer load took over before this one finished.
type LoadLoanOutcome = 'loaded' | 'stale' | 'failed';

interface LoadLoanOptions {
  /**
   * Reload after Reopen: strict recon and line-item reads, the reopened draft is
   * confirmed before anything is shown, and the source row is freshly fetched
   * (never already overlaid by a delivery lock).
   */
  reloadAfterReopen?: { expectedReconId: string };
}

interface TerminalAccountingValues {
  loPayout: number | null;
  companyRevenue: number | null;
  gosAmount: number | null;
  netProfit: number | null;
}

interface SavedDraftIdentity {
  volumeBonusReviewToken?: string;
  volumeBonusInstallment?: { amount: number; number: number; total: number };
  id: string;
  updatedAt: string;
  fhaUfmipSignature: string | null;
  fhaUfmipFlowMode: FhaUfmipFlowMode;
  fhaUfmipFinalizationErrors: string[];
}

type BuiltLineItem = {
  fee_source?: import('@/utils/banker-recon/feePaymentLedger').ReconFeeSource | null;
  category: LineItemCategory;
  line_type: string;
  description: string;
  amount: number;
  display_order: number;
  reimbursable?: boolean;
  nets_to_warehouse_advance?: boolean | null;
  /** Retained-at-warehouse amount excluded from P&L (warehouse basis = amount + this). */
  warehouse_retained_amount?: number | null;
  source?: LineItemSource;
};

type EncompassLoanWithBuydown = EncompassLoan & {
  banker_recon_buydown_pass_through_amount?: number;
};

function getBuydownPassThroughAmountFromLoan(loan: EncompassLoan | null | undefined): number {
  const amount = (loan as EncompassLoanWithBuydown | null | undefined)?.banker_recon_buydown_pass_through_amount;
  return typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
}

function withBuydownPassThroughAmount(loan: EncompassLoan, amount: number): EncompassLoan {
  return { ...loan, banker_recon_buydown_pass_through_amount: amount } as EncompassLoanWithBuydown;
}

function resolveRevenueStateAction(
  next: React.SetStateAction<RevenueState>,
  prev: RevenueState
): RevenueState {
  return typeof next === 'function'
    ? (next as (prevState: RevenueState) => RevenueState)(prev)
    : next;
}

type MilestoneStep = {
  milestoneName: string;
  milestoneDate?: string;
  moveToFolder?: string;
  sourceType?: string;
  sourceId?: string;
  fieldWrites?: EncompassFieldWrite[];
  requireFieldWritesSuccess?: boolean;
  syncInvestorLoanCopies?: boolean;
};

interface LiveLenderCreditValues {
  lenderCredit: number;
  cureAppliedLenderCredit: number;
}

function parseCurrencyLike(value: unknown): number {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  const cleaned = String(value).replace(/[$,\s]/g, '');
  return toNumber(cleaned);
}

function getLiveLenderCreditValuesFromLoan(loan: Partial<EncompassLoan> | null | undefined): LiveLenderCreditValues {
  const leFields = (loan as Record<string, unknown> | null | undefined)?.le_fields as Record<string, unknown> | null | undefined;
  const field4794Value = toNumber((loan as Record<string, unknown> | null | undefined)?.trans_details_non_specific_lender_credit);
  const lenpccValue = parseCurrencyLike(leFields?.LENPCC);
  return {
    lenderCredit: field4794Value + lenpccValue,
    cureAppliedLenderCredit: toNumber((loan as Record<string, unknown> | null | undefined)?.cure_applied_lender_credit)
  };
}

function clampCompensationDeductionsToLiveCredits(
  compensation: CompensationState,
  liveCredits: LiveLenderCreditValues
): CompensationState {
  const deductions = (compensation.deductions ?? [])
    .map(deduction => {
      if (deduction.type === LO_COMP_REDUCTION_TYPE) {
        return deduction.readOnly && deduction.deductionAmount > 0 ? deduction : null;
      }

      const sourceAmount = deduction.type === 'lender_credit'
        ? liveCredits.lenderCredit
        : deduction.type === 'cure_applied_lender_credit'
          ? liveCredits.cureAppliedLenderCredit
          : 0;
      if (sourceAmount <= 0) {
        return null;
      }
      return {
        ...deduction,
        sourceAmount,
        deductionAmount: Math.min(Math.max(0, deduction.deductionAmount), sourceAmount)
      };
    })
    .filter((deduction): deduction is CompensationState['deductions'][number] => deduction !== null);

  return { ...compensation, deductions };
}

function normalizeDraftOverrides(value: unknown): DraftOverrideEntry[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index): DraftOverrideEntry | null => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const raw = entry as Record<string, unknown>;
      const rawBps = raw.bps;
      const numericBps = rawBps === null || rawBps === undefined || rawBps === ''
        ? null
        : toNumber(rawBps, Number.NaN);

      return {
        id: typeof raw.id === 'string' && raw.id ? raw.id : `override-${index}`,
        recipientId: typeof raw.recipientId === 'string' && raw.recipientId ? raw.recipientId : null,
        recipientName: typeof raw.recipientName === 'string' && raw.recipientName ? raw.recipientName : null,
        bps: numericBps !== null && Number.isFinite(numericBps)
          ? Math.round(Math.max(0, Math.min(60, numericBps)) * 10000) / 10000
          : null
      };
    })
    .filter((entry): entry is DraftOverrideEntry => entry !== null && (entry.recipientId !== null || entry.bps !== null));
}

// Sentinel identifying handleFinalize's OWN preliminary save: external saves are
// rejected while a finalize is in flight, but the finalize's save must pass. UI
// click handlers pass a MouseEvent as the first arg, which never equals this.
const FINALIZE_SAVE_CALLER = Symbol('banker-recon-finalize-save');

function buildLineItemsFromState(
  costs: CostsState,
  revenue: RevenueState,
  compensation: CompensationState,
  // line_types filled by document auto-fill — tagged source:'extracted' for provenance (save path only)
  extractedLineTypes?: Set<string>,
  // line_types seeded from a completed Doc Order — tagged source:'doc_order' for provenance (save path only)
  docOrderLineTypes?: Set<string>,
  // Closing Disclosure payee of the MI premium (e.g. "MGIC") for the derived expense description
  miPremiumPayee?: string | null,
  // Reviewer-entered manual adjustments; serialized once here, never via revenue/costs state
  manualAdjustments: readonly ManualReconAdjustment[] = []
): BuiltLineItem[] {
  const lineItems: BuiltLineItem[] = [];
  let order = 0;

  // Helper: safely coerce to number and validate (strips commas from formatted numbers)
  const toValidAmount = (value: unknown): number | null => {
    const cleaned = typeof value === 'string' ? value.replace(/,/g, '') : value;
    const num = Number(cleaned);
    return Number.isFinite(num) && num !== 0 ? num : null;
  };

  // Auto-fill provenance is keyed `${category}:${baseType}` (timestamp suffix stripped) so dynamic
  // investor-cost ids (`${type}_<ts>`) resolve to source:'extracted' the same way as stable warehouse ids.
  const isExtracted = (category: string, lineType: string, description?: string): boolean => {
    const baseType = category === 'investor_cost'
      ? canonicalInvestorCostBaseType(lineType, description)
      : lineType.replace(/_\d+$/, '');
    return !!extractedLineTypes?.has(`${category}:${baseType}`);
  };

  // Warehouse costs
  costs.warehouse.forEach(item => {
    const validAmount = toValidAmount(item.amount);
    if (validAmount !== null) {
      lineItems.push({
        category: 'warehouse_cost',
        line_type: item.id,
        description: item.description,
        amount: validAmount,
        ...(isExtracted('warehouse_cost', item.id) ? { source: 'extracted' as const } : {}),
        display_order: order++
      });
    }
  });

  // Investor costs
  costs.investor.forEach(item => {
    // Reserved: company concessions are NEVER P&L line items — the company's cost
    // is already absorbed in GOS (sale − full LO payout); a line here double-counts.
    if (item.id === 'lendwise_concession') {
      return;
    }
    const validAmount = toValidAmount(item.amount);
    if (validAmount !== null) {
      lineItems.push({
        category: 'investor_cost',
        line_type: item.id,
        description: item.description,
        amount: validAmount,
        ...(isExtracted('investor_cost', item.id, item.description) ? { source: 'extracted' as const } : {}),
        display_order: order++
      });
    }
  });

  // Encompass-authoritative lender credit as a cost
  const validLenderCredit = toValidAmount(revenue.lenderCredit);
  if (validLenderCredit !== null) {
    lineItems.push({
      category: 'lender_cost',
      line_type: 'lender_credit',
      description: 'Lender Credit Given',
      amount: validLenderCredit,
      source: 'encompass',
      nets_to_warehouse_advance: revenue.lenderCreditNetsToWarehouseAdvance,
      display_order: order++
    });
  }

  // Encompass-authoritative cure applied to lender credit.
  const validCureAppliedLenderCredit = toValidAmount(revenue.cureAppliedLenderCredit);
  if (validCureAppliedLenderCredit !== null) {
    lineItems.push({
      category: 'lender_cost',
      line_type: 'cure_applied_lender_credit',
      description: 'Cure Applied to Lender Credit',
      amount: validCureAppliedLenderCredit,
      source: 'encompass',
      nets_to_warehouse_advance: revenue.cureAppliedLenderCreditNetsToWarehouseAdvance,
      display_order: order++
    });
  }

  // "Warehouse withheld MI": the premium row above is REVENUE that nets to the warehouse advance; it
  // always carries this matching company EXPENSE — Lendwise pays the insurer outside the warehouse
  // wire. Net profit is unchanged. It must NOT be a warehouse or investor cost: either would distort
  // the wire audits (a warehouse charge would cancel the expected-wire increase).
  const miPremiumRow = revenue.loan.find(item => isMiPremiumLineType(item.id));
  const validMiPremium = miPremiumRow ? toValidAmount(miPremiumRow.amount) : null;
  if (validMiPremium !== null && validMiPremium > 0) {
    lineItems.push({
      category: 'lender_cost',
      line_type: MI_PREMIUM_REMITTANCE_LINE_TYPE,
      description: miPremiumExpenseDescription(miPremiumPayee),
      amount: validMiPremium,
      display_order: order++
    });
  }

  // LO compensation deductions
  (compensation.deductions ?? []).forEach(deduction => {
    if (deduction.deductionAmount > 0) {
      lineItems.push({
        category: 'lo_deduction',
        line_type: deduction.type,
        description: deduction.label,
        amount: deduction.deductionAmount,
        display_order: order++
      });
    }
  });

  // Loan revenue (exclude Encompass-sourced fees - they're read-only and always from live data)
  const ENCOMPASS_FEE_IDS = ['processing_fee', 'underwriting_fee', 'interest_charged'];
  revenue.loan.forEach(item => {
    if (ENCOMPASS_FEE_IDS.includes(item.id)) {
      return;
    }
    if (isBuydownLineType(item.id, item.description)) {
      return;
    }
    const validAmount = toValidAmount(item.amount);
    const warehouseRetained = toValidAmount(item.warehouse_retained_amount);
    // Keep a row if it has a revenue amount OR a warehouse-only retained amount / nets flag
    // (a 100%-seller-paid or third-party pass-through fee has amount 0 but still reduces the advance).
    if (validAmount !== null || warehouseRetained !== null || item.nets_to_warehouse_advance === true) {
      lineItems.push({
        category: 'loan_revenue',
        line_type: item.id,
        description: item.description,
        amount: validAmount ?? 0,
        warehouse_retained_amount: warehouseRetained ?? 0,
        nets_to_warehouse_advance: item.nets_to_warehouse_advance ?? null,
        display_order: order++,
        reimbursable: item.reimbursable || false,
        ...(docOrderLineTypes?.has(item.id) ? { source: 'doc_order' as const } : {}),
        ...(item.fee_source ? {
          fee_source: item.fee_source,
          source: item.fee_source.owner === 'doc_order' ? 'doc_order' as const : 'encompass' as const
        } : {})
      });
    }
  });

  // Investor revenue
  revenue.investor.forEach(item => {
    const validAmount = toValidAmount(item.amount);
    if (validAmount !== null) {
      lineItems.push({
        category: 'investor_revenue',
        line_type: item.id,
        description: item.description,
        amount: validAmount,
        display_order: order++
      });
    }
  });

  // Manual adjustments, one row each: income and pass-through → loan_revenue, expense → lender_cost
  // (see manualAdjustments.ts)
  manualAdjustments.forEach(entry => {
    lineItems.push({ ...manualAdjustmentToLineItem(entry), display_order: order++ });
  });

  return lineItems;
}

/** Human label for the auto-fill modal header, based on the classified document type. */
function humanizeDocType(t: BankerDocumentType): string {
  switch (t) {
    case 'combined':
      return 'Final Purchase Reconciliation';
    case 'purchase_advice':
      return 'Purchase Advice';
    case 'warehouse_reconciliation':
      return 'Warehouse Reconciliation';
    default:
      return 'Document';
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const BankerRecon: React.FC = () => {
  const { user } = useAuth();
  const toast = showBankerReconToast;

  // Saving state - using enum for cleaner state management
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [reconId, setReconId] = useState<string | null>(null);
  const [storedVolumeBonusAmount, setStoredVolumeBonusAmount] = useState(0);


  // Derived saving state for components
  const isSaving = saveState === 'saving';
  const saveSuccess = saveState === 'success';

  // Selected loan state - starts empty, populated when loan selected
  const [selectedLoan, setSelectedLoan] = useState<EncompassLoan | null>(null);
  const [reconInvestorId, setReconInvestorId] = useState<string | null>(null);
  const [servicerSelection, setServicerSelection] = useState<ServicerSelection>({});
  const [servicerMode, setServicerMode] = useState<string | null>(null);
  const [servicingRecordId, setServicingRecordId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setServicerMode(null);
    if (!reconInvestorId) {
      return;
    }
    void (async () => {
      const { data, error } = await supabase.from('investors').select('servicer_selection_mode').eq(
        'id',
        reconInvestorId
      ).maybeSingle();
      if (!cancelled && !error) {
        setServicerMode(data?.servicer_selection_mode ?? 'product_scope');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reconInvestorId]);
  const servicerSelectionRequired = servicerMode === 'per_loan' &&
    !servicerSelection.servicing_template_id;
  const [deliveryLockSelection, setDeliveryLockSelection] = useState<DeliveryLockSelection | null>(null);
  const [currentEncompassLockExpires, setCurrentEncompassLockExpires] = useState<string | null>(null);

  // Key dates state - use defaults from types.ts
  const [keyDates, setKeyDates] = useState<KeyDates>(DEFAULT_KEY_DATES);

  // Costs state - use defaults from types.ts
  const [costs, setCosts] = useState<CostsState>(DEFAULT_COSTS);

  // Revenue state - use defaults from types.ts
  const [revenue, setRevenueRaw] = useState<RevenueState>(DEFAULT_REVENUE);

  // Manual adjustments (company income/expense entered by the reviewer); kept apart from revenue
  // state so fee refreshes and draft normalizers never touch them
  const [manualAdjustments, setManualAdjustments] = useState<ManualReconAdjustment[]>([]);

  // Compensation state - use defaults from types.ts
  const [compensation, setCompensation] = useState<CompensationState>(DEFAULT_COMPENSATION);

  // Pricing performance state - use defaults from types.ts
  const [pricing, setPricing] = useState<PricingState>(DEFAULT_PRICING);

  // Purchase price adjustments (Mandatory Trade, Extensions, Suspense Fee, + synthetic auto_extracted_price)
  const [purchaseAdjustments, setPurchaseAdjustments] = useState<PurchaseAdjustment[]>([]);

  // Recon status
  const [status, setStatus] = useState<'draft' | 'finalized' | 'pushed'>('draft');
  const [terminalAccounting, setTerminalAccounting] = useState<TerminalAccountingValues | null>(null);
  const [lenderCreditTimingBasis, setLenderCreditTimingBasis] = useState<'field4794' | 'total'>('total');
  const [retainedClosingFeeBasis, setRetainedClosingFeeBasis] = useState<'legacy' | 'expandedLeFees'>('expandedLeFees');
  const [feeAccounting, setFeeAccounting] = useState<ReconFeeAccountingSnapshot | null>(null);
  const feeContextRef = useRef<ReconFeeContext | null>(null);
  const feeReconUpdatedAtRef = useRef<string | null>(null);
  // Latest fee capture seen by realtime while a save, Finalize or Reopen held the
  // screen. Reopen compares it with the reloaded fee context.
  const deferredFeeCaptureRef = useRef<{ status?: string; signature?: string } | null>(null);
  const [feeCaptureError, setFeeCaptureError] = useState<string | null>(null);
  const feeLedgerIssues = useMemo(() => feeAccounting && status === 'draft'
    ? [...feeAccounting.issues, ...reconcileFeePaymentLedger(revenue.loan, feeAccounting.capture).issues]
    : [], [feeAccounting, revenue.loan, status]);

  // Declared after `status`: it also re-runs when Finalize or Reopen changes the
  // status, so the Servicing tab sees the record Finalize just created.
  useEffect(() => {
    let cancelled = false;
    setServicingRecordId(null);
    if (!selectedLoan?.encompass_loan_id || !user?.organization_id) {
      return;
    }
    void (async () => {
      const { data, error } = await supabase.from('servicing_loans').select('id').eq(
        'organization_id',
        user.organization_id
      ).eq('encompass_loan_id', selectedLoan.encompass_loan_id).maybeSingle();
      if (!cancelled && !error) {
        setServicingRecordId(data?.id ?? null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLoan?.encompass_loan_id, user?.organization_id, reconId, status]);

  const setRevenueForDraft = useCallback((next: React.SetStateAction<RevenueState>) => {
    setRevenueRaw(prev => {
      const nextRevenue = resolveRevenueStateAction(next, prev);
      const normalized = normalizeRevenueStateForDraft(nextRevenue, retainedClosingFeeBasis);
      return feeContextRef.current ? {
        ...normalized,
        loan: reconcileFeePaymentLedger(normalized.loan, feeContextRef.current.accounting.capture).rows
      } : normalized;
    });
  }, [retainedClosingFeeBasis]);

  const setRevenueForDraftWithBasis = useCallback((
    next: React.SetStateAction<RevenueState>,
    basis: 'legacy' | 'expandedLeFees'
  ) => {
    setRevenueRaw(prev => {
      const nextRevenue = resolveRevenueStateAction(next, prev);
      const normalized = normalizeRevenueStateForDraft(nextRevenue, basis);
      return feeContextRef.current ? {
        ...normalized,
        loan: reconcileFeePaymentLedger(normalized.loan, feeContextRef.current.accounting.capture).rows
      } : normalized;
    });
  }, []);

  // Investor purchase date (from Investor tab)
  const [investorPurchaseDate, setInvestorPurchaseDate] = useState('');

  // First payment to investor date (from Investor tab)
  const [firstPaymentToInvestor, setFirstPaymentToInvestor] = useState('');

  // Wire reconciliation amounts
  const [wireFromInvestor, setWireFromInvestor] = useState(0);
  const [wireFromWarehouse, setWireFromWarehouse] = useState(0);

  // Servicing amounts (auto-populated from servicing pipeline)
  const [servicingInterestIncome, setServicingInterestIncome] = useState(0);
  const [servicingPrincipalCollected, setServicingPrincipalCollected] = useState(0);

  // LO Profitability — per-credit routing choices. Company routing must be
  // persisted explicitly because, like profitability, it has no payout deduction.
  const [routingChoices, setRoutingChoices] = useState<
    Partial<Record<RoutableCreditType, { target: ProfitabilityRoutingTarget; amount?: number }>>
  >({});
  // Σ active (non-legacy) concession points on the loan — mirrors usePricingRecalc.
  const [concessionActivePoints, setConcessionActivePoints] = useState(0);
  // Σ active ENTERED concession dollars — audit context for the bridge sub-note.
  const [concessionEnteredDollars, setConcessionEnteredDollars] = useState(0);
  // Concession truth: LO Profitability renders (and drafts save) only when 'loaded'
  // on non-terminal recons — "never save after a failed or unknown concession read".
  const [concessionTruth, setConcessionTruth] = useState<'loading' | 'loaded' | 'error'>('loading');
  // Monotonic token — bumped on loan load/clear and by every fetch, so a slow
  // load-time response (success OR failure) can't clobber a fresher save-time result.
  const concessionRequestIdRef = useRef(0);
  // The LO's remaining available profitability across other recons (null = unavailable).
  const [availableProfitability, setAvailableProfitability] = useState<number | null>(null);
  // Persisted LO Profitability snapshot from a loaded recon — shown verbatim on terminal recons.
  const [loadedProfitabilitySnapshot, setLoadedProfitabilitySnapshot] = useState<
    Pick<BankerRecon, 'lo_profitability_gross' | 'lo_profitability_net' | 'lo_profitability_components'> | null
  >(null);
  const [fhaUfmipSource, setFhaUfmipSource] = useState<FhaUfmipSourceSnapshot>(
    EMPTY_FHA_UFMIP_SOURCE
  );
  const [fhaUfmipTruth, setFhaUfmipTruth] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [terminalFhaUfmipSnapshot, setTerminalFhaUfmipSnapshot] = useState<{
    flowMode: FhaUfmipFlowMode;
    companyVarianceAmount: number;
  } | null>(null);

  // Dirty state tracking for unsaved changes
  const [isDirty, setIsDirtyRaw] = useState(false);
  // Monotonic edit revision: bumped by EVERY dirty-marking edit (all edit surfaces
  // route through this setter — internal call sites and the setIsDirty prop the
  // tabs receive). Lets a save detect edits that landed while it was in flight
  // (never silently mark them saved) and lets finalize abort if the accounting
  // inputs changed after its preliminary save.
  const editRevisionRef = useRef(0);
  const setIsDirty = useCallback((value: boolean) => {
    if (value) {
      ++editRevisionRef.current;
    }
    setIsDirtyRaw(value);
  }, []);
  const [pendingLoanAction, setPendingLoanAction] = useState<PendingLoanAction | null>(null);
  const loanSearchRef = useRef<HTMLDivElement>(null);

  // Document upload state (shared across tabs)
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);

  // Loan officer state - for LO override capability
  const [loanOfficers, setLoanOfficers] = useState<User[]>([]);
  const [loadingOfficers, setLoadingOfficers] = useState(true);
  const [selectedLOId, setSelectedLOId] = useState<string>('');
  const volumeBonus = useVolumeBonusPreview(reconId, selectedLOId || null, investorPurchaseDate || null);
  const { reviewedForSave: reviewedVolumeBonusForSave, refresh: refreshVolumeBonus } = volumeBonus;
  const volumeBonusAmount = status === 'draft' ? volumeBonus.preview?.amount ?? 0 : storedVolumeBonusAmount;
  // All active users — for override recipient dropdown (includes LOAs, admins, etc.)
  const [allActiveUsers, setAllActiveUsers] = useState<User[]>([]);
  // Override fields — multiple overrides per recon (persisted as JSONB array)
  const [overrides, setOverridesState] = useState<DraftOverrideEntry[]>([]);
  const overridesRef = useRef<DraftOverrideEntry[]>([]);
  const setOverrides = useCallback((value: React.SetStateAction<DraftOverrideEntry[]>) => {
    const next = typeof value === 'function'
      ? (value as (current: DraftOverrideEntry[]) => DraftOverrideEntry[])(overridesRef.current)
      : value;
    overridesRef.current = next;
    setOverridesState(next);
  }, []);

  // View mode toggle: 'new' for working on recons, 'finalized' for browsing completed records
  const [viewMode, setViewMode] = useState<'new' | 'finalized'>('new');
  const [finalizedRecons, setFinalizedRecons] = useState<BankerRecon[]>([]);
  const [finalizedReconsLoading, setFinalizedReconsLoading] = useState(false);

  // Request counter to prevent race conditions when switching loans quickly
  const loadRequestIdRef = useRef(0);
  // Owner token of an in-flight loadLoanInternal (0 = none). Saves are rejected
  // while a load runs: recon identity/status aren't authoritative yet (STEP 1
  // transiently resets status to 'draft' before the existing-recon lookup lands).
  const loadInFlightRef = useRef(0);
  // A failed read is not an empty reconciliation. Keep writes blocked until
  // another loan load succeeds (or the selection is cleared).
  const loanLoadFailedRef = useRef(false);
  const [loanLoadFailed, setLoanLoadFailed] = useState(false);
  // Bumped each time a load releases ownership; resumes deferred auto-fill review after render.
  const [loanLoadSettled, setLoanLoadSettled] = useState(0);
  // Bumped when an auto-fill review was interrupted mid-build; retries it after render.
  const [autoFillRetry, setAutoFillRetry] = useState(0);
  // Bumped whenever a save, Finalize or Reopen takes the screen. An auto-fill review
  // whose read overlapped one (even one that already ended) is rebuilt.
  const formWorkflowRef = useRef(0);
  // True while handleSaveDraft runs. Loan switching/clearing/deleting is blocked
  // for the duration so a resumed save can never mutate another loan's screen
  // state or delete the wrong recon. (Save-and-Switch awaits the save first.)
  const saveInFlightRef = useRef(false);
  // True while handleDeleteDraft runs — the symmetric exclusion: saves (and loan
  // switches) are blocked while a deletion (which ends in a fresh reload) is in
  // flight, so a save can never resurrect a just-deleted draft.
  const deleteInFlightRef = useRef(false);
  // True for the ENTIRE finalize workflow (preliminary save → lock checks →
  // finalizeRecon → publishing terminal state). Loan switch/clear/delete and
  // external saves are blocked for the whole lifetime, not just the save leg —
  // otherwise finalization could complete against a captured old recon and
  // publish terminal state into a newly selected loan's screen.
  const finalizeInFlightRef = useRef(false);

  // Finalized loan exclusion state — prevents finalized loans from appearing in search
  const [finalizedLoanIds, setFinalizedLoanIds] = useState<string[]>([]);
  const [finalizedIdsLoaded, setFinalizedIdsLoaded] = useState(false);
  const [finalizedIdsFailed, setFinalizedIdsFailed] = useState(false);
  const finalizedIdsRequestIdRef = useRef(0);

  // Race guard for LO changes (prevents stale async comp settings from overwriting)
  const latestLOChangeRef = useRef(0);
  // Count of in-flight LO compensation-settings fetches. Saves (and therefore
  // finalizes) are blocked while any is pending — the settled application would
  // land AFTER the save's snapshot and be silently absorbed by the dirty-clear.
  const loSettingsPendingRef = useRef(0);
  // True when the last LO compensation-settings read FAILED: the LO's real
  // overrides/compensation are unverified, so saves are blocked until a
  // successful re-read (reselect the LO). Fail closed, never null-as-empty.
  const loSettingsErrorRef = useRef(false);
  // A realtime lender-credit update was SKIPPED during persistence. The value
  // must not be silently lost: after the operation unlocks, an authoritative
  // re-read runs; a finalize seeing this set aborts BEFORE finalizeRecon.
  const pendingLiveCreditRefreshRef = useRef(false);
  // Current effect-scoped authoritative re-reader (null when unsubscribed).
  const rereadLiveCreditsRef = useRef<(() => Promise<void>) | null>(null);

  // Always-current compensation value for async callbacks (avoids stale closure reads)
  const compensationValueRef = useRef(compensation.value);
  compensationValueRef.current = compensation.value;
  const revenueRef = useRef(revenue);
  revenueRef.current = revenue;
  const selectedLoanRef = useRef<EncompassLoan | null>(selectedLoan);
  selectedLoanRef.current = selectedLoan;
  const loCompReductionImportRef = useRef<LoCompReductionImportMetadata | null>(null);
  const [loCompReductionMetadata, setLoCompReductionMetadata] = useState<LoCompReductionImportMetadata | null>(null);
  const setLoCompReductionImportMetadata = useCallback((metadata: LoCompReductionImportMetadata | null) => {
    loCompReductionImportRef.current = metadata;
    setLoCompReductionMetadata(metadata);
  }, []);
  const lastSavedCompensationRef = useRef<CompensationState | null>(null);
  // Holds the exact LO Profitability persisted by the last save, so the
  // post-finalize compensation-breakdown email reflects what was stored.
  const lastSavedProfitabilityRef = useRef<ReconLoProfitabilityResult | null>(null);
  const lastSavedTerminalAccountingRef = useRef<TerminalAccountingValues | null>(null);
  const lastSavedFhaUfmipRef = useRef<FhaUfmipBridge | null>(null);

  const fetchLiveLenderCreditValues = useCallback(async (
    encompassLoanId: string,
    fallbackLoan?: EncompassLoan | null
  ): Promise<{ values: LiveLenderCreditValues; loanPatch: Partial<EncompassLoan> | null }> => {
    const { data, error } = await supabase
      .from('encompass_loans')
      // origination_fee_percent feeds Case-2 borrower-paid comp in LO Profitability —
      // refresh it here so an already-open recon doesn't persist stale BPC at save/finalize.
      .select('encompass_loan_id, trans_details_non_specific_lender_credit, le_fields, cure_applied_lender_credit, origination_fee_percent')
      .eq('encompass_loan_id', encompassLoanId)
      .maybeSingle();

    if (error) {
      throw error;
    }

    const loanPatch = data ? data as Partial<EncompassLoan> : null;
    return {
      values: getLiveLenderCreditValuesFromLoan({ ...(fallbackLoan ?? {}), ...(loanPatch ?? {}) } as Partial<EncompassLoan>),
      loanPatch
    };
  }, []);

  const fetchBuydownPassThroughAmount = useCallback(async (
    encompassLoanId: string,
    fallbackLoan?: EncompassLoan | null
  ): Promise<number> => {
    const { data, error } = await supabase
      .from('encompass_loans')
      .select('encompass_loan_id, raw_data')
      .eq('encompass_loan_id', encompassLoanId)
      .eq('deleted_from_encompass', false)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return extractBuydownPassThroughAmount({
      ...(fallbackLoan ?? {}),
      raw_data: (data as { raw_data?: unknown } | null)?.raw_data
    });
  }, []);

  const applyBuydownPassThroughAmount = useCallback((amount: number) => {
    if (amount <= 0) {
      return;
    }
    setCosts(prev => normalizeBuydownPassThroughCostsForDraft(prev, amount));
    setIsDirty(true);
  }, [setIsDirty]);

  /**
   * Fetch concession truth (active points + entered dollars) for a loan.
   * Single shared read for BOTH the load path (STEP 6c) and the mandatory
   * save-time refresh. Returns the values for direct use (never rely on React
   * state for a save); commits state only when its token is still current so a
   * slow earlier response — success or failure — can't clobber a fresher one.
   * Returns null on failure (caller fails closed).
   */
  const fetchConcessionTruth = useCallback(async (
    encompassLoansId: string
  ): Promise<{ points: number; dollars: number } | null> => {
    // Loan-scoped token ownership: a fetch for a loan that is no longer selected
    // (e.g. a save that resumed after the user switched loans) may still USE its
    // returned values, but must neither claim the token (which would suppress the
    // current loan's in-flight response) nor commit into the current loan's state.
    const isForActiveLoan = () => selectedLoanRef.current?.id === encompassLoansId;
    const token = isForActiveLoan() ? ++concessionRequestIdRef.current : null;
    try {
      const concessions = await fetchConcessions(encompassLoansId);
      const points = sumActiveConcessionPoints(concessions);
      const dollars = sumActiveConcessionDollars(concessions);
      if (token !== null && token === concessionRequestIdRef.current && isForActiveLoan()) {
        setConcessionActivePoints(points);
        setConcessionEnteredDollars(dollars);
        setConcessionTruth('loaded');
      }
      return { points, dollars };
    } catch (err) {
      log.warn('Failed to fetch Lendwise concessions for Banker Recon', { loanId: encompassLoansId }, err as Error);
      if (token !== null && token === concessionRequestIdRef.current && isForActiveLoan()) {
        setConcessionTruth('error');
      }
      return null;
    }
  }, []);

  const applyLiveLenderCreditValues = useCallback((
    values: LiveLenderCreditValues,
    loanPatch: Partial<EncompassLoan> | null,
    options: { showToast?: boolean } = {}
  ) => {
    const current = revenueRef.current;
    const changed =
      toNumber(current.lenderCredit) !== toNumber(values.lenderCredit) ||
      toNumber(current.cureAppliedLenderCredit) !== toNumber(values.cureAppliedLenderCredit);

    if (loanPatch?.encompass_loan_id) {
      setSelectedLoan(prev =>
        prev?.encompass_loan_id === loanPatch.encompass_loan_id
          ? { ...prev, ...loanPatch } as EncompassLoan
          : prev
      );
    }

    setRevenueForDraft(prev => ({
      ...prev,
      lenderCredit: values.lenderCredit,
      cureAppliedLenderCredit: values.cureAppliedLenderCredit
    }));
    setCompensation(prev => clampCompensationDeductionsToLiveCredits(prev, values));

    if (changed && options.showToast) {
      toast({
        title: 'Lender credit updated from Encompass',
        description: 'The draft now reflects the current Encompass lender credit values.'
      });
    }

    return changed;
  }, [setRevenueForDraft, toast]);

  // Cleanup save timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Fetch finalized loan IDs for search exclusion (fail-closed design)
  const refreshFinalizedLoanIds = useCallback(async () => {
    if (!user?.organization_id) {
      return;
    }
    const thisRequestId = ++finalizedIdsRequestIdRef.current;
    setFinalizedIdsFailed(false);
    const result = await bankerReconService.getFinalizedLoanIds(user.organization_id);
    if (thisRequestId !== finalizedIdsRequestIdRef.current) {
      return;
    }
    if (result.success) {
      setFinalizedLoanIds(result.ids);
      setFinalizedIdsLoaded(true);
    } else {
      setFinalizedIdsFailed(true);
      log.warn('Finalized loan ID fetch failed, preserving previous exclusion list');
    }
  }, [user?.organization_id]);

  useEffect(() => {
    refreshFinalizedLoanIds();
  }, [refreshFinalizedLoanIds]);

  // Reset save success state when user makes edits
  useEffect(() => {
    if (isDirty && saveState === 'success') {
      setSaveState('idle');
    }
  }, [isDirty, saveState]);

  // Load loan officers for LO dropdown + all active users for override recipients
  useEffect(() => {
    const loadUsers = async () => {
      if (!user?.organization_id) {
        setLoadingOfficers(false);
        return;
      }
      const [loResult, allResult] = await Promise.all([
        UserService.getLoanOfficers(user.organization_id),
        UserService.getOrganizationUsers(user.organization_id)
      ]);
      if (loResult.error) {
        log.error('Failed to load loan officers', { error: loResult.error });
      }
      if (loResult.data) {
        setLoanOfficers(loResult.data);
      }
      if (allResult.data) {
        setAllActiveUsers(allResult.data);
      }
      setLoadingOfficers(false);
    };
    loadUsers();
  }, [user?.organization_id]);

  // ============================================================================
  // CALCULATED VALUES
  // ============================================================================

  const totalWarehouseCosts = useMemo(() => {
    return costs.warehouse.reduce((sum, item) => sum + item.amount, 0);
  }, [costs.warehouse]);

  const totalInvestorCosts = useMemo(() => {
    return costs.investor.reduce((sum, item) => sum + item.amount, 0);
  }, [costs.investor]);

  const totalCosts = useMemo(() => {
    return totalWarehouseCosts + totalInvestorCosts;
  }, [totalWarehouseCosts, totalInvestorCosts]);

  const totalLoanRevenue = useMemo(() => {
    return revenue.loan.reduce((sum, item) => sum + item.amount, 0);
  }, [revenue.loan]);

  const totalInvestorRevenue = useMemo(() => {
    return revenue.investor.reduce((sum, item) => sum + item.amount, 0);
  }, [revenue.investor]);

  const grossRevenue = useMemo(() => {
    return totalLoanRevenue + totalInvestorRevenue;
  }, [totalLoanRevenue, totalInvestorRevenue]);

  // Total Revenue = Gross Revenue (lender credit is a cost, not a revenue deduction)
  const totalRevenue = useMemo(() => {
    return grossRevenue;
  }, [grossRevenue]);

  // ============================================================================
  // BUILD LINE ITEMS (must be before reconResult since it's a dependency)
  // ============================================================================

  /**
   * Build line items array from UI state for database
   * Uses explicit numeric validation to prevent invalid values like "inc" from reaching the database
   */
  const builtLineItems = useMemo(() => {
    const miPremiumPayee = findConventionalMiPremiumFee(selectedLoan?.le_fees, selectedLoan?.trans_details_loan_type)?.payee ?? null;
    return buildLineItemsFromState(costs, revenue, compensation, undefined, undefined, miPremiumPayee, manualAdjustments);
  }, [costs, revenue, compensation, manualAdjustments, selectedLoan?.le_fees, selectedLoan?.trans_details_loan_type]);

  const investorWireAuditForFha = useMemo(() => calculateInvestorWireAudit({
    loanAmount: selectedLoan?.trans_details_loan_amt || 0,
    actualPurchasePricePercent: pricing.actualPricePercent || 0,
    totalInvestorRevenue,
    totalInvestorCosts,
    servicingPrincipalCollected,
    wireFromInvestor
  }), [
    selectedLoan?.trans_details_loan_amt,
    pricing.actualPricePercent,
    totalInvestorRevenue,
    totalInvestorCosts,
    servicingPrincipalCollected,
    wireFromInvestor
  ]);

  const fhaUfmipBridge = useMemo<FhaUfmipBridge>(() => {
    const derived = buildFhaUfmipBridge({
      loanType: selectedLoan?.trans_details_loan_type || null,
      source: fhaUfmipSource,
      lineItems: builtLineItems,
      investorAuditBalanced: investorWireAuditForFha.isInvestorBalanced
    });

    if (!terminalFhaUfmipSnapshot || !['finalized', 'pushed'].includes(status)) {
      return derived;
    }

    const lenderSideFlow =
      terminalFhaUfmipSnapshot.flowMode === 'pending_lender_remittance'
      || terminalFhaUfmipSnapshot.flowMode === 'lender_remitted';
    return {
      ...derived,
      flowMode: terminalFhaUfmipSnapshot.flowMode,
      warehousePremiumAmount: lenderSideFlow ? derived.premiumAmount || 0 : 0,
      clearingPremiumAmount: lenderSideFlow ? derived.premiumAmount || 0 : 0,
      outsideRemittanceAmount:
        terminalFhaUfmipSnapshot.flowMode === 'lender_remitted'
          ? derived.remittedAmount || 0
          : 0,
      companyVarianceAmount: terminalFhaUfmipSnapshot.companyVarianceAmount
    };
  }, [
    selectedLoan?.trans_details_loan_type,
    fhaUfmipSource,
    builtLineItems,
    investorWireAuditForFha.isInvestorBalanced,
    terminalFhaUfmipSnapshot,
    status
  ]);

  // ============================================================================
  // SHARED FINANCIAL MODEL — Single source of truth via computeBankedRecon()
  // ============================================================================

  // Standard/Encompass fee IDs that have dedicated handling
  const standardFeeIds = useMemo(() => ['processing_fee', 'underwriting_fee', 'interest_charged'], []);

  const reconResult: BankedReconResult = useMemo(() => computeBankedRecon({
    loanAmount: selectedLoan?.trans_details_loan_amt || 0,
    actualPurchasePrice: pricing.actualPricePercent || 0,
    compensationType: compensation.type,
    compensationValue: compensation.value,
    volumeBonusAmount,
    overrides: getActiveOverrides(overrides).map(o => ({ bps: o.bps })),
    processingFee: revenue.loan.find(r => r.id === 'processing_fee')?.amount || 0,
    underwritingFee: revenue.loan.find(r => r.id === 'underwriting_fee')?.amount || 0,
    interestIncome: revenue.loan.find(r => r.id === 'interest_charged')?.amount || 0,
    servicingInterestIncome,
    flexcloseCredit: costs.flexcloseCredit,
    loanType: selectedLoan?.trans_details_loan_type || null,
    fhaUfmip: fhaUfmipBridge,
    lineItems: builtLineItems
  }), [selectedLoan, pricing.actualPricePercent, compensation, volumeBonusAmount, overrides, revenue, costs, servicingInterestIncome, builtLineItems, fhaUfmipBridge]);

  // Destructure for downstream use
  const {
    loPayout, totalReimbursable, loPayoutWithReimbursables: _loPayoutWithReimbursables,
    actualPurchaseDollar, gosAmount, processingFee: _processingFee, underwritingFee: _underwritingFee,
    nonReimbursableUserFees: _nonReimbursableUserFees, companyRevenue, totalOverrideAmount,
    warehouseInterest: _warehouseInterestExpense, otherWarehouseCosts: _otherWarehouseCosts,
    shortageFunded: _shortageFunded, investorInterestCost: _investorInterestCost, otherInvestorCosts: _otherInvestorCosts, escrowPassThrough: _escrowPassThrough,
    totalRevenueForDisplay, totalCostsForDisplay, netProfit
  } = reconResult;
  const isTerminalRecon = status === 'finalized' || status === 'pushed';
  const displayLoPayout = isTerminalRecon && terminalAccounting?.loPayout !== null && terminalAccounting?.loPayout !== undefined
    ? terminalAccounting.loPayout
    : loPayout;
  const displayCompanyRevenue = isTerminalRecon && terminalAccounting?.companyRevenue !== null && terminalAccounting?.companyRevenue !== undefined
    ? terminalAccounting.companyRevenue
    : companyRevenue;
  const displayGosAmount = isTerminalRecon && terminalAccounting?.gosAmount !== null && terminalAccounting?.gosAmount !== undefined
    ? terminalAccounting.gosAmount
    : gosAmount;
  const displayNetProfit = isTerminalRecon && terminalAccounting?.netProfit !== null && terminalAccounting?.netProfit !== undefined
    ? terminalAccounting.netProfit
    : netProfit;
  const displayTotalRevenueForPdf = isTerminalRecon
    ? displayCompanyRevenue
    : totalRevenueForDisplay;
  const displayTotalCostsForPdf = isTerminalRecon
    ? displayCompanyRevenue - displayNetProfit
    : totalCostsForDisplay;

  // ============================================================================
  // LO PROFITABILITY (routing-aware, signed, report-only)
  // ============================================================================
  const profitabilityRouting = useMemo<ProfitabilityRouting>(
    () => deriveProfitabilityRouting({
      revenue,
      compensation,
      loCompReductionMetadata,
      routingChoices
    }),
    [revenue, compensation, loCompReductionMetadata, routingChoices]
  );

  const loProfitability = useMemo<ReconLoProfitabilityResult | null>(() => {
    // Terminal recons display the persisted snapshot verbatim (never recompute).
    if (isTerminalRecon) {
      if (
        loadedProfitabilitySnapshot
        && loadedProfitabilitySnapshot.lo_profitability_net !== null
        && loadedProfitabilitySnapshot.lo_profitability_net !== undefined
      ) {
        return {
          gross: toNumber(loadedProfitabilitySnapshot.lo_profitability_gross ?? 0),
          net: toNumber(loadedProfitabilitySnapshot.lo_profitability_net),
          components: loadedProfitabilitySnapshot.lo_profitability_components ?? []
        };
      }
      return null; // historical finalized recon predating the feature ⇒ hide
    }
    // Fail closed on unknown concession truth: never DISPLAY (or later save) a
    // concession-blind number. The Summary shows "Concessions unavailable" instead.
    if (concessionTruth !== 'loaded') {
      return null;
    }
    return computeReconLoProfitability({
      basePriceLocked: toNumber(selectedLoan?.rate_lock_total_buy_price) || 0,
      discountPoints: toNumber(selectedLoan?.discount_point_percent) || 0,
      concessionPoints: concessionActivePoints,
      concessionDollarsEntered: concessionEnteredDollars,
      loanAmount: toNumber(selectedLoan?.trans_details_loan_amt) || 0,
      routing: profitabilityRouting,
      // Missing pricing data must NOT be read as confirmed loCompBps=0 (which would
      // wrongly trigger Case 2): pass undefined unless adjustments are actually present.
      loCompBps: Array.isArray(selectedLoan?.rate_lock_buy_side_adjustments)
        ? extractEncompassLoCompBps(selectedLoan?.rate_lock_buy_side_adjustments)
        : undefined,
      borrowerPaidCompDollars:
        (toNumber(selectedLoan?.origination_fee_percent) || 0) * (toNumber(selectedLoan?.trans_details_loan_amt) || 0) / 100,
      loPayoutBase: reconResult?.loPayoutBase ?? 0
    });
  }, [
    isTerminalRecon,
    loadedProfitabilitySnapshot,
    selectedLoan?.rate_lock_total_buy_price,
    selectedLoan?.discount_point_percent,
    selectedLoan?.origination_fee_percent,
    selectedLoan?.rate_lock_buy_side_adjustments,
    selectedLoan?.trans_details_loan_amt,
    concessionActivePoints,
    concessionEnteredDollars,
    concessionTruth,
    profitabilityRouting,
    reconResult?.loPayoutBase
  ]);

  // Fetch the LO's remaining available profitability (excluding this recon).
  useEffect(() => {
    if (!selectedLOId) {
      setAvailableProfitability(null);
      return;
    }
    let cancelled = false;
    getLoAvailableProfitability(selectedLOId, reconId)
      .then(value => {
        if (!cancelled) {
          setAvailableProfitability(value);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAvailableProfitability(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedLOId, reconId]);

  // ============================================================================
  // LIVE SERVICING CALCULATION
  // Auto-compute expected servicing P&I from amortization schedule when dates exist
  // ============================================================================

  useEffect(() => {
    if (isTerminalRecon) {
      return;
    }

    const rawRate = selectedLoan?.trans_details_interest_rate;
    const hasValidRate = rawRate !== null
      && rawRate !== undefined
      && rawRate !== ''
      && Number.isFinite(toNumber(rawRate, Number.NaN));

    if (!selectedLoan?.first_payment_date || !firstPaymentToInvestor || !hasValidRate) {
      setServicingInterestIncome(0);
      setServicingPrincipalCollected(0);
      return;
    }

    const loanAmount = toNumber(selectedLoan.trans_details_loan_amt);
    const rate = toNumber(selectedLoan.trans_details_interest_rate);
    const term = toNumber(selectedLoan.loan_term_months) || 360;
    const pi = toNumber(selectedLoan.proposed_pi_payment) || calculateMonthlyPI(loanAmount, rate, term);

    const count = calculatePaymentCount(selectedLoan.first_payment_date, firstPaymentToInvestor);
    if (count === 0) {
      setServicingInterestIncome(0);
      setServicingPrincipalCollected(0);
      return;
    }

    const schedule = generatePaymentSchedule(loanAmount, rate, term, pi, 0, selectedLoan.first_payment_date, firstPaymentToInvestor);
    const totalInterest = schedule.reduce((sum, e) => sum + e.interest_due, 0);
    const totalPrincipal = schedule.reduce((sum, e) => sum + e.principal_due, 0);
    const investorWirePrincipalReduction = calculateInvestorWirePrincipalReduction(
      loanAmount,
      rate,
      term,
      selectedLoan.first_payment_date,
      firstPaymentToInvestor,
      toNumber(selectedLoan.interest_only_term_months)
    );

    setServicingInterestIncome(Math.round(totalInterest * 100) / 100);
    setServicingPrincipalCollected(investorWirePrincipalReduction ?? Math.round(totalPrincipal * 100) / 100);
  }, [selectedLoan, firstPaymentToInvestor, status, isTerminalRecon]);

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const validationIssues = useMemo((): ValidationIssue[] => {
    const issues: ValidationIssue[] = [];
    if (status === 'draft') {
      for (const message of [...(feeCaptureError ? [feeCaptureError] : []), ...feeLedgerIssues]) {
        issues.push({ type: 'error', message });
      }
    }

    if (!selectedLoan) {
      issues.push({ type: 'info', message: 'Select a loan to begin reconciliation' });
    }

    if (selectedLoan && totalCosts === 0 && totalRevenue === 0) {
      issues.push({ type: 'warning', message: 'No costs or revenue entered yet' });
    }

    // Override validation
    if (overrides.length > 3) {
      issues.push({ type: 'error', message: 'Maximum 3 override rows allowed' });
    }
    const halfFilled = overrides.filter(o =>
      (o.recipientId && (o.bps === null || !Number.isFinite(o.bps)))
      || (!o.recipientId && typeof o.bps === 'number' && Number.isFinite(o.bps) && o.bps > 0)
    );
    if (halfFilled.length > 0) {
      issues.push({ type: 'error', message: 'Override row incomplete — set both recipient and BPS, or remove it' });
    }
    const activeOvr = getActiveOverrides(overrides);
    const configuredOvr = getConfiguredOverrides(overrides);
    const totalBps = activeOvr.reduce((sum, o) => sum + o.bps, 0);
    if (totalBps > 60) {
      issues.push({ type: 'error', message: `Combined override BPS (${totalBps}) exceeds maximum of 60` });
    }
    const recipientIds = configuredOvr.map(o => o.recipientId);
    if (new Set(recipientIds).size !== recipientIds.length) {
      issues.push({ type: 'error', message: 'Duplicate override recipients — each person can only appear once' });
    }

    if (status === 'draft') {
      if (volumeBonus.error) {
        issues.push({ type: 'error', message: volumeBonus.error });
      } else if (volumeBonus.loading) {
        issues.push({ type: 'info', message: 'Checking volume bonus eligibility…' });
      }
      if (revenue.lenderCredit > 0 && revenue.lenderCreditNetsToWarehouseAdvance === null) {
        issues.push({ type: 'error', message: 'Review required: choose whether Lender Credit Given nets to WH advance' });
      }
      if (revenue.cureAppliedLenderCredit > 0 && revenue.cureAppliedLenderCreditNetsToWarehouseAdvance === null) {
        issues.push({ type: 'error', message: 'Review required: choose whether Cure Applied to Lender Credit nets to WH advance' });
      }
      if (fhaUfmipTruth === 'error') {
        issues.push({
          type: 'error',
          message: 'FHA UFMIP could not be verified from the canonical Encompass snapshot'
        });
      } else if (fhaUfmipTruth === 'loaded') {
        for (const message of fhaUfmipBridge.finalizationErrors) {
          issues.push({ type: 'error', message });
        }
      }
    }

    return issues;
  }, [feeCaptureError, feeLedgerIssues, selectedLoan, totalCosts, totalRevenue, overrides, status, volumeBonus.error, volumeBonus.loading, revenue.lenderCredit, revenue.lenderCreditNetsToWarehouseAdvance, revenue.cureAppliedLenderCredit, revenue.cureAppliedLenderCreditNetsToWarehouseAdvance, fhaUfmipTruth, fhaUfmipBridge.finalizationErrors]);

  // ============================================================================
  // DOCUMENT AUTO-FILL COORDINATOR
  // A single parent-owned coordinator serializes extraction + the review modal across both docs.
  // ============================================================================

  // line_types filled by auto-fill this session (provenance, used only at save time)
  const extractedLineTypesRef = useRef<Set<string>>(new Set());
  // line_types seeded from a completed Doc Order this session (provenance, used only at save time)
  const docOrderLineTypesRef = useRef<Set<string>>(new Set());
  // review markers queued by Apply, flushed to the DB only after a successful Save
  const pendingReviewMarksRef = useRef<Map<string, AutoFillReviewMark>>(new Map());
  // in-flight lock + pending-doc queue (warehouse/combined first, purchase advice second)
  const coordinatorRef = useRef<{ inFlight: boolean; queue: ReconDocumentAutoFill[] }>({ inFlight: false, queue: [] });
  // docId|reviewKey already enqueued this session (don't re-pop after cancel within a session)
  const processedKeysRef = useRef<Set<string>>(new Set());
  // the doc whose modal is currently open
  const currentDocRef = useRef<ReconDocumentAutoFill | null>(null);
  const [autoFillReview, setAutoFillReview] = useState<{
    rows: AutoFillRow[];
    documentLabel: string;
    classificationConfidence?: number;
  } | null>(null);

  // Latest form-state snapshot the coordinator reads at presentation/apply time (updated every render).
  const autoFillStateRef = useRef({
    keyDates, costs, pricing, investorPurchaseDate, firstPaymentToInvestor,
    purchaseAdjustments, wireFromInvestor, wireFromWarehouse, selectedLoan, reconId, status
  });
  autoFillStateRef.current = {
    keyDates, costs, pricing, investorPurchaseDate, firstPaymentToInvestor,
    purchaseAdjustments, wireFromInvestor, wireFromWarehouse, selectedLoan, reconId, status
  };

  // Build the current-state snapshot (incl. fresh persistedLineTypes) at the moment a modal opens.
  const buildAutoFillReviewState = useCallback(async (): Promise<AutoFillCurrentState> => {
    const f = autoFillStateRef.current;
    let persistedLineTypes = new Set<string>();
    if (f.reconId) {
      const items = await bankerReconService.getLineItems(f.reconId);
      // Category-aware anchor `${category}:${baseType}` for warehouse + investor (matches the mapper dedupe).
      persistedLineTypes = new Set(
        items
          .filter(li => li.category === 'warehouse_cost' || li.category === 'investor_cost')
          .map(li => `${li.category}:${li.category === 'investor_cost' ? canonicalInvestorCostBaseType(li.line_type, li.description) : li.line_type.replace(/_\d+$/, '')}`)
      );
    }
    return {
      keyDates: f.keyDates,
      costs: f.costs,
      pricing: f.pricing,
      investorPurchaseDate: f.investorPurchaseDate,
      firstPaymentToInvestor: f.firstPaymentToInvestor,
      purchaseAdjustments: f.purchaseAdjustments,
      wireFromInvestor: f.wireFromInvestor,
      wireFromWarehouse: f.wireFromWarehouse,
      investorLockPrice: computeInvestorLockPrice(f.selectedLoan),
      persistedLineTypes
    };
  }, []);

  // Reuse a cached extraction (matched reviewKey) or invoke the edge function; persist the result.
  const getOrInvokeExtraction = useCallback(async (doc: ReconDocumentAutoFill): Promise<AutoFillExtractionCache | null> => {
    if (!doc.reviewKey || !doc.extractedText) {
      return null;
    }
    if (doc.autoFillExtraction) {
      if (doc.autoFillExtraction.status === 'extracted') {
        return {
          ...doc.autoFillExtraction,
          fields: normalizeExtractedFieldsForBuydown(doc.autoFillExtraction.fields)
        };
      }
      return doc.autoFillExtraction; // cache hit
    }
    const f = autoFillStateRef.current;
    try {
      const { data, error } = await supabase.functions.invoke('banker-recon-extract', {
        body: {
          extractedText: doc.extractedText,
          storagePath: doc.storagePath,
          organizationId: user?.organization_id,
          loanNumber: f.selectedLoan?.trans_details_loan_number,
          // Let the warehouse doc auto-classify (it may resolve to 'combined'); pin the PA doc.
          documentType: doc.documentType === 'purchase_advice' ? 'purchase_advice' : undefined
        }
      });
      if (error) {
        throw error;
      }
      if (!data?.success || data?.needsClassification) {
        const cache: AutoFillExtractionCache = {
          status: 'ignored',
          reviewKey: doc.reviewKey,
          extractorVersion: data?.extractorVersion || BANKER_EXTRACTOR_VERSION,
          reason: data?.message || 'Could not classify document'
        };
        void bankerReconService.markDocumentAutoFill(doc.documentId, { extraction: cache });
        return cache;
      }
      const cache: AutoFillExtractionCache = {
        status: 'extracted',
        reviewKey: doc.reviewKey,
        extractorVersion: data.extractorVersion || BANKER_EXTRACTOR_VERSION,
        documentType: data.documentType,
        classification: data.classification ?? null,
        fields: normalizeExtractedFieldsForBuydown(data.fields || {}),
        fieldConfidence: data.fieldConfidence || {}
      };
      void bankerReconService.markDocumentAutoFill(doc.documentId, { extraction: cache });
      return cache;
    } catch (err) {
      log.warn('banker-recon-extract invoke failed', { documentId: doc.documentId }, err as Error);
      return null;
    }
  }, [user?.organization_id]);

  // Process the queue: extract → build rows → open one modal. Skipped/ignored docs are consumed in a
  // loop; a doc that opens a modal returns (keeping inFlight) until the modal resolves.
  const pumpAutoFillQueue = useCallback(async () => {
    const c = coordinatorRef.current;
    if (c.inFlight) {
      return;
    }
    // Review rows compare against the on-screen form, so never build them while a
    // load, Finalize or Reopen is rewriting it (or for a non-draft). Deferred
    // documents stay queued; the resume effect pumps again once the screen settles.
    const reviewDeferred = () => loadInFlightRef.current !== 0
      || loanLoadFailedRef.current
      || finalizeInFlightRef.current
      || autoFillStateRef.current.status !== 'draft';
    c.inFlight = true;
    try {
      while (c.queue.length > 0) {
        if (reviewDeferred()) {
          c.inFlight = false;
          return;
        }
        const doc = c.queue.shift() as ReconDocumentAutoFill;
        const extraction = await getOrInvokeExtraction(doc);
        // The loan changed meanwhile: its coordinator was replaced, so drop this work.
        if (coordinatorRef.current !== c) {
          return;
        }
        if (!extraction || extraction.status === 'ignored') {
          if (extraction?.status === 'ignored') {
            toast({ title: 'Document not recognized', description: 'Could not auto-fill from this document.' });
          }
          continue;
        }
        // Keep the document, with its extraction, for after the screen settles.
        const requeue = () => {
          c.queue.unshift({ ...doc, autoFillExtraction: extraction });
          c.inFlight = false;
        };
        if (reviewDeferred()) {
          requeue();
          return;
        }
        const loadGeneration = loadRequestIdRef.current;
        const workflowGeneration = formWorkflowRef.current;
        const current = await buildAutoFillReviewState();
        if (coordinatorRef.current !== c) {
          return;
        }
        // The snapshot was taken before the line-item read; a load, save, Finalize or
        // Reopen that started (or even finished) during it makes the snapshot unusable.
        // Retry after the next render, from the settled form.
        if (reviewDeferred()
          || loadRequestIdRef.current !== loadGeneration
          || formWorkflowRef.current !== workflowGeneration) {
          requeue();
          setAutoFillRetry(count => count + 1);
          return;
        }
        const rows = buildAutoFillRows(extraction.fields, extraction.fieldConfidence, current);
        if (rows.length === 0) {
          continue;
        }
        currentDocRef.current = doc;
        setAutoFillReview({
          rows,
          documentLabel: humanizeDocType(extraction.documentType),
          classificationConfidence: extraction.classification?.confidence
        });
        return; // keep inFlight; modal resolution calls pumpAutoFillQueue again
      }
      c.inFlight = false;
    } catch (err) {
      log.warn('pumpAutoFillQueue failed', {}, err as Error);
      c.inFlight = false;
    }
  }, [getOrInvokeExtraction, buildAutoFillReviewState, toast]);

  // Resume deferred review once a load, Finalize or Reopen settles, or after a review
  // was interrupted mid-build. This runs after render, so rows use the settled form.
  useEffect(() => {
    void pumpAutoFillQueue();
  }, [isFinalizing, loanLoadSettled, autoFillRetry, pumpAutoFillQueue]);

  const enqueueAutoFillDocs = useCallback((docs: ReconDocumentAutoFill[]) => {
    const c = coordinatorRef.current;
    for (const d of docs) {
      if (!d.reviewKey || !d.extractedText) {
        continue;
      }
      const key = `${d.documentId}|${d.reviewKey}`;
      if (processedKeysRef.current.has(key)) {
        continue;
      }
      if (c.queue.some(q => q.documentId === d.documentId) || currentDocRef.current?.documentId === d.documentId) {
        continue;
      }
      processedKeysRef.current.add(key);
      if (d.documentType === 'purchase_advice') {
        c.queue.push(d); // purchase advice second
      } else {
        c.queue.unshift(d); // warehouse / combined first
      }
    }
    void pumpAutoFillQueue();
  }, [pumpAutoFillQueue]);

  const finishCurrentAutoFillDoc = useCallback(() => {
    coordinatorRef.current.inFlight = false;
    currentDocRef.current = null;
    setAutoFillReview(null);
    void pumpAutoFillQueue();
  }, [pumpAutoFillQueue]);

  const handleAutoFillApply = useCallback((acceptedRows: AutoFillRow[]) => {
    const doc = currentDocRef.current;
    const f = autoFillStateRef.current;
    const current: AutoFillCurrentState = {
      keyDates: f.keyDates,
      costs: f.costs,
      pricing: f.pricing,
      investorPurchaseDate: f.investorPurchaseDate,
      firstPaymentToInvestor: f.firstPaymentToInvestor,
      purchaseAdjustments: f.purchaseAdjustments,
      wireFromInvestor: f.wireFromInvestor,
      wireFromWarehouse: f.wireFromWarehouse,
      investorLockPrice: computeInvestorLockPrice(f.selectedLoan),
      // Apply dedupes investor costs against the live costs.investor list, so the persisted anchor isn't needed here.
      persistedLineTypes: new Set<string>()
    };
    const result = applyAutoFillRows(current, acceptedRows);
    setKeyDates(result.keyDates);
    setCosts(result.costs);
    setPricing(result.pricing);
    setInvestorPurchaseDate(result.investorPurchaseDate);
    setFirstPaymentToInvestor(result.firstPaymentToInvestor);
    setPurchaseAdjustments(result.purchaseAdjustments);
    setWireFromInvestor(result.wireFromInvestor);
    setWireFromWarehouse(result.wireFromWarehouse);
    result.filledLineTypes.forEach(ft => extractedLineTypesRef.current.add(ft.provenanceKey));
    // Reflect the apply into the ref SYNCHRONOUSLY (before the React re-render commits) so the next queued
    // doc's modal — built from autoFillStateRef.current in pumpAutoFillQueue — sees the just-applied wires /
    // investor costs. Without this, a fast cached PA extraction can build against pre-apply state and offer
    // the combined doc's wire/costs again as default-checked duplicates.
    autoFillStateRef.current = {
      ...autoFillStateRef.current,
      keyDates: result.keyDates,
      costs: result.costs,
      pricing: result.pricing,
      investorPurchaseDate: result.investorPurchaseDate,
      firstPaymentToInvestor: result.firstPaymentToInvestor,
      purchaseAdjustments: result.purchaseAdjustments,
      wireFromInvestor: result.wireFromInvestor,
      wireFromWarehouse: result.wireFromWarehouse
    };
    if (result.filledKeys.length > 0) {
      setIsDirty(true);
      if (doc?.reviewKey) {
        // Queue the 'applied' marker — flushed to the DB only after a successful Save.
        pendingReviewMarksRef.current.set(doc.documentId, {
          reviewKey: doc.reviewKey,
          status: 'applied',
          reviewedAt: new Date().toISOString(),
          extractorVersion: BANKER_EXTRACTOR_VERSION
        });
      }
      toast({
        title: `Auto-filled ${result.filledKeys.length} field${result.filledKeys.length === 1 ? '' : 's'}`,
        description: 'Applied to form — not saved yet. Review and click Save.'
      });
    }
    finishCurrentAutoFillDoc();
  }, [finishCurrentAutoFillDoc, toast, setIsDirty]);

  // Plain close / Cancel — write nothing, mark nothing (re-offers next load).
  const handleAutoFillCancel = useCallback(() => {
    finishCurrentAutoFillDoc();
  }, [finishCurrentAutoFillDoc]);

  // "Skip this document" — mark reviewed/dismissed so it won't re-pop.
  const handleAutoFillSkip = useCallback(() => {
    const doc = currentDocRef.current;
    if (doc?.reviewKey) {
      void bankerReconService.markDocumentAutoFill(doc.documentId, {
        review: {
          reviewKey: doc.reviewKey,
          status: 'dismissed',
          reviewedAt: new Date().toISOString(),
          extractorVersion: BANKER_EXTRACTOR_VERSION
        }
      });
    }
    finishCurrentAutoFillDoc();
  }, [finishCurrentAutoFillDoc]);

  // Realtime trigger: a tab reports a doc finished extracting (new upload during the session).
  const handleDocumentReady = useCallback(async (documentId: string) => {
    const f = autoFillStateRef.current;
    const loanId = f.selectedLoan?.encompass_loan_id;
    if (!loanId || f.status !== 'draft') {
      return;
    }
    try {
      const docs = await bankerReconService.getReconDocumentTexts({ encompassLoanId: loanId, reconId: f.reconId });
      const target = docs.filter(d => d.documentId === documentId && !d.alreadyReviewed);
      if (target.length > 0) {
        enqueueAutoFillDocs(target);
      }
    } catch (err) {
      log.warn('handleDocumentReady failed', { documentId }, err as Error);
    }
  }, [enqueueAutoFillDocs]);

  // Reset coordinator state whenever the loan changes (stop processing the old loan's docs).
  useEffect(() => {
    coordinatorRef.current = { inFlight: false, queue: [] };
    processedKeysRef.current = new Set();
    pendingReviewMarksRef.current = new Map();
    currentDocRef.current = null;
    setAutoFillReview(null);
  }, [selectedLoan?.encompass_loan_id]);

  // On-load trigger: after a draft loads, enqueue any already-extracted, not-yet-reviewed docs (this is
  // how the existing Darnov draft auto-fills). buildAutoFillReviewState fetches persistedLineTypes fresh,
  // so the fee precedence is correct even though this runs independently of the main load flow.
  useEffect(() => {
    const loanId = selectedLoan?.encompass_loan_id;
    if (!loanId || status !== 'draft') {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const docs = await bankerReconService.getReconDocumentTexts({ encompassLoanId: loanId, reconId });
        if (cancelled) {
          return;
        }
        const ready = docs.filter(d => d.extractedText && d.reviewKey && !d.alreadyReviewed);
        if (ready.length > 0) {
          enqueueAutoFillDocs(ready);
        }
      } catch (err) {
        log.warn('on-load auto-fill check failed', {}, err as Error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLoan?.encompass_loan_id, reconId, status, enqueueAutoFillDocs]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  // Internal function to load loan data (called after dirty check passes)
  // Resets ALL state first, then checks for existing draft before auto-populating from Encompass
  // @param ignoreDraft - if true, skip draft loading and use fresh Encompass data
  const loadLoanInternal = useCallback(async (
    sourceLoan: EncompassLoan,
    ignoreDraft = false,
    options: LoadLoanOptions = {}
  ): Promise<LoadLoanOutcome> => {
    let loan = sourceLoan;
    const reopenReload = options.reloadAfterReopen;
    // A row fetched fresh for a Reopen reload is never overlaid, even when the
    // same loan's delivery lock is still selected on screen.
    const sourceLoanAlreadyHasDeliveryLockOverlay = !reopenReload &&
      deliveryLockSelection?.encompassLoanId === sourceLoan.encompass_loan_id;
    const liveEncompassLockExpires = sourceLoanAlreadyHasDeliveryLockOverlay
      ? currentEncompassLockExpires
      : sourceLoan.rate_lock_buy_side_lock_expires || null;
    // Increment request counter to prevent race conditions
    const thisRequestId = ++loadRequestIdRef.current;
    loadInFlightRef.current = thisRequestId;
    loanLoadFailedRef.current = false;
    setLoanLoadFailed(false);
    log.info('Loading loan', { loanNumber: loan.trans_details_loan_number, encompassId: loan.encompass_loan_id, requestId: thisRequestId });
    try {

    // =====================================================
    // STEP 1: Reset ALL state to defaults BEFORE setting new loan
    // This prevents old values from persisting when switching loans
    // =====================================================
    setReconId(null);
    setStoredVolumeBonusAmount(0);
    setReconInvestorId(null);
    setServicerSelection({});
    setStatus('draft');
    setTerminalAccounting(null);
    setLenderCreditTimingBasis('total');
    setRetainedClosingFeeBasis('expandedLeFees');
    setKeyDates(DEFAULT_KEY_DATES);
    setCosts(DEFAULT_COSTS);
    feeContextRef.current = null;
    feeReconUpdatedAtRef.current = null;
    deferredFeeCaptureRef.current = null;
    setFeeAccounting(null);
    setFeeCaptureError(null);
    setRevenueRaw(DEFAULT_REVENUE);
    setManualAdjustments([]);
    setCompensation(DEFAULT_COMPENSATION);
    setPricing(DEFAULT_PRICING);
    setPurchaseAdjustments([]);
    extractedLineTypesRef.current = new Set();  // Reset auto-fill provenance
    docOrderLineTypesRef.current = new Set();  // Reset doc-order feed provenance
    setWireFromInvestor(0);  // Reset wire reconciliation
    setWireFromWarehouse(0);
    setServicingInterestIncome(0);
    setServicingPrincipalCollected(0);
    setRoutingChoices({});  // Reset LO Profitability routing choices
    ++concessionRequestIdRef.current;  // Invalidate any in-flight concession fetch
    loSettingsErrorRef.current = false;  // Cleared with the loan context
    setConcessionActivePoints(0);
    setConcessionEnteredDollars(0);
    setConcessionTruth('loading');
    setLoadedProfitabilitySnapshot(null);
    setFhaUfmipSource(EMPTY_FHA_UFMIP_SOURCE);
    setFhaUfmipTruth('loading');
    setTerminalFhaUfmipSnapshot(null);
    setSelectedLOId('');  // Reset LO selection
    setOverrides([]);
    ++latestLOChangeRef.current; // Invalidate any pending comp settings fetch
    // Reset save state when switching loans
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    setSaveState('idle');
    setUploadedDocuments([]);  // Clear documents when switching loans
    setDeliveryLockSelection(null);
    setCurrentEncompassLockExpires(liveEncompassLockExpires);
    setLoCompReductionImportMetadata(null);
    lastSavedCompensationRef.current = null;
    lastSavedProfitabilityRef.current = null;
    lastSavedTerminalAccountingRef.current = null;
    lastSavedFhaUfmipRef.current = null;

    // =====================================================
    // STEP 2: Set the new loan
    // =====================================================
    setSelectedLoan(loan);
    setIsDirty(false);

    // =====================================================
    // STEP 3: Check for existing draft in database
    // If found, populate from draft (except Encompass fees which are always live)
    // =====================================================
    let loadedDraft = false;
    let existingDraft: BankerRecon | null = null;

    if (user?.organization_id && !ignoreDraft) {
      existingDraft = await bankerReconService.getBankerReconByLoan(
        user.organization_id, loan.encompass_loan_id, { throwOnError: true }
      );

      // Check if this request is stale (user selected another loan while we were fetching)
      if (thisRequestId !== loadRequestIdRef.current) {
        log.info('Ignoring stale draft fetch', { requestId: thisRequestId, currentRequestId: loadRequestIdRef.current });
        return 'stale';
      }

      // Confirm the reopened draft before showing anything from it: a recon that was
      // finalized again elsewhere, or a different one, must not be presented as reopened.
      if (reopenReload && (
        !existingDraft
        || existingDraft.id !== reopenReload.expectedReconId
        || existingDraft.status !== 'draft'
        || !existingDraft.updated_at
      )) {
        throw new Error('The reopened reconciliation could not be confirmed.');
      }

      if (existingDraft) {
        loadedDraft = true;
        log.info('Loading existing draft', { reconId: existingDraft.id, status: existingDraft.status });
        const draftLoanSnapshot = (existingDraft.loan_snapshot || {}) as Record<string, unknown>;
        const savedFeeAccounting = readReconFeeAccounting(draftLoanSnapshot);
        feeReconUpdatedAtRef.current = existingDraft.updated_at;
        setFeeAccounting(savedFeeAccounting);
        const draftCureAppliedLenderCredit =
          toNumber(draftLoanSnapshot.cure_applied_lender_credit) ||
          toNumber(loan.cure_applied_lender_credit);

        // Invalidate any in-flight late-match profile fetch so it won't clobber saved override fields
        ++latestLOChangeRef.current;

        // Set recon ID and status from draft
        const isLoadedTerminalRecon = ['finalized', 'pushed'].includes(existingDraft.status);
        setReconId(existingDraft.id);
        setStoredVolumeBonusAmount(toNumber(existingDraft.volume_bonus_amount));
        setReconInvestorId(existingDraft.investor_id ?? null);
        setServicerSelection({
          servicing_template_id: existingDraft.servicing_template_id ?? null,
          servicing_template_selected_by: existingDraft.servicing_template_selected_by ?? null,
          servicing_template_selected_at: existingDraft.servicing_template_selected_at ?? null,
          servicing_template_selection_source: existingDraft.servicing_template_selection_source ?? null,
          servicing_template_evidence: existingDraft.servicing_template_evidence ?? null
        });
        setStatus(existingDraft.status as BankerReconStatus);
        setLenderCreditTimingBasis(isLoadedTerminalRecon && !savedFeeAccounting ? 'field4794' : 'total');
        setRetainedClosingFeeBasis(isLoadedTerminalRecon && !savedFeeAccounting ? 'legacy' : 'expandedLeFees');
        setTerminalAccounting(isLoadedTerminalRecon
          ? {
            loPayout: existingDraft.lo_payout === null || existingDraft.lo_payout === undefined ? null : toNumber(existingDraft.lo_payout),
            companyRevenue: existingDraft.company_revenue === null || existingDraft.company_revenue === undefined ? null : toNumber(existingDraft.company_revenue),
            gosAmount: existingDraft.gos_amount === null || existingDraft.gos_amount === undefined ? null : toNumber(existingDraft.gos_amount),
            netProfit: existingDraft.net_profit === null || existingDraft.net_profit === undefined ? null : toNumber(existingDraft.net_profit)
          }
          : null);
        if (isLoadedTerminalRecon) {
          setFhaUfmipSource(
            fhaUfmipSourceFromRecon(existingDraft as unknown as Record<string, unknown>)
          );
          setFhaUfmipTruth('loaded');
          setTerminalFhaUfmipSnapshot({
            flowMode: existingDraft.fha_ufmip_flow_mode || 'not_applicable',
            companyVarianceAmount: toNumber(existingDraft.fha_ufmip_company_variance)
          });
        }

        // LO Profitability: keep the persisted snapshot for terminal-recon display,
        // and rehydrate the user's per-credit routing choices for editable drafts.
        setLoadedProfitabilitySnapshot({
          lo_profitability_gross: existingDraft.lo_profitability_gross ?? null,
          lo_profitability_net: existingDraft.lo_profitability_net ?? null,
          lo_profitability_components: existingDraft.lo_profitability_components ?? null
        });
        const savedRouting = existingDraft.profitability_routing;
        if (savedRouting && typeof savedRouting === 'object') {
          const choices: Partial<Record<RoutableCreditType, { target: ProfitabilityRoutingTarget; amount?: number }>> = {};
          for (const type of ROUTABLE_CREDIT_TYPES) {
            const entry = savedRouting[type];
            if (entry && isAllowedProfitabilityRoutingTarget(type, entry.target)) {
              choices[type] = { target: entry.target, amount: toNumber(entry.amount) };
            }
          }
          setRoutingChoices(choices);
        }

        // Populate key dates from draft
        setKeyDates({
          closingDate: existingDraft.closing_date || loan.closing_date || '',
          fundsToEscrow: existingDraft.funds_to_escrow_date || '',
          fundsReceived: existingDraft.funds_received_date || '',
          warehouseDays: toNumber(existingDraft.warehouse_days),
          dailyInterestRate: toNumber(existingDraft.warehouse_daily_rate)
        });

        // Populate pricing from draft (coerce to numbers - Supabase returns numeric as strings)
        setPricing({
          rateLockPrice: toNumber(existingDraft.expected_rate_lock_price),
          profitMargin: toNumber(existingDraft.company_margin_percent),
          actualPricePercent: toNumber(existingDraft.actual_purchase_price)
        });

        // Populate purchase adjustments from draft
        if (existingDraft.purchase_adjustments && Array.isArray(existingDraft.purchase_adjustments)) {
          setPurchaseAdjustments(existingDraft.purchase_adjustments);
        }

        // Populate compensation from draft (deductions loaded later from line items)
        setCompensation(prev => ({
          type: existingDraft.compensation_type || 'flat_fee',
          value: toNumber(existingDraft.compensation_value),
          notes: '',
          deductions: prev.deductions
        }));

        // Restore LO selection from saved draft
        if (existingDraft.lo_user_id) {
          setSelectedLOId(existingDraft.lo_user_id);
        }

        // Restore override fields from draft (prefer overrides[] array, fallback to legacy columns)
        const savedOverrides = normalizeDraftOverrides(existingDraft.overrides);
        if (savedOverrides.length > 0) {
          setOverrides(savedOverrides);
        } else if (existingDraft.override_bps && existingDraft.override_recipient_id) {
          setOverrides([{
            id: crypto.randomUUID(),
            recipientId: existingDraft.override_recipient_id,
            recipientName: existingDraft.override_recipient
              ? `${existingDraft.override_recipient.first_name} ${existingDraft.override_recipient.last_name}`
              : null,
            bps: toNumber(existingDraft.override_bps)
          }]);
        }

        // Populate investor purchase date from draft
        if (existingDraft.investor_purchase_date) {
          setInvestorPurchaseDate(existingDraft.investor_purchase_date);
        }

        // Populate first payment to investor from draft
        if (existingDraft.first_payment_to_investor) {
          setFirstPaymentToInvestor(existingDraft.first_payment_to_investor);
        }

        // Populate wire reconciliation amounts from draft
        if (existingDraft.wire_from_investor !== undefined && existingDraft.wire_from_investor !== null) {
          setWireFromInvestor(toNumber(existingDraft.wire_from_investor));
        }
        if (existingDraft.wire_from_warehouse !== undefined && existingDraft.wire_from_warehouse !== null) {
          setWireFromWarehouse(toNumber(existingDraft.wire_from_warehouse));
        }

        // Terminal recons are immutable accounting records; keep stored servicing values.
        if (isLoadedTerminalRecon) {
          setServicingInterestIncome(toNumber(existingDraft.servicing_interest_income));
          setServicingPrincipalCollected(toNumber(existingDraft.servicing_principal_collected));
        }

        // Load line items and populate costs/revenue (except Encompass fees)
        const rawLineItems = await bankerReconService.getLineItems(existingDraft.id, { throwOnError: true });

        // Check again if request is stale after second async call
        if (thisRequestId !== loadRequestIdRef.current) {
          log.info('Ignoring stale line items fetch', { requestId: thisRequestId, currentRequestId: loadRequestIdRef.current });
          return 'stale';
        }

        // Manual adjustments load into their own list and stay out of every mapping below
        // (incl. the buydown pre-pass, which reads loan_revenue descriptions).
        setManualAdjustments(
          rawLineItems
            .map(manualAdjustmentFromLineItem)
            .filter((entry): entry is ManualReconAdjustment => entry !== null)
        );
        const lineItems = rawLineItems.filter(li => !isManualAdjustmentLineType(li.line_type));

        // Preserve auto-fill provenance across reloads (source:'extracted' line items), keyed
        // `${category}:${baseType}` to match buildLineItemsFromState's dynamic-id lookup.
        extractedLineTypesRef.current = new Set(
          lineItems
            .filter(li => li.source === 'extracted')
            .map(li => `${li.category}:${li.category === 'investor_cost' ? canonicalInvestorCostBaseType(li.line_type, li.description) : li.line_type.replace(/_\d+$/, '')}`)
        );

        // Preserve doc-order feed provenance across reloads (source:'doc_order' loan-revenue rows),
        // keyed by the exact deterministic line_type.
        docOrderLineTypesRef.current = new Set(
          lineItems
            .filter(li => li.source === 'doc_order')
            .map(li => li.line_type)
        );

        if (lineItems.length > 0) {
          log.info('Loading line items from draft', { count: lineItems.length });

          // Map line items back to costs state (coerce amounts - Supabase returns numeric as strings)
          // Get all investor_cost line items from the database
          const investorCostItems = lineItems.filter(li => li.category === 'investor_cost');
          const legacyLoanRevenueBuydownAmount = isLoadedTerminalRecon
            ? 0
            : lineItems
              .filter(li => li.category === 'loan_revenue' && isBuydownLineType(li.line_type, li.description))
              .reduce((sum, li) => sum + toNumber(li.amount), 0);

          setCosts(prev => {
            // Warehouse costs - update amounts from saved data
            const updatedWarehouse = prev.warehouse.map(item => {
              const lineItem = lineItems.find(li => li.category === 'warehouse_cost' && li.line_type === item.id);
              return lineItem ? { ...item, amount: toNumber(lineItem.amount) } : item;
            });

            // Investor costs - load ALL saved items (no default list anymore).
            // Reserved type stripped on EDITABLE loads: a stale persisted
            // lendwise_concession row would otherwise contaminate on-screen
            // investor totals/wire expectations until the next save filters it.
            // Terminal recons keep every saved row verbatim (frozen display).
            const loadedInvestorCosts = investorCostItems
              .filter(li => isLoadedTerminalRecon || li.line_type !== 'lendwise_concession')
              .map(li => ({
                id: li.line_type,
                description: li.description,
                amount: toNumber(li.amount)
              }));

            const loadedCosts = {
              warehouse: updatedWarehouse,
              investor: loadedInvestorCosts,
              flexcloseCredit: toNumber(existingDraft.flexclose_credit),
              warehouseHaircutPercent: toNumber(existingDraft.warehouse_haircut_percent)
            };
            return isLoadedTerminalRecon
              ? loadedCosts
              : normalizeBuydownPassThroughCostsForDraft(loadedCosts, legacyLoanRevenueBuydownAmount);
          });

          // Map line items back to revenue state (except Encompass fees which are set below)
          // Encompass fee IDs are excluded - they always come from live Encompass data
          const ENCOMPASS_FEE_IDS = ['processing_fee', 'underwriting_fee', 'interest_charged'];
          const lenderCreditItem = lineItems.find(li => li.category === 'lender_cost' && li.line_type === 'lender_credit');
          const cureAppliedLenderCreditItem = lineItems.find(li => li.category === 'lender_cost' && li.line_type === 'cure_applied_lender_credit');
          const lenderCreditSourceAmount = toNumber(lenderCreditItem?.amount) || toNumber(existingDraft.lender_credit_given);
          const cureAppliedLenderCreditSourceAmount = cureAppliedLenderCreditItem
            ? toNumber(cureAppliedLenderCreditItem.amount)
            : draftCureAppliedLenderCredit;
          const lenderCreditNetsToWarehouseAdvance = lenderCreditItem
            ? (lenderCreditItem.nets_to_warehouse_advance ?? null)
            : false;
          const cureAppliedLenderCreditNetsToWarehouseAdvance = cureAppliedLenderCreditItem
            ? (cureAppliedLenderCreditItem.nets_to_warehouse_advance ?? null)
            : false;

          // Get all loan_revenue line items from the database
          const loanRevenueItems = lineItems.filter(li => li.category === 'loan_revenue');
          const revenueLoanItemsForState = isLoadedTerminalRecon
            ? loanRevenueItems
            : loanRevenueItems.filter(li => !isBuydownLineType(li.line_type, li.description));

          const setLoadedRevenue = isLoadedTerminalRecon
            ? setRevenueRaw
            : (next: React.SetStateAction<RevenueState>) => setRevenueForDraftWithBasis(next, 'expandedLeFees');
          setLoadedRevenue(prev => {
            // Start with default items, updating amounts from saved data
            const updatedLoan = prev.loan.map(item => {
              // Skip Encompass fees - they're set from live data below
              if (ENCOMPASS_FEE_IDS.includes(item.id) && !(isLoadedTerminalRecon && savedFeeAccounting)) {
                return item;
              }
              const lineItem = revenueLoanItemsForState.find(li => li.line_type === item.id);
              return lineItem
                ? {
                    ...item,
                    amount: toNumber(lineItem.amount),
                    reimbursable: lineItem.reimbursable || false,
                    fee_source: lineItem.fee_source,
                    warehouse_retained_amount: toNumber(lineItem.warehouse_retained_amount),
                    nets_to_warehouse_advance: lineItem.nets_to_warehouse_advance ?? null
                  }
                : item;
            });

            // Add any user-added fees that aren't in the default list
            const existingIds = new Set(prev.loan.map(item => item.id));
            const userAddedFees = revenueLoanItemsForState
              .filter(li => !existingIds.has(li.line_type) && !ENCOMPASS_FEE_IDS.includes(li.line_type))
              .map(li => ({
                id: li.line_type,
                description: li.description,
                amount: toNumber(li.amount),
                reimbursable: li.reimbursable || false,
                fee_source: li.fee_source,
                warehouse_retained_amount: toNumber(li.warehouse_retained_amount),
                nets_to_warehouse_advance: li.nets_to_warehouse_advance ?? null
              }));

            // Drafts keep a saved "Warehouse withheld MI" premium row on the CURRENT Closing Disclosure
            // amount; terminal recons keep their frozen rows as saved.
            const loadedLoanRows = [...updatedLoan, ...userAddedFees];
            return {
              loan: isLoadedTerminalRecon
                ? loadedLoanRows
                : syncMiPremiumRow(
                    loadedLoanRows,
                    findConventionalMiPremiumFee(loan.le_fees, loan.trans_details_loan_type)
                  ),
              investor: prev.investor.map(item => {
                const lineItem = lineItems.find(li => li.category === 'investor_revenue' && li.line_type === item.id);
                return lineItem ? { ...item, amount: toNumber(lineItem.amount) } : item;
              }),
              lenderCredit: lenderCreditSourceAmount,
              cureAppliedLenderCredit: cureAppliedLenderCreditSourceAmount,
              lenderCreditNetsToWarehouseAdvance,
              cureAppliedLenderCreditNetsToWarehouseAdvance
            };
          });
          // Load LO deductions from line items
          const deductionItems = lineItems.filter(li => li.category === 'lo_deduction');
          if (deductionItems.length > 0) {
            const deductionSourceAmounts: Record<Exclude<CompensationDeductionType, 'lo_comp_reduction'>, number> = {
              lender_credit: lenderCreditSourceAmount,
              cure_applied_lender_credit: cureAppliedLenderCreditSourceAmount
            };
            setCompensation(prev => ({
              ...prev,
              deductions: deductionItems.map(li => {
                const type = li.line_type as CompensationDeductionType;
                if (type === LO_COMP_REDUCTION_TYPE) {
                  const amount = toNumber(li.amount);
                  return {
                    type,
                    label: li.description || 'LO Comp Reduction',
                    sourceAmount: amount,
                    deductionAmount: amount,
                    readOnly: true,
                    source: 'saved' as const
                  };
                }

                const sourceAmount = deductionSourceAmounts[type];
                return {
                  type,
                  label: li.description,
                  sourceAmount: sourceAmount > 0 ? sourceAmount : toNumber(li.original_value ?? li.amount),
                  deductionAmount: toNumber(li.amount)
                };
              })
            }));
          }
        } else {
          // No line items saved yet - use lender_credit_given from recon record
          const setLoadedRevenue = isLoadedTerminalRecon
            ? setRevenueRaw
            : (next: React.SetStateAction<RevenueState>) => setRevenueForDraftWithBasis(next, 'expandedLeFees');
          setLoadedRevenue(prev => ({
            ...prev,
            lenderCredit: toNumber(existingDraft.lender_credit_given),
            cureAppliedLenderCredit: draftCureAppliedLenderCredit,
            lenderCreditNetsToWarehouseAdvance: false,
            cureAppliedLenderCreditNetsToWarehouseAdvance: false
          }));
        }
      }
    }

    const reconIsFinalized = !!existingDraft && ['finalized', 'pushed'].includes(existingDraft.status);
    const frozenFeeAccounting = reconIsFinalized ? readReconFeeAccounting(existingDraft?.loan_snapshot) : null;
    let loadedFeeContext: ReconFeeContext | null = null;
    let preserveUnverifiedFees = false;
    if (!reconIsFinalized && user?.id) {
      try {
        loadedFeeContext = await refreshBankerReconFees(loan.encompass_loan_id, user.id, !!readReconFeeAccounting(existingDraft?.loan_snapshot));
        if (thisRequestId !== loadRequestIdRef.current) {
          return 'stale';
        }
        if (loadedFeeContext) {
          feeContextRef.current = loadedFeeContext;
          setFeeAccounting(loadedFeeContext.accounting);
          loan = { ...loan, ...loadedFeeContext.loanPatch };
          setSelectedLoan(loan);
        }
      } catch (error) {
        if (thisRequestId !== loadRequestIdRef.current) {
          return 'stale';
        }
        preserveUnverifiedFees = !!readReconFeeAccounting(existingDraft?.loan_snapshot);
        setFeeCaptureError(error instanceof Error ? error.message : 'Fee capture unavailable');
      }
    }
    if (reconIsFinalized && existingDraft?.loan_snapshot) {
      loan = { ...loan, ...(existingDraft.loan_snapshot as Record<string, unknown>) } as EncompassLoan;
      setSelectedLoan(loan);
      log.info('Using saved finalized banker recon loan snapshot', {
        reconId: existingDraft.id,
        loanNumber: loan.trans_details_loan_number
      });
    }

    if (!reconIsFinalized) {
      try {
        const buydownAmount = await fetchBuydownPassThroughAmount(loan.encompass_loan_id, loan);
        if (thisRequestId !== loadRequestIdRef.current) {
          log.info('Ignoring stale buydown raw-data fetch', { requestId: thisRequestId, currentRequestId: loadRequestIdRef.current });
          return 'stale';
        }
        if (buydownAmount > 0) {
          loan = withBuydownPassThroughAmount(loan, buydownAmount);
          setSelectedLoan(loan);
          applyBuydownPassThroughAmount(buydownAmount);
          log.info('Seeded buydown pass-through from Encompass raw data', {
            loanNumber: loan.trans_details_loan_number,
            amount: buydownAmount
          });
        }
      } catch (err) {
        log.warn('Failed to fetch buydown raw data for banker recon', {
          loanNumber: loan.trans_details_loan_number,
          encompassId: loan.encompass_loan_id
        }, err as Error);
      }
    }

    const reconAllowsDeliveryLock = !reconIsFinalized;
    let appliedDeliveryLock = false;
    if (user?.organization_id && reconAllowsDeliveryLock) {
      const activeDeliveryLock = await getActiveDeliveryLockSelection(loan.encompass_loan_id);
      if (thisRequestId !== loadRequestIdRef.current) {
        log.info('Ignoring stale delivery lock fetch', { requestId: thisRequestId, currentRequestId: loadRequestIdRef.current });
        return 'stale';
      }
      if (activeDeliveryLock) {
        loan = applyDeliveryLockToLoan(loan, activeDeliveryLock) as EncompassLoan;
        appliedDeliveryLock = true;
        setDeliveryLockSelection(activeDeliveryLock);
        setSelectedLoan(loan);
        log.info('Using selected delivery lock for banker recon', {
          loanNumber: loan.trans_details_loan_number,
          lockSnapshotId: activeDeliveryLock.lockSnapshotId,
          snapshotLabel: activeDeliveryLock.snapshotLabel
        });
      }
    }

    // =====================================================
    // STEP 4: ALWAYS set Encompass fees (regardless of draft)
    // Read from le_fees JSONB — same source as Loan Center's Loan Estimate.
    // le_fees is updated by the webhook in real-time so it's always current.
    // Fall back to dedicated columns only for older loans without le_fees.
    // =====================================================
    const leFees = loan.le_fees || [];
    const lePrepaidInterest = leFees.find(f => f.section === 'F' && f.name === 'Prepaid Interest');

    // Lendwise-scoped Processing/UW scalars (single source of truth — same helper used before save).
    // Credits the seller portion to revenue + the warehouse basis when the fee is paid to Lendwise.
    const { processing: processingAmount, underwriting: underwritingAmount } =
      deriveLendwiseProcessingUwScalars(leFees, loan);
    const prepaidInterestAmount = lePrepaidInterest ? toNumber(lePrepaidInterest.borrowerAmount) : toNumber(loan.fees_prepaid_interest);

    log.info('Setting Encompass fees (read-only)', {
      processing: processingAmount,
      underwriting: underwritingAmount,
      prepaidInterest: prepaidInterestAmount,
      source: leFees.length > 0 ? 'le_fees' : 'dedicated_columns'
    });

    // Credit retained Lendwise fees to net profit: Processing/UW via the scalars above;
    // Application Fee and the seller/mixed Loan Discount via mergeAutoCaptureRows. The canonical
    // REVENUE rows are re-applied idempotently before save, so they remain non-removable. Draft/future
    // retained non-reimbursable third-party fees normalize into P&L revenue at the draft state boundary.
    const setEncompassFeeRevenue = reconIsFinalized
      ? setRevenueRaw
      : (next: React.SetStateAction<RevenueState>) => setRevenueForDraftWithBasis(next, 'expandedLeFees');
    if (!frozenFeeAccounting && !preserveUnverifiedFees) {
      setEncompassFeeRevenue(prev => ({
        ...prev,
        loan: mergeAutoCaptureRows(
          prev.loan.map(item => {
            if (item.id === 'processing_fee') {
              return { ...item, amount: processingAmount };
            }
            if (item.id === 'underwriting_fee') {
              return { ...item, amount: underwritingAmount };
            }
            if (item.id === 'interest_charged') {
              return { ...item, amount: prepaidInterestAmount };
            }
            return item;
          }),
          leFees
        )
      }));
    }
    if ((frozenFeeAccounting || preserveUnverifiedFees) && existingDraft) {
      const frozenRecon = existingDraft;
      setRevenueRaw(prev => ({ ...prev, loan: prev.loan.map(item =>
        item.id === 'processing_fee' ? { ...item, amount: toNumber(frozenRecon.processing_fee) }
          : item.id === 'underwriting_fee' ? { ...item, amount: toNumber(frozenRecon.underwriting_fee) }
            : item.id === 'interest_charged' ? { ...item, amount: toNumber(frozenRecon.interest_income) }
              : item) }));
    }

    // =====================================================
    // STEP 4B: ALWAYS refresh rate lock price & margin (regardless of draft)
    // These are read-only derived from Encompass, like Step 4 fees.
    // When live fields are absent, toNumber(null) → 0 clears stale draft values.
    // =====================================================
    {
      // Sum ALL |ProfitMargin| rows (not just the first) so company_margin_percent matches investorLockPrice.
      const profitMarginRate = sumProfitMargin(loan);

      log.info('Refreshing pricing from rate lock (unconditional)', {
        rateLockPrice: loan.rate_lock_total_buy_price,
        profitMargin: profitMarginRate
      });

      setPricing(prev => ({
        ...prev,
        rateLockPrice: toNumber(loan.rate_lock_total_buy_price),
        profitMargin: toNumber(profitMarginRate)
      }));
    }

    // =====================================================
    // STEP 4C: Seed completed Doc Order feed rows (pull-at-build)
    // A completed Doc Order contributes LO/Lendwise reimbursement portions and
    // manual doc-order fees to the recon. Deterministic line_type keys keep this
    // idempotent; a saved line item with the same line_type wins (no double-seed).
    // Finalized/pushed recons are immutable accounting records — never re-seed them.
    // =====================================================
    if (!reconIsFinalized && !preserveUnverifiedFees && loan.id) {
      const docOrderFeed = loadedFeeContext
        ? { authoritative: true, rows: loadedFeeContext.docRows }
        : await bankerReconService.getDocOrderReconFeed(loan.id);

      // Bail if the user switched loans while we were fetching
      if (thisRequestId !== loadRequestIdRef.current) {
        log.info('Ignoring stale doc-order feed fetch', { metadata: { requestId: thisRequestId, currentRequestId: loadRequestIdRef.current } });
        return 'stale';
      }

      if (docOrderFeed.authoritative) {
        // Draft recons mirror the CURRENT Doc Order generation. Remove saved
        // feed rows first, then install the latest complete order's exact rows;
        // an active/reopened latest order supplies an authoritative empty set.
        // Read failures are non-authoritative and preserve the existing draft.
        const managedIds = docOrderLineTypesRef.current;
        docOrderLineTypesRef.current = new Set(docOrderFeed.rows.map(row => row.lineType));
        setRevenueForDraft(prev => ({
          ...prev,
          loan: replaceDocOrderReconRows(prev.loan, managedIds, docOrderFeed.rows)
        }));
      }
    }

    // =====================================================
    // STEP 4A: Restore documents from database
    // Find documents linked to this banker_recon OR unlinked docs for this loan
    // =====================================================
    const bankerReconDocTypes = ['warehouse_reconciliation', 'purchase_advice'];

    // Build query: docs linked to this recon OR unlinked banker-recon docs for this loan
    const docsQuery = supabase
      .from('loan_documents')
      .select('id, source_file, original_filename, document_type, metadata, created_at, banker_recon_id')
      .eq('encompass_loan_id', loan.encompass_loan_id)
      .in('document_type', bankerReconDocTypes)
      .order('created_at', { ascending: false });

    const { data: savedDocs } = await docsQuery;

    // Check if request is stale after docs fetch
    if (thisRequestId !== loadRequestIdRef.current) {
      log.info('Ignoring stale docs fetch', { requestId: thisRequestId, currentRequestId: loadRequestIdRef.current });
      return 'stale';
    }

    if (savedDocs && savedDocs.length > 0) {
      // Filter: include if linked to this recon OR unlinked (null banker_recon_id)
      const relevantDocs = savedDocs.filter(doc =>
        doc.banker_recon_id === existingDraft?.id || doc.banker_recon_id === null
      );

      if (relevantDocs.length > 0) {
        const restoredDocuments: UploadedDocument[] = relevantDocs.map(doc => ({
          id: doc.id,
          file: null,  // File blob not available after reload
          fileName: doc.original_filename || 'document.pdf',
          documentType: (doc.document_type as BankerDocumentType) || 'unknown',
          confidence: doc.metadata?.classification_confidence || 1,
          storagePath: doc.source_file,
          extractedFields: doc.metadata?.extracted_data,
          uploadedAt: new Date(doc.created_at),
          processingStatus: 'complete'
        }));
        setUploadedDocuments(restoredDocuments);
        log.info('Restored saved documents', { count: restoredDocuments.length });
      }
    }

    // Track whether Net Revenue set compensation (used in Step 7 to avoid overwriting with LO settings)
    let netRevenueSetComp = false;
    let pricingItemsForRecon: Awaited<ReturnType<typeof getPricingItemsForRecon>> | null = null;

    if (!reconIsFinalized) {
      const loanAmount = toNumber(loan.trans_details_loan_amt);
      const baseLoCompBps = extractEncompassLoCompBps(loan.rate_lock_buy_side_adjustments);
      pricingItemsForRecon = await getPricingItemsForRecon(loan.encompass_loan_id, loanAmount, baseLoCompBps);

      if (thisRequestId !== loadRequestIdRef.current) {
        log.info('Ignoring stale pricing items fetch', { requestId: thisRequestId, currentRequestId: loadRequestIdRef.current });
        return 'stale';
      }

      const reduction = pricingItemsForRecon.loCompReduction;
      const nextLoCompReductionMetadata = reduction
        ? buildLoCompReductionImportMetadata({
          loanAmount,
          baseLoCompBps: reduction.baseLoCompBps,
          rawReductionBps: reduction.rawReductionBps,
          source: appliedDeliveryLock ? 'delivery_lock' : 'encompass_current'
        })
        : null;
      setLoCompReductionImportMetadata(nextLoCompReductionMetadata);
      if (!loadedDraft && nextLoCompReductionMetadata) {
        netRevenueSetComp = true;
      }

      setCompensation(prev => {
        const result = normalizeLoCompReductionForRecon({
          compensation: prev,
          metadata: nextLoCompReductionMetadata,
          reconStatus: 'draft',
          isFreshNoComp: !loadedDraft && prev.type === 'flat_fee' && toNumber(prev.value) === 0 && (prev.deductions ?? []).length === 0
        });

        if (result.status === 'applied') {
          setIsDirty(true);
        } else if (result.status === 'blocked') {
          log.warn('LO comp reduction normalization blocked during load', {
            loanNumber: loan.trans_details_loan_number,
            reason: result.reason
          });
        } else if (result.reason === 'manual_or_mismatched_comp' && nextLoCompReductionMetadata) {
          log.warn('LO comp reduction import skipped for manual compensation mismatch', {
            loanNumber: loan.trans_details_loan_number,
            reason: result.reason
          });
          toast({
            title: 'LO comp reduction not imported',
            description: 'Product & Price has an LO comp reduction, but this draft has a manual compensation value. Review LO Compensation before finalizing.'
          });
        }

        return result.compensation;
      });
    }

    // =====================================================
    // STEP 5: If no draft loaded, auto-populate other fields from Encompass
    // =====================================================
    if (!loadedDraft) {
      // Closing date
      if (loan.closing_date) {
        setKeyDates(prev => ({
          ...prev,
          closingDate: loan.closing_date || ''
        }));
      }

      // (Pricing from rate lock data moved to Step 4B — runs unconditionally)

      // =====================================================
      // STEP 6: Auto-populate from loan_pricing_items (Net Revenue data)
      // =====================================================
      const loanAmount = toNumber(loan.trans_details_loan_amt);
      const baseLoCompBps = extractEncompassLoCompBps(loan.rate_lock_buy_side_adjustments);
      const pricingItems = pricingItemsForRecon
        ?? await getPricingItemsForRecon(loan.encompass_loan_id, loanAmount, baseLoCompBps);

      // netRevenueSetComp is declared above the if-block so Step 7 can read it

      if (pricingItems.hasData) {
        log.info('Auto-populating from Net Revenue pricing items', pricingItems);

        // LO Compensation
        if (!loCompReductionImportRef.current && toNumber(pricingItems.loCompensation.value) > 0) {
          setCompensation(prev => ({
            ...prev,
            type: pricingItems.loCompensation.type,
            value: toNumber(pricingItems.loCompensation.value),
            notes: 'Auto-populated from Net Revenue'
          }));
          netRevenueSetComp = true;
        }

        // Lender Credit
        if (toNumber(pricingItems.lenderCreditTotal) > 0) {
          setRevenueForDraft(prev => ({
            ...prev,
            lenderCredit: toNumber(pricingItems.lenderCreditTotal)
          }));
        }

      }

    }

    // =====================================================
    // STEP 6b: Refresh lender credit and cure from live Encompass for non-finalized recons
    // Total lender credit = Field 4794 + LENPCC (additive, not alternatives)
    //   - Field 4794 (trans_details_non_specific_lender_credit) — canonical typed column
    //   - LENPCC (le_fields) — total lender-paid closing costs
    //   - Cure applied lender credit comes from cure_applied_lender_credit (FV.X396)
    // NOT URLA.X149 — unreliable (can contain garbage values).
    // Finalized/pushed recons keep the saved line items and loan_snapshot.
    // =====================================================
    if (!reconIsFinalized) {
      const { values: liveCredits, loanPatch } = await fetchLiveLenderCreditValues(loan.encompass_loan_id, loan);

      // Check again if request is stale after async call
      if (thisRequestId !== loadRequestIdRef.current) {
        log.info('Ignoring stale lender credit fetch', { requestId: thisRequestId, currentRequestId: loadRequestIdRef.current });
        return 'stale';
      }

      if (loanPatch) {
        loan = { ...loan, ...loanPatch } as EncompassLoan;
      }
      applyLiveLenderCreditValues(liveCredits, loanPatch);
      log.info('Refreshed lender credit and cure from Encompass', {
        lenderCredit: liveCredits.lenderCredit,
        cureAppliedLenderCredit: liveCredits.cureAppliedLenderCredit
      });
    }

    // =====================================================
    // STEP 6c: Refresh company concessions for ALL non-finalized recons.
    // Runs on fresh loads AND reopened drafts — draft reloads previously skipped
    // the concession read entirely, persisting concession-blind LO Profitability.
    // On failure the profitability section shows "Concessions unavailable" and
    // saves are blocked (fail closed) until a read succeeds.
    // =====================================================
    if (!reconIsFinalized && loan.id) {
      await fetchConcessionTruth(loan.id);

      // Check again if request is stale after async call
      if (thisRequestId !== loadRequestIdRef.current) {
        log.info('Ignoring stale concession fetch', { metadata: { requestId: thisRequestId, currentRequestId: loadRequestIdRef.current } });
        return 'stale';
      }
    }

    // =====================================================
    // STEP 6d: Load the canonical FHA UFMIP snapshot for every editable loan.
    // The dedicated read throws on missing/deleted rows, not_read, read_error,
    // anomalous statuses, or non-finite amounts. Keep that distinction visible
    // and fail closed at save/finalize rather than interpreting it as $0.
    // =====================================================
    if (!reconIsFinalized && user?.organization_id) {
      try {
        const canonicalFhaUfmip = await bankerReconService.getCanonicalFhaUfmip(
          user.organization_id,
          loan.encompass_loan_id
        );
        if (thisRequestId !== loadRequestIdRef.current) {
          return 'stale';
        }
        setFhaUfmipSource(canonicalFhaUfmip);
        setFhaUfmipTruth('loaded');
      } catch (err) {
        if (thisRequestId !== loadRequestIdRef.current) {
          return 'stale';
        }
        setFhaUfmipSource({
          ...EMPTY_FHA_UFMIP_SOURCE,
          captureStatus: 'read_error'
        });
        setFhaUfmipTruth('error');
        log.warn('Failed to load canonical FHA UFMIP', {
          metadata: {
            loanNumber: loan.trans_details_loan_number,
            encompassId: loan.encompass_loan_id
          }
        }, err as Error);
      }
    }

    // =====================================================
    // STEP 7: Auto-match LO using 4-tier matching (for fresh loans only)
    // Draft loans already had LO restored in Step 3
    // =====================================================
    if (!loadedDraft && loanOfficers.length > 0) {
      let matchedOfficer: User | undefined;
      const loFirstName = loan.loan_officer_first_name?.toLowerCase().trim();
      const loLastName = loan.loan_officer_last_name?.toLowerCase().trim();
      const loEncompassId = loan.loan_officer_id;

      // Tier 1: Match by Encompass user ID (most reliable)
      if (loEncompassId) {
        matchedOfficer = loanOfficers.find(officer =>
          officer.encompassUserId === loEncompassId
        );
      }

      // Tier 2: Exact first + last name match
      if (!matchedOfficer && loFirstName && loLastName) {
        matchedOfficer = loanOfficers.find(officer =>
          officer.firstName?.toLowerCase().trim() === loFirstName &&
          officer.lastName?.toLowerCase().trim() === loLastName
        );
      }

      // Tier 3: Last name only match
      if (!matchedOfficer && loLastName) {
        matchedOfficer = loanOfficers.find(officer =>
          officer.lastName?.toLowerCase().trim() === loLastName
        );
      }

      // Tier 4: Partial last name match
      if (!matchedOfficer && loLastName) {
        matchedOfficer = loanOfficers.find(officer =>
          officer.lastName?.toLowerCase().includes(loLastName) ||
          loLastName.includes(officer.lastName?.toLowerCase() || '')
        );
      }

      if (matchedOfficer) {
        setSelectedLOId(matchedOfficer.id);
        log.info('Auto-matched loan officer', {
          matchedId: matchedOfficer.id,
          name: `${matchedOfficer.firstName} ${matchedOfficer.lastName}`
        });

        // Always fetch comp settings (override fields are independent of compensation)
        const loChangeId = latestLOChangeRef.current;
        try {
          const compSettings = await bankerReconService.getCompensationSettings(matchedOfficer.id);
          if (thisRequestId !== loadRequestIdRef.current) {
            return 'stale';
          }
          // Also bail if user manually changed LO during the fetch
          if (loChangeId !== latestLOChangeRef.current) {
            return 'loaded';
          }

          // Override always populates from LO profile (seeds single default entry)
          if (compSettings?.override_recipient_id && compSettings?.banked_override_bps) {
            setOverrides([{
              id: crypto.randomUUID(),
              recipientId: compSettings.override_recipient_id,
              recipientName: compSettings.override_recipient_name ?? null,
              bps: compSettings.banked_override_bps
            }]);
          } else {
            setOverrides([]);
          }

          // Compensation only if Net Revenue didn't already set it and user hasn't entered one
          if (!netRevenueSetComp && compSettings?.banked_compensation && toNumber(compensationValueRef.current) === 0) {
            setCompensation(prev => ({
              ...prev,
              type: 'percentage',
              value: compSettings.banked_compensation,
              notes: 'Auto-populated from LO compensation settings'
            }));
          }
          loSettingsErrorRef.current = false;
        } catch (settingsError) {
          log.error('Failed to fetch comp settings during auto-match', { metadata: { officerId: matchedOfficer.id } }, settingsError as Error);
          // Fail closed for the CURRENT load + LO context only — a stale
          // rejection after switching must not re-block the new context.
          if (thisRequestId === loadRequestIdRef.current && loChangeId === latestLOChangeRef.current) {
            loSettingsErrorRef.current = true;
            toast({
              title: 'LO settings could not be verified',
              description: 'The LO compensation settings read failed. Saving will retry it automatically.',
              variant: 'destructive'
            });
          }
        }
      } else {
        log.warn('Could not auto-match loan officer', {
          encompassId: loEncompassId,
          name: `${loan.loan_officer_first_name} ${loan.loan_officer_last_name}`
        });
      }
    }
    return 'loaded';
    } catch (error) {
      // An older request cannot lock the newer loan's screen.
      if (thisRequestId !== loadRequestIdRef.current) {
        return 'stale';
      }
      loanLoadFailedRef.current = true;
      setLoanLoadFailed(true);
      log.error('Failed to load reconciliation', { metadata: { requestId: thisRequestId } },
        error instanceof Error ? error : new Error('Reconciliation load failed'));
      // Reopen owns its specific "reopened but reload failed" message and lock.
      if (!reopenReload) {
        toast({
          title: 'Reconciliation could not be loaded',
          description: 'Refresh the page before editing or saving. Saved entries have not been changed.',
          variant: 'destructive'
        });
      }
      return 'failed';
    } finally {
      // Release load ownership (unless a newer load already took over).
      if (loadInFlightRef.current === thisRequestId) {
        loadInFlightRef.current = 0;
        setLoanLoadSettled(count => count + 1);
      }
    }
  }, [
    user?.organization_id,
    user?.id,
    loanOfficers,
    fetchLiveLenderCreditValues,
    fetchBuydownPassThroughAmount,
    fetchConcessionTruth,
    applyLiveLenderCreditValues,
    applyBuydownPassThroughAmount,
    setOverrides,
    setRevenueForDraft,
    setRevenueForDraftWithBasis,
    toast,
    deliveryLockSelection?.encompassLoanId,
    currentEncompassLockExpires,
    setLoCompReductionImportMetadata,
    setIsDirty
  ]);

  // Race guard: Re-run LO matching when officers finish loading after loan was already selected
  useEffect(() => {
    if (selectedLoan && !selectedLOId && !reconId && !loadingOfficers && loanOfficers.length > 0) {
      const loEncompassId = selectedLoan.loan_officer_id;
      const loFirstName = selectedLoan.loan_officer_first_name?.toLowerCase().trim();
      const loLastName = selectedLoan.loan_officer_last_name?.toLowerCase().trim();

      let matchedOfficer: User | undefined;

      if (loEncompassId) {
        matchedOfficer = loanOfficers.find(o => o.encompassUserId === loEncompassId);
      }
      if (!matchedOfficer && loFirstName && loLastName) {
        matchedOfficer = loanOfficers.find(o =>
          o.firstName?.toLowerCase().trim() === loFirstName &&
          o.lastName?.toLowerCase().trim() === loLastName
        );
      }
      if (!matchedOfficer && loLastName) {
        matchedOfficer = loanOfficers.find(o => o.lastName?.toLowerCase().trim() === loLastName);
      }

      if (matchedOfficer) {
        setSelectedLOId(matchedOfficer.id);
        log.info('Late-matched loan officer after officers loaded', { matchedId: matchedOfficer.id });

        // Fetch override + compensation settings (mirrors main auto-match path)
        const changeId = ++latestLOChangeRef.current;
        loSettingsPendingRef.current += 1;
        bankerReconService.getCompensationSettings(matchedOfficer.id).then(compSettings => {
          if (changeId !== latestLOChangeRef.current) {
            return;
          }
          if (compSettings?.override_recipient_id && compSettings?.banked_override_bps) {
            setOverrides([{
              id: crypto.randomUUID(),
              recipientId: compSettings.override_recipient_id,
              recipientName: compSettings.override_recipient_name ?? null,
              bps: compSettings.banked_override_bps
            }]);
          } else {
            setOverrides([]);
          }
          // Also populate compensation if not already set (read from ref for current value)
          if (compSettings?.banked_compensation && toNumber(compensationValueRef.current) === 0) {
            setCompensation(prev => ({
              ...prev,
              type: 'percentage',
              value: compSettings.banked_compensation,
              notes: 'Auto-populated from LO compensation settings'
            }));
          }
          // Applied settings are UNSAVED values — bump the edit revision so a
          // concurrently completing save keeps the draft dirty.
          setIsDirty(true);
          loSettingsErrorRef.current = false;
        }).catch(err => {
          log.error('Failed to fetch comp settings on late match', { officerId: matchedOfficer.id }, err);
          // Fail closed for the CURRENT request only — stale rejections after a
          // loan/LO change must not re-block the new context.
          if (changeId === latestLOChangeRef.current) {
            loSettingsErrorRef.current = true;
            toast({
              title: 'LO settings could not be verified',
              description: 'The LO compensation settings read failed. Saving will retry it automatically.',
              variant: 'destructive'
            });
          }
        }).finally(() => {
          loSettingsPendingRef.current -= 1;
        });
      }
    }
  }, [selectedLoan, selectedLOId, reconId, loadingOfficers, loanOfficers, setOverrides, setIsDirty, toast]);

  useEffect(() => {
    if (!selectedLoan?.encompass_loan_id || status !== 'draft' || viewMode !== 'new') {
      return;
    }

    const encompassLoanId = selectedLoan.encompass_loan_id;
    let active = true;

    const rereadLiveCredits = async () => {
      if (feeContextRef.current || feeAccounting) {
        // Versioned sources refresh together on Save; never apply just one credit column.
        return;
      }
      try {
        const { values, loanPatch } = await fetchLiveLenderCreditValues(encompassLoanId, selectedLoanRef.current);
        if (!active) {
          return;
        }
        // Never mutate revenue while a save/finalize is persisting a snapshot —
        // an interleaved apply would desync screen from the stored snapshot.
        // Mark the refresh as OWED so the value is re-read (not lost) once the
        // operation unlocks.
        if (saveInFlightRef.current || finalizeInFlightRef.current) {
          pendingLiveCreditRefreshRef.current = true;
          log.info('Deferred realtime lender-credit apply during persistence', { metadata: { encompassLoanId } });
          return;
        }
        applyLiveLenderCreditValues(values, loanPatch, { showToast: false });
      } catch (err) {
        log.warn('Failed to refresh lender credit after realtime subscribe', { encompassLoanId }, err);
      }
    };

    const channel = supabase
      .channel(`banker-recon-lender-credit-${encompassLoanId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'encompass_loans',
          filter: `encompass_loan_id=eq.${encompassLoanId}`
        },
        (payload) => {
          // Record the capture BEFORE choosing a branch: during a Reopen reload the fee
          // context is briefly empty, so a versioned recon's event can take the legacy branch.
          if (saveInFlightRef.current || finalizeInFlightRef.current) {
            const deferredCapture = (payload.new as Record<string, unknown>).le_fee_capture as { status?: string; signature?: string } | null;
            deferredFeeCaptureRef.current = { status: deferredCapture?.status, signature: deferredCapture?.signature };
          }
          if (feeContextRef.current || feeAccounting) {
            const row = payload.new as Record<string, unknown>;
            const capture = row.le_fee_capture as { status?: string; signature?: string } | null;
            if (!saveInFlightRef.current && !finalizeInFlightRef.current
              && (capture?.status !== 'complete' || capture.signature !== (feeContextRef.current?.accounting ?? feeAccounting)?.capture.signature)) {
              setFeeCaptureError('Fee sources changed. Save Draft to refresh and review the updated amounts.');
            }
            return;
          }
          // Same persistence guard as rereadLiveCredits: never mutate revenue
          // while a save/finalize snapshot is in flight. The event is NOT lost:
          // the owed-refresh flag triggers an authoritative re-read after the
          // operation unlocks (and forces a finalize to abort pre-freeze).
          if (saveInFlightRef.current || finalizeInFlightRef.current) {
            pendingLiveCreditRefreshRef.current = true;
            log.info('Deferred realtime lender-credit event during persistence', { metadata: { encompassLoanId } });
            return;
          }
          const row = payload.new as Partial<EncompassLoan>;
          applyLiveLenderCreditValues(getLiveLenderCreditValuesFromLoan(row), row, { showToast: true });
        }
      )
      .subscribe((subscribeStatus) => {
        if (subscribeStatus === 'SUBSCRIBED') {
          void rereadLiveCredits();
        }
      });

    // Expose the authoritative re-reader so save/finalize can settle an owed
    // refresh right after they unlock.
    rereadLiveCreditsRef.current = rereadLiveCredits;

    return () => {
      active = false;
      rereadLiveCreditsRef.current = null;
      supabase.removeChannel(channel);
    };
  }, [
    feeAccounting,
    selectedLoan?.encompass_loan_id,
    status,
    viewMode,
    fetchLiveLenderCreditValues,
    applyLiveLenderCreditValues
  ]);

  // Public handler - checks dirty state before loading
  const handleLoanSelect = useCallback((loan: EncompassLoan) => {
    // Skip if same loan is already selected (no-op to preserve unsaved edits)
    if (selectedLoan && loan.encompass_loan_id === selectedLoan.encompass_loan_id) {
      return;
    }
    // Never switch loans mid-save/mid-delete/mid-finalize: a resumed mutation
    // must not find another loan's state on screen, and a finalize must never
    // publish terminal state into a newly selected loan. (Save-and-Switch
    // awaits the save first.)
    if (saveInFlightRef.current || deleteInFlightRef.current || finalizeInFlightRef.current) {
      toast({
        title: finalizeInFlightRef.current ? 'Finalize in progress' : saveInFlightRef.current ? 'Save in progress' : 'Deletion in progress',
        description: 'Please wait for the current operation to finish before switching loans.'
      });
      return;
    }
    // If dirty and switching to a different loan, prompt to save
    if (isDirty && selectedLoan) {
      log.info('Dirty state detected, showing unsaved changes modal');
      setPendingLoanAction({ type: 'switch', loan });
      return;
    }
    // Otherwise proceed with load
    loadLoanInternal(loan);
  }, [isDirty, selectedLoan, loadLoanInternal, toast]);

  // Only called after the operation and unsaved-change checks have passed.
  const clearLoanInternal = useCallback(() => {
    // Invalidate any stale in-flight load or save against the cleared loan.
    ++loadRequestIdRef.current;
    loanLoadFailedRef.current = false;
    setLoanLoadFailed(false);
    setSelectedLoan(null);
    setReconId(null);
    setStoredVolumeBonusAmount(0);
    setReconInvestorId(null);
    setServicerSelection({});  // Clear recon ID to prevent targeting wrong loan on finalize
    // Reset all form state using defaults from types.ts
    setKeyDates(DEFAULT_KEY_DATES);
    setCosts(DEFAULT_COSTS);
    feeContextRef.current = null;
    feeReconUpdatedAtRef.current = null;
    deferredFeeCaptureRef.current = null;
    setFeeAccounting(null);
    setFeeCaptureError(null);
    setRevenueRaw(DEFAULT_REVENUE);
    setManualAdjustments([]);
    setCompensation(DEFAULT_COMPENSATION);
    setPricing(DEFAULT_PRICING);
    setStatus('draft');
    setTerminalAccounting(null);
    setLenderCreditTimingBasis('total');
    setRetainedClosingFeeBasis('expandedLeFees');
    setInvestorPurchaseDate('');  // Clear investor purchase date
    setFirstPaymentToInvestor('');  // Clear first payment to investor
    setWireFromInvestor(0);  // Clear wire reconciliation
    setWireFromWarehouse(0);
    setServicingInterestIncome(0);
    setServicingPrincipalCollected(0);
    setSelectedLOId('');  // Clear LO selection
    setOverrides([]);
    setDeliveryLockSelection(null);
    setCurrentEncompassLockExpires(null);
    setLoCompReductionImportMetadata(null);
    setRoutingChoices({});  // Reset LO Profitability routing choices
    ++concessionRequestIdRef.current;  // Invalidate any in-flight concession fetch
    loSettingsErrorRef.current = false;  // Cleared with the loan context
    setConcessionActivePoints(0);
    setConcessionEnteredDollars(0);
    setConcessionTruth('loading');
    setLoadedProfitabilitySnapshot(null);
    setFhaUfmipSource(EMPTY_FHA_UFMIP_SOURCE);
    setFhaUfmipTruth('loading');
    setTerminalFhaUfmipSnapshot(null);
    lastSavedCompensationRef.current = null;
    lastSavedProfitabilityRef.current = null;  // Full symmetry with the loan-switch reset
    lastSavedTerminalAccountingRef.current = null;
    lastSavedFhaUfmipRef.current = null;
    ++latestLOChangeRef.current; // Invalidate any pending comp settings fetch
    setIsDirty(false);  // Reset dirty state when clearing
    setUploadedDocuments([]);  // Clear any uploaded documents
    // Reset save state when clearing loan
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    setSaveState('idle');
  }, [setOverrides, setLoCompReductionImportMetadata, setIsDirty]);

  const handleLoanClear = useCallback(() => {
    if (saveInFlightRef.current || deleteInFlightRef.current || finalizeInFlightRef.current) {
      toast({
        title: finalizeInFlightRef.current ? 'Finalize in progress' : saveInFlightRef.current ? 'Save in progress' : 'Deletion in progress',
        description: 'Please wait for the current operation to finish before clearing the loan.'
      });
      return;
    }
    if (isDirty && selectedLoan) {
      setPendingLoanAction({ type: 'clear' });
      return;
    }
    clearLoanInternal();
  }, [isDirty, selectedLoan, clearLoanInternal, toast]);

  /**
   * Delete the current draft and reload fresh data from Encompass
   */
  const handleDeleteDraft = useCallback(async () => {
    if (!reconId || !selectedLoan || loanLoadFailedRef.current) {
      return;
    }

    // Never delete mid-save/mid-finalize: the reconId on screen may be about to
    // change, and a resumed save/finalize must not race a deletion.
    if (saveInFlightRef.current || finalizeInFlightRef.current) {
      toast({
        title: finalizeInFlightRef.current ? 'Finalize in progress' : 'Save in progress',
        description: 'Please wait for the current operation to finish before deleting the draft.'
      });
      return;
    }

    // Reentrancy: a second click while a deletion is already running must not
    // launch a parallel delete — its completion would clear the shared flag
    // while the first is still active, reopening the save/switch race.
    if (deleteInFlightRef.current) {
      return;
    }

    // Guard: Only allow deleting drafts
    if (status !== 'draft') {
      toast({
        variant: 'destructive',
        title: 'Cannot Delete',
        description: 'Only draft recons can be deleted.'
      });
      return;
    }

    deleteInFlightRef.current = true;
    try {
      const { error } = await supabase
        .from('banker_recons')
        .delete()
        .eq('id', reconId)
        .eq('status', 'draft');  // Extra safety: only delete if still a draft

      if (error) {
throw error;
}

      toast({
        title: 'Draft Deleted',
        description: 'Reloading from Encompass...'
      });

      // Clear reconId and reload loan with ignoreDraft flag to get fresh Encompass data
      setReconId(null);
    setReconInvestorId(null);
    setServicerSelection({});
      loadLoanInternal(selectedLoan, true);  // ignoreDraft = true

    } catch (error) {
      log.error('Failed to delete draft', { reconId }, error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete draft'
      });
    } finally {
      deleteInFlightRef.current = false;
    }
  }, [reconId, selectedLoan, status, loadLoanInternal, toast]);

  /**
   * Handle LO selection change from dropdown
   * Auto-populates compensation from LO settings (unless Net Revenue already set it)
   */
  const handleLOChange = useCallback(async (officerId: string) => {
    if (volumeBonus.preview?.allocated && officerId !== selectedLOId) {
      toast({ title: 'Volume bonus already allocated', description: 'This installment stays attached to its original loan officer.', variant: 'destructive' });
      return;
    }
    const changeId = ++latestLOChangeRef.current;
    loSettingsErrorRef.current = false; // fresh attempt for the newly selected LO
    setSelectedLOId(officerId);
    // Clear override state immediately so a quick save can't persist the previous LO's overrides
    setOverrides([]);
    setIsDirty(true);

    if (!officerId) {
      return;
    }

    loSettingsPendingRef.current += 1;
    try {
      const compSettings = await bankerReconService.getCompensationSettings(officerId);

      // Discard if another LO was selected while we were fetching
      if (changeId !== latestLOChangeRef.current) {
        return;
      }

      // Override fields always populate from LO profile (seeds single default entry)
      if (compSettings?.override_recipient_id && compSettings?.banked_override_bps) {
        setOverrides([{
          id: crypto.randomUUID(),
          recipientId: compSettings.override_recipient_id,
          recipientName: compSettings.override_recipient_name ?? null,
          bps: compSettings.banked_override_bps
        }]);
      } else {
        setOverrides([]);
      }

      // Only auto-populate compensation if not already set (read from ref for current value)
      if (compSettings?.banked_compensation && toNumber(compensationValueRef.current) === 0) {
        setCompensation(prev => ({
          ...prev,
          type: 'percentage',
          value: compSettings.banked_compensation,
          notes: 'Auto-populated from LO compensation settings'
        }));
      }
      // The applied settings are UNSAVED accounting values — bump the edit
      // revision so any concurrently completing save keeps the draft dirty.
      setIsDirty(true);
      loSettingsErrorRef.current = false;
    } catch (error) {
      log.error('Failed to fetch compensation settings', { officerId }, error);
      // Fail closed — but ONLY for the CURRENT request: a stale rejection
      // (loan/LO already changed) must not re-block the new context.
      if (changeId === latestLOChangeRef.current) {
        loSettingsErrorRef.current = true;
        toast({
          title: 'LO settings could not be verified',
          description: 'The LO compensation settings read failed. Saving will retry it automatically.',
          variant: 'destructive'
        });
      }
    } finally {
      loSettingsPendingRef.current -= 1;
    }
  }, [setOverrides, setIsDirty, toast, volumeBonus.preview?.allocated, selectedLOId]); // compensationValueRef used instead of closure capture

  // ============================================================================
  // LO PROFITABILITY ROUTING
  // ============================================================================

  /**
   * Route a single adjustment among the LO scorecard, base comp, and (for lender
   * credit) company-only treatment.
   *
   * base_comp keeps the existing payout mechanism: an `lo_deduction` row reduces
   * the LO payout (and the math adds it back as an LOCR margin). lo_profitability
   * removes that `lo_deduction` (payout unchanged → still matches Product &
   * Pricing) and lets the signed profitability `net` absorb the credit instead.
   * `company` also removes the payout deduction, but leaves the LO scorecard at
   * zero effect; the lender credit remains a company Net Profit cost.
   * Routing choices are recorded so the derivation stays stable after the
   * deduction is removed and the toggle remains reversible.
   */
  const handleProfitabilityRoutingChange = useCallback(
    (type: RoutableCreditType, target: ProfitabilityRoutingTarget) => {
      if (!isAllowedProfitabilityRoutingTarget(type, target)) {
        return;
      }
      const sourceAmount =
        type === 'lender_credit'
          ? toNumber(revenue.lenderCredit)
          : type === 'cure_applied_lender_credit'
            ? toNumber(revenue.cureAppliedLenderCredit)
            : toNumber(loCompReductionImportRef.current?.reductionAmount ?? 0);
      // Preserve the currently-routed amount (a partial deduction or a prior
      // choice) instead of resetting to the full source amount; clamp to source.
      const existing = (compensation.deductions ?? []).find(d => d.type === type);
      const currentAmount = existing
        ? toNumber(existing.deductionAmount)
        : toNumber(routingChoices[type]?.amount ?? sourceAmount);
      const clampedAmount = Math.min(Math.max(0, currentAmount), sourceAmount);

      setRoutingChoices(prev => ({ ...prev, [type]: { target, amount: clampedAmount } }));

      if (target !== 'base_comp') {
        // Profitability/company routes never reduce the actual payout.
        setCompensation(prev => ({
          ...prev,
          deductions: (prev.deductions ?? []).filter(d => d.type !== type)
        }));
      } else if (type === LO_COMP_REDUCTION_TYPE) {
        setCompensation(prev =>
          applyLoCompReductionToCompensation(prev, loCompReductionImportRef.current, 'product_pricing') ?? prev
        );
      } else {
        setCompensation(prev => {
          const others = (prev.deductions ?? []).filter(d => d.type !== type);
          return {
            ...prev,
            deductions: [
              ...others,
              {
                type,
                label: ROUTABLE_CREDIT_LABELS[type],
                sourceAmount,
                deductionAmount: clampedAmount
              }
            ]
          };
        });
      }

      setIsDirty(true);
    },
    [compensation, routingChoices, revenue.lenderCredit, revenue.cureAppliedLenderCredit, setIsDirty]
  );

  // ============================================================================
  // DOCUMENT UPLOAD HANDLERS
  // ============================================================================

  /**
   * Save draft to database
   * Returns the recon ID on success for immediate use (avoids stale closure issue)
   */
  const handleSaveDraft = useCallback(async (caller?: unknown): Promise<SavedDraftIdentity | null> => {
    if (loanLoadFailedRef.current) {
      toast({
        title: 'Reconciliation could not be loaded',
        description: 'Refresh the page before editing or saving. Saved entries have not been changed.',
        variant: 'destructive'
      });
      return null;
    }
    if (!selectedLoan || !user?.organization_id) {
      toast({
        title: 'Error',
        description: 'Please select a loan first',
        variant: 'destructive'
      });
      return null;
    }

    // External saves are blocked for the ENTIRE finalize workflow; only the
    // finalize's own preliminary save (identified by sentinel) may proceed.
    // Checked BEFORE reentrancy so an external save during the finalize's save
    // leg reports the accurate reason ("Finalize in progress").
    if (finalizeInFlightRef.current && caller !== FINALIZE_SAVE_CALLER) {
      toast({
        title: 'Finalize in progress',
        description: 'Please wait for finalization to finish.',
        variant: 'destructive'
      });
      return null;
    }

    // Reentrancy: a second invocation while a save is already running must not
    // start a parallel save — the first completion would clear the in-flight
    // flag while the second still runs, reopening every guarded race.
    if (saveInFlightRef.current) {
      toast({
        title: 'Save in progress',
        description: 'Please wait for the current save to finish.',
        variant: 'destructive'
      });
      return null;
    }

    // Terminal recons are frozen — only drafts may persist. Without this guard a
    // finalized recon could be re-saved from reset state (concession-blind
    // profitability, stale accounting) via any surface exposing the save button.
    // (The service additionally enforces this on the AUTHORITATIVE status with a
    // conditional status='draft' update — this client check is UX, not the wall.)
    if (status !== 'draft') {
      toast({
        title: 'Recon is finalized',
        description: 'Finalized recons are frozen. Unfinalize the recon before making changes.',
        variant: 'destructive'
      });
      return null;
    }

    // While a loan is loading, recon identity/status are not authoritative yet
    // (STEP 1 transiently resets status to 'draft'). Never save mid-load.
    if (loadInFlightRef.current !== 0) {
      toast({
        title: 'Loan is still loading',
        description: 'Please wait for the loan to finish loading, then save again.',
        variant: 'destructive'
      });
      return null;
    }

    // Symmetric exclusion: never save while a draft deletion is in flight — the
    // save would resurrect the recon the deletion just removed.
    if (deleteInFlightRef.current) {
      toast({
        title: 'Deletion in progress',
        description: 'Please wait for the draft deletion to finish.',
        variant: 'destructive'
      });
      return null;
    }

    // LO compensation settings are still being fetched: their application would
    // land AFTER this save's snapshot and be silently absorbed. Block until settled.
    if (loSettingsPendingRef.current > 0) {
      toast({
        title: 'LO settings still loading',
        description: 'Compensation settings for the selected LO are still loading. Try again in a moment.',
        variant: 'destructive'
      });
      return null;
    }

    // The last LO settings read FAILED — the LO's overrides/compensation are
    // unverified and must not be persisted (fail closed). Kick an automatic
    // retry: Radix Select won't re-fire onValueChange for the already-selected
    // LO, so "reselect to retry" is not a real affordance.
    if (loSettingsErrorRef.current) {
      if (selectedLOId) {
        void handleLOChange(selectedLOId);
      }
      toast({
        title: 'LO settings could not be verified',
        description: 'The LO compensation settings read failed. Retrying now — save again in a moment.',
        variant: 'destructive'
      });
      return null;
    }

    // Clear any existing success timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    setSaveState('saving');
    log.info('Saving banker recon draft', { loanNumber: selectedLoan.trans_details_loan_number });

    // Loan-generation guard: if the selected loan somehow changes mid-save
    // (switching is blocked below, so this is defense-in-depth), abort before
    // any persistence and skip every post-save state mutation.
    const saveGeneration = loadRequestIdRef.current;
    const saveIsStale = () => loadRequestIdRef.current !== saveGeneration;
    // Edits that land AFTER this point are NOT in the snapshot being persisted —
    // the post-save dirty-clear must not silently mark them as saved.
    const editRevisionAtSaveStart = editRevisionRef.current;
    const abortStaleSave = (): null => {
      log.warn('Save aborted — loan changed mid-save', { metadata: { loanNumber: selectedLoan.trans_details_loan_number } });
      setSaveState('idle');
      toast({
        title: 'Save canceled',
        description: 'The selected loan changed while saving. Nothing was persisted.',
        variant: 'destructive'
      });
      return null;
    };
    saveInFlightRef.current = true;
    formWorkflowRef.current += 1;

    try {
      const currentOverrides = overridesRef.current;
      // Re-read the slot under the current loan/LO/date before using it in P&L.
      // Finalization revalidates this token under the shared compensation lock.
      const savedVolumePreview = reconId ? await reviewedVolumeBonusForSave(reconId) : null;
      if (saveIsStale()) {
        return abortStaleSave();
      }
      const activeOverrides = getActiveOverrides(currentOverrides);
      let effectiveLoan = selectedLoan;
      let effectiveRevenue = revenue;
      let effectiveCompensation = compensation;
      let effectiveBuydownPassThroughAmount = getBuydownPassThroughAmountFromLoan(selectedLoan);
      let effectiveFeeContext = feeContextRef.current;

      if (status === 'draft') {
        const previousFeeContext = effectiveFeeContext;
        effectiveFeeContext = await refreshBankerReconFees(selectedLoan.encompass_loan_id, user.id, !!feeAccounting);
        if (saveIsStale()) {
          return abortStaleSave();
        }
        if (feeCaptureError && !effectiveFeeContext) {
          throw new Error('Fee capture is unavailable. Reload this reconciliation before using the legacy save path.');
        }
        if (effectiveFeeContext) {
          feeContextRef.current = effectiveFeeContext;
          setFeeAccounting(effectiveFeeContext.accounting);
          setFeeCaptureError(null);
        }
        const { values: liveCredits, loanPatch } = effectiveFeeContext
          ? { values: getLiveLenderCreditValuesFromLoan(effectiveFeeContext.loanPatch), loanPatch: effectiveFeeContext.loanPatch }
          : await fetchLiveLenderCreditValues(selectedLoan.encompass_loan_id, selectedLoan);
        if (saveIsStale()) {
          return abortStaleSave();
        }
        effectiveLoan = { ...selectedLoan, ...(loanPatch ?? {}) } as EncompassLoan;
        effectiveRevenue = {
          ...revenue,
          lenderCredit: liveCredits.lenderCredit,
          cureAppliedLenderCredit: liveCredits.cureAppliedLenderCredit
        };
        effectiveCompensation = clampCompensationDeductionsToLiveCredits(compensation, liveCredits);
        if (effectiveFeeContext) {
          const managed = new Set([...docOrderLineTypesRef.current, ...revenue.loan.filter(row => row.fee_source?.owner === 'doc_order').map(row => row.id)]);
          effectiveRevenue.loan = replaceDocOrderReconRows(effectiveRevenue.loan, managed, effectiveFeeContext.docRows);
          const ledger = reconcileFeePaymentLedger(effectiveRevenue.loan, effectiveFeeContext.accounting.capture);
          const issues = [...effectiveFeeContext.accounting.issues, ...ledger.issues];
          if (issues.length) {
            throw new Error(issues.join(' '));
          }
          effectiveRevenue.loan = ledger.rows;
          docOrderLineTypesRef.current = new Set(effectiveFeeContext.docRows.map(row => row.lineType));
          if (finalizeInFlightRef.current && (previousFeeContext?.accounting.capture.signature !== effectiveFeeContext.accounting.capture.signature
            || previousFeeContext?.accounting.docOrderSignature !== effectiveFeeContext.accounting.docOrderSignature)) {
            setSelectedLoan(effectiveLoan);
            setRevenueRaw(effectiveRevenue);
            setIsDirty(true);
            throw new Error('Fee sources changed. Review the refreshed amounts before finalizing.');
          }
        }
        applyLiveLenderCreditValues(liveCredits, loanPatch);
        try {
          const refreshedBuydownAmount = await fetchBuydownPassThroughAmount(selectedLoan.encompass_loan_id, effectiveLoan);
          if (saveIsStale()) {
            return abortStaleSave();
          }
          if (refreshedBuydownAmount > 0) {
            effectiveBuydownPassThroughAmount = refreshedBuydownAmount;
            effectiveLoan = withBuydownPassThroughAmount(effectiveLoan, refreshedBuydownAmount);
            setSelectedLoan(effectiveLoan);
          }
        } catch (err) {
          log.warn('Failed to refresh buydown raw data during save', {
            loanNumber: selectedLoan.trans_details_loan_number
          }, err as Error);
        }
      }

      const normalizedCompensation = normalizeLoCompReductionForRecon({
        compensation: effectiveCompensation,
        metadata: loCompReductionImportRef.current,
        reconStatus: status,
        isFreshNoComp: !reconId
          && effectiveCompensation.type === 'flat_fee'
          && toNumber(effectiveCompensation.value) === 0
          && (effectiveCompensation.deductions ?? []).length === 0
      });
      if (normalizedCompensation.status === 'blocked') {
        log.warn('Saving draft with LO comp reduction review needed', {
          loanNumber: selectedLoan.trans_details_loan_number,
          reason: normalizedCompensation.reason
        });
        toast({
          title: 'LO comp reduction needs review',
          description: 'The draft was saved with the current LO compensation values preserved. Review LO Compensation before finalizing.'
        });
      }
      effectiveCompensation = normalizedCompensation.compensation;

      // Route-aware: normalizeLoCompReductionForRecon re-adds the LOCR base-comp
      // deduction; strip it when the user routed LOCR to Profitability so the saved
      // line items + derived routing honor the profitability route (no payout cut).
      effectiveCompensation = stripProfitabilityRoutedLoCompReduction(
        effectiveCompensation,
        profitabilityRouting
      );

      // Re-upsert the auto-captured Lendwise REVENUE rows (Application Fee and the seller/mixed Loan
      // Discount) from le_fees before save/finalize so they are authoritative and cannot be dropped by
      // edits (canonical line_type, idempotent).
      // Re-derive the Lendwise-scoped Processing/UW scalars with the SAME helper used at load and apply
      // them to the scalar rows, so the folded values persist even if the loaded state predates this
      // change. computeBankedRecon (below) reads Processing/UW from effectiveRevenueForSave.
      const { processing: savedProcessing, underwriting: savedUnderwriting } =
        deriveLendwiseProcessingUwScalars(effectiveLoan?.le_fees, effectiveLoan);
      const rawEffectiveRevenueForSave = {
        ...effectiveRevenue,
        loan: mergeAutoCaptureRows(effectiveRevenue.loan, effectiveLoan?.le_fees).map(item =>
          item.id === 'processing_fee'
            ? { ...item, amount: savedProcessing }
            : item.id === 'underwriting_fee'
              ? { ...item, amount: savedUnderwriting }
              : item
        )
      };
      const normalizedRevenueForSave = status === 'draft'
        ? normalizeRevenueStateForDraft(rawEffectiveRevenueForSave, retainedClosingFeeBasis)
        : rawEffectiveRevenueForSave;
      // "Warehouse withheld MI": keep a checked premium row on the CURRENT Closing Disclosure amount
      // before persisting; buildLineItemsFromState derives the matching expense line from it.
      const miPremiumEligible = findConventionalMiPremiumFee(effectiveLoan?.le_fees, effectiveLoan?.trans_details_loan_type);
      let effectiveRevenueForSave = status === 'draft'
        ? { ...normalizedRevenueForSave, loan: syncMiPremiumRow(normalizedRevenueForSave.loan, miPremiumEligible) }
        : normalizedRevenueForSave;
      if (effectiveFeeContext) {
        const ledger = reconcileFeePaymentLedger(effectiveRevenueForSave.loan, effectiveFeeContext.accounting.capture);
        if (ledger.issues.length) {
          throw new Error(ledger.issues.join(' '));
        }
        effectiveRevenueForSave = { ...effectiveRevenueForSave, loan: ledger.rows };
      }
      const effectiveCostsForSave = status === 'draft'
        ? normalizeBuydownPassThroughCostsForDraft(costs, effectiveBuydownPassThroughAmount)
        : costs;
      const lineItems = buildLineItemsFromState(effectiveCostsForSave, effectiveRevenueForSave, effectiveCompensation, extractedLineTypesRef.current, docOrderLineTypesRef.current, miPremiumEligible?.payee ?? null, manualAdjustments);
      const processingFee = effectiveRevenueForSave.loan.find(r => r.id === 'processing_fee')?.amount || 0;
      const underwritingFee = effectiveRevenueForSave.loan.find(r => r.id === 'underwriting_fee')?.amount || 0;

      // FHA UFMIP truth — mandatory fresh canonical read for every draft save.
      // Use the returned values directly for math and persistence; React state is
      // display-only and can never authorize a save/finalize.
      let effectiveFhaUfmipSource = fhaUfmipSource;
      try {
        effectiveFhaUfmipSource = await bankerReconService.getCanonicalFhaUfmip(
          user.organization_id,
          effectiveLoan.encompass_loan_id
        );
      } catch (err) {
        if (saveIsStale()) {
          return abortStaleSave();
        }
        setFhaUfmipTruth('error');
        setSaveState('idle');
        toast({
          variant: 'destructive',
          title: 'FHA UFMIP unavailable',
          description: 'Could not verify the canonical FHA UFMIP snapshot — the draft was NOT saved. Retry after Encompass sync completes.'
        });
        log.warn('Draft save blocked by canonical FHA UFMIP read', {
          metadata: {
            loanNumber: effectiveLoan.trans_details_loan_number
          }
        }, err as Error);
        return null;
      }
      if (saveIsStale()) {
        return abortStaleSave();
      }
      setFhaUfmipSource(effectiveFhaUfmipSource);
      setFhaUfmipTruth('loaded');

      const effectiveInvestorCosts = lineItems
        .filter(item => item.category === 'investor_cost')
        .reduce((sum, item) => sum + item.amount, 0);
      const effectiveInvestorRevenue = lineItems
        .filter(item => item.category === 'investor_revenue')
        .reduce((sum, item) => sum + item.amount, 0);
      const effectiveInvestorAudit = calculateInvestorWireAudit({
        loanAmount: effectiveLoan.trans_details_loan_amt || 0,
        actualPurchasePricePercent: pricing.actualPricePercent,
        totalInvestorRevenue: effectiveInvestorRevenue,
        totalInvestorCosts: effectiveInvestorCosts,
        servicingPrincipalCollected,
        wireFromInvestor: toNumber(wireFromInvestor)
      });
      const effectiveFhaUfmipBridge = buildFhaUfmipBridge({
        loanType: effectiveLoan.trans_details_loan_type || null,
        source: effectiveFhaUfmipSource,
        lineItems,
        investorAuditBalanced: effectiveInvestorAudit.isInvestorBalanced
      });

      const effectiveReconResult = computeBankedRecon({
        loanAmount: effectiveLoan.trans_details_loan_amt || 0,
        actualPurchasePrice: pricing.actualPricePercent,
        compensationType: effectiveCompensation.type,
        compensationValue: effectiveCompensation.value,
        volumeBonusAmount: savedVolumePreview?.amount ?? 0,
        overrides: activeOverrides.map(o => ({ bps: o.bps })),
        processingFee,
        underwritingFee,
        interestIncome: effectiveRevenueForSave.loan.find(r => r.id === 'interest_charged')?.amount || 0,
        servicingInterestIncome,
        flexcloseCredit: effectiveCostsForSave.flexcloseCredit,
        loanType: effectiveLoan.trans_details_loan_type || null,
        fhaUfmip: effectiveFhaUfmipBridge,
        lineItems
      });

      // Concession truth — MANDATORY fresh read for draft saves (fail closed:
      // "never save after a failed or unknown concession read"). Uses the
      // returned values directly, never React state, so a save can't race the
      // load-time fetch. Finalize runs through this same path first.
      let effectiveConcession = {
        points: concessionActivePoints,
        dollars: concessionEnteredDollars
      };
      if (status === 'draft') {
        const freshConcession = effectiveLoan.id
          ? await fetchConcessionTruth(effectiveLoan.id)
          : null;
        if (saveIsStale()) {
          return abortStaleSave();
        }
        if (!freshConcession) {
          setSaveState('idle');
          toast({
            variant: 'destructive',
            title: 'Concessions unavailable',
            description: 'Could not verify company concessions — the draft was NOT saved. Retry in a moment.'
          });
          return null;
        }
        effectiveConcession = freshConcession;
      }

      // LO Profitability — derive routing + signed net from the SAME effective
      // revenue/compensation that produced the saved line items, so the persisted
      // snapshot matches what gets written.
      const effectiveProfitabilityRouting = deriveProfitabilityRouting({
        revenue: effectiveRevenueForSave,
        compensation: effectiveCompensation,
        loCompReductionMetadata: loCompReductionImportRef.current,
        routingChoices
      });
      const effectiveLoProfitability = computeReconLoProfitability({
        basePriceLocked: toNumber(effectiveLoan.rate_lock_total_buy_price) || 0,
        discountPoints: toNumber(effectiveLoan.discount_point_percent) || 0,
        concessionPoints: effectiveConcession.points,
        concessionDollarsEntered: effectiveConcession.dollars,
        loanAmount: toNumber(effectiveLoan.trans_details_loan_amt) || 0,
        routing: effectiveProfitabilityRouting,
        // Missing pricing data must NOT be read as confirmed loCompBps=0 (which would
        // wrongly trigger Case 2): pass undefined unless adjustments are actually present.
        loCompBps: Array.isArray(effectiveLoan.rate_lock_buy_side_adjustments)
          ? extractEncompassLoCompBps(effectiveLoan.rate_lock_buy_side_adjustments)
          : undefined,
        borrowerPaidCompDollars:
          (toNumber(effectiveLoan.origination_fee_percent) || 0) * (toNumber(effectiveLoan.trans_details_loan_amt) || 0) / 100,
        loPayoutBase: effectiveReconResult?.loPayoutBase ?? 0
      });

      // Use the selected LO from dropdown (already resolved by 4-tier matching or admin override)
      const loUserId = selectedLOId || undefined;

      // Final gate before persistence: never write with a stale loan context.
      if (saveIsStale()) {
        return abortStaleSave();
      }

      const result = await bankerReconService.saveBankerReconDraft(
        user.organization_id,
        effectiveLoan.encompass_loan_id,
        {
          ...servicerSelection,
          loan_number: effectiveLoan.trans_details_loan_number || '',
          loan_amount: toNumber(effectiveLoan.trans_details_loan_amt),
          lo_user_id: loUserId,
          overrides: currentOverrides, // persist as-is (including half-filled rows)
          // Legacy mirror: only when exactly 1 active override
          override_bps: activeOverrides.length === 1 ? activeOverrides[0].bps : null,
          override_recipient_id: activeOverrides.length === 1 ? activeOverrides[0].recipientId : null,
          closing_date: keyDates.closingDate || undefined,
          funds_to_escrow_date: keyDates.fundsToEscrow || undefined,
          funds_received_date: keyDates.fundsReceived || undefined,
          investor_purchase_date: investorPurchaseDate || undefined,
          first_payment_to_investor: firstPaymentToInvestor || undefined,
          warehouse_days: effectiveWarehouseDays(keyDates),
          warehouse_daily_rate: toNumber(keyDates.dailyInterestRate),
          expected_rate_lock_price: toNumber(pricing.rateLockPrice),
          company_margin_percent: toNumber(pricing.profitMargin),
          expected_total_price: toNumber(pricing.rateLockPrice) + toNumber(pricing.profitMargin),
          actual_purchase_price: toNumber(pricing.actualPricePercent),
          pricing_variance: toNumber(pricing.actualPricePercent) - (toNumber(pricing.rateLockPrice) + toNumber(pricing.profitMargin)),
          purchase_adjustments: purchaseAdjustments,
          // Send the explicit value (incl. 0) so clearing a previously-saved wire actually persists.
          wire_from_investor: toNumber(wireFromInvestor),
          wire_from_warehouse: toNumber(wireFromWarehouse),
          flexclose_credit: toNumber(effectiveCostsForSave.flexcloseCredit),
          warehouse_haircut_percent: toNumber(effectiveCostsForSave.warehouseHaircutPercent),
          lender_credit_given: toNumber(effectiveRevenue.lenderCredit),
          compensation_type: effectiveCompensation.type,
          compensation_value: toNumber(effectiveCompensation.value),
          lo_payout: toNumber(effectiveReconResult.loPayout),
          ...(savedVolumePreview ? { volume_bonus_amount: savedVolumePreview.amount } : {}),
          company_revenue: toNumber(effectiveReconResult.companyRevenue),
          processing_fee: toNumber(processingFee),
          underwriting_fee: toNumber(underwritingFee),
          gos_amount: toNumber(effectiveReconResult.gosAmount),
          interest_income: toNumber(effectiveReconResult.interestIncome),
          servicing_interest_income: toNumber(effectiveReconResult.servicingInterestIncome),
          servicing_principal_collected: toNumber(servicingPrincipalCollected),
          fha_ufmip_flow_mode: effectiveFhaUfmipBridge.flowMode,
          fha_ufmip_premium_amount: effectiveFhaUfmipBridge.premiumAmount,
          fha_ufmip_financed_amount: effectiveFhaUfmipBridge.financedAmount,
          fha_ufmip_remitted_date: effectiveFhaUfmipBridge.remittedDate,
          fha_ufmip_remitted_by: effectiveFhaUfmipBridge.remittedBy,
          fha_ufmip_remitted_amount: effectiveFhaUfmipBridge.remittedAmount,
          fha_ufmip_verification_code: effectiveFhaUfmipBridge.verificationCode,
          fha_ufmip_capture_status: effectiveFhaUfmipBridge.captureStatus,
          fha_ufmip_source_signature: effectiveFhaUfmipBridge.sourceSignature,
          fha_ufmip_capture_details: effectiveFhaUfmipBridge.captureDetails,
          fha_ufmip_snapshot_at: new Date().toISOString(),
          fha_ufmip_company_variance: effectiveFhaUfmipBridge.companyVarianceAmount,
          net_profit: toNumber(effectiveReconResult.netProfit),
          total_reimbursable: toNumber(effectiveReconResult.totalReimbursable),
          pricing_source_type: getPricingSourceType(deliveryLockSelection),
          pricing_source_lock_version_id: deliveryLockSelection?.lockVersionId ?? null,
          pricing_source_lock_snapshot_id: deliveryLockSelection?.lockSnapshotId ?? null,
          pricing_source_selection_id: deliveryLockSelection?.id ?? null,
          pricing_source_snapshot: buildDeliveryLockSourceSnapshot(deliveryLockSelection),
          loan_snapshot: {
            ...effectiveLoan,
            ...(effectiveFeeContext ? { banker_recon_fee_accounting: effectiveFeeContext.accounting } : {})
          } as unknown as Record<string, unknown>,
          // LO Profitability snapshot (report-only; payout math unchanged)
          profitability_routing: effectiveProfitabilityRouting,
          lo_profitability_gross: effectiveLoProfitability.gross,
          lo_profitability_net: effectiveLoProfitability.net,
          lo_profitability_components: effectiveLoProfitability.components
        },
        lineItems,
        user.id,
        feeReconUpdatedAtRef.current
      );

      if (result.error || !result.recon) {
        throw new Error(result.error || 'Failed to save');
      }

      const savedReconId = result.recon.id;
      if (saveIsStale()) {
        // Persisted correctly for ITS loan, but the screen has moved on — do not
        // touch the current loan's state or refs, and do NOT return a success id:
        // a stale result must never authorize downstream actions (finalize would
        // continue into payouts/emails against a context that no longer exists).
        log.warn('Save persisted but its loan context is stale — reporting failure to callers', { metadata: { savedReconId } });
        setSaveState('idle');
        toast({
          title: 'Save needs review',
          description: 'The loan changed while saving. The draft was stored, but please reopen the loan to continue.',
          variant: 'destructive'
        });
        return null;
      }
      lastSavedCompensationRef.current = effectiveCompensation;
      feeReconUpdatedAtRef.current = result.recon.updated_at;
      lastSavedTerminalAccountingRef.current = {
        loPayout: toNumber(effectiveReconResult.loPayout),
        companyRevenue: toNumber(effectiveReconResult.companyRevenue),
        gosAmount: toNumber(effectiveReconResult.gosAmount),
        netProfit: toNumber(effectiveReconResult.netProfit)
      };
      // Only after the save SUCCEEDED (mirrors lastSavedCompensationRef) — the
      // post-finalize email must reflect what was actually persisted.
      lastSavedProfitabilityRef.current = effectiveLoProfitability;
      lastSavedFhaUfmipRef.current = effectiveFhaUfmipBridge;
      setTerminalFhaUfmipSnapshot({
        flowMode: effectiveFhaUfmipBridge.flowMode,
        companyVarianceAmount: effectiveFhaUfmipBridge.companyVarianceAmount
      });
      // Publish the persisted values into the snapshot too: when finalize flips
      // the status terminal, the display replays this snapshot — it must be the
      // JUST-SAVED profitability, not the one loaded when the draft was opened.
      setLoadedProfitabilitySnapshot({
        lo_profitability_gross: effectiveLoProfitability.gross,
        lo_profitability_net: effectiveLoProfitability.net,
        lo_profitability_components: effectiveLoProfitability.components
      });

      // Link uploaded documents to this banker_recon record
      if (uploadedDocuments.length > 0) {
        const docIds = uploadedDocuments.map(doc => doc.id).filter(Boolean);
        if (docIds.length > 0) {
          const { error: docError } = await supabase
            .from('loan_documents')
            .update({ banker_recon_id: savedReconId })
            .in('id', docIds);

          if (docError) {
            log.warn('Failed to link documents to recon', { error: docError });
          } else {
            log.info('Linked documents to banker_recon', { count: docIds.length, reconId: savedReconId });
          }
        }
      }

      setReconId(savedReconId);
      setStoredVolumeBonusAmount(toNumber(result.recon.volume_bonus_amount));
      setReconInvestorId(result.recon.investor_id ?? null);
      // Publish the save's normalized snapshot and clear dirty ONLY when no edit
      // landed while this save was in flight — an intervening edit is NOT in the
      // persisted snapshot: it must stay on screen (not be overwritten by the
      // older normalized costs) and remain visibly unsaved.
      if (editRevisionRef.current === editRevisionAtSaveStart) {
        if (status === 'draft') {
          setCosts(effectiveCostsForSave);
          if (effectiveFeeContext) {
            setRevenueRaw(effectiveRevenueForSave);
            setSelectedLoan(effectiveLoan);
            setCompensation(effectiveCompensation);
          }
        }
        setIsDirty(false);
      } else {
        log.warn('Edits landed during the save — keeping current state and the dirty flag', { metadata: { savedReconId } });
      }

      // Flush queued auto-fill review markers now that the applied values are persisted.
      if (pendingReviewMarksRef.current.size > 0) {
        for (const [docId, mark] of pendingReviewMarksRef.current) {
          void bankerReconService.markDocumentAutoFill(docId, { review: mark });
        }
        pendingReviewMarksRef.current.clear();
      }

      // Show success state on button for 2 seconds
      // A new recon has no server preview until it exists. If it qualifies,
      // stop here for review; never finalize an unseen installment automatically.
      const finalVolumePreview = reconId ? savedVolumePreview : await reviewedVolumeBonusForSave(savedReconId);
      setSaveState('success');
      saveTimeoutRef.current = setTimeout(() => setSaveState('idle'), 2000);

      // Finalize's preliminary save stays quiet; Finalize reports its own outcome.
      if (caller !== FINALIZE_SAVE_CALLER) {
        toast({
          title: 'Draft Saved',
          description: `Recon saved for loan ${effectiveLoan.trans_details_loan_number}`
        });
      }
      log.info('Saved banker recon draft', { id: savedReconId });
      return {
        id: savedReconId,
        volumeBonusReviewToken: finalVolumePreview?.review_token,
        volumeBonusInstallment: finalVolumePreview?.applies ? {
          amount: finalVolumePreview.amount,
          number: finalVolumePreview.installment_number!,
          total: finalVolumePreview.loan_count!
        } : undefined,
        updatedAt: result.recon.updated_at,
        fhaUfmipSignature: effectiveFhaUfmipBridge.sourceSignature,
        fhaUfmipFlowMode: effectiveFhaUfmipBridge.flowMode,
        fhaUfmipFinalizationErrors: effectiveFhaUfmipBridge.finalizationErrors
      };
    } catch (error) {
      log.error('Failed to save draft', { loanNumber: selectedLoan.trans_details_loan_number }, error);
      setSaveState('idle');
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
      return null;
    } finally {
      saveInFlightRef.current = false;
      // Settle any realtime lender-credit update deferred during this save with
      // an authoritative re-read. (Inside a finalize the re-reader defers again
      // and the pre-freeze check aborts; the finalize's own unlock settles it.)
      if (pendingLiveCreditRefreshRef.current && !finalizeInFlightRef.current) {
        pendingLiveCreditRefreshRef.current = false;
        void rereadLiveCreditsRef.current?.();
      }
    }
  }, [feeAccounting, feeCaptureError, servicerSelection, selectedLoan, user, keyDates, pricing, revenue, compensation, servicingInterestIncome, servicingPrincipalCollected, deliveryLockSelection, toast, uploadedDocuments, investorPurchaseDate, firstPaymentToInvestor, costs, purchaseAdjustments, wireFromInvestor, wireFromWarehouse, selectedLOId, status, reconId, routingChoices, profitabilityRouting, concessionActivePoints, concessionEnteredDollars, fetchConcessionTruth, retainedClosingFeeBasis, fetchLiveLenderCreditValues, fetchBuydownPassThroughAmount, applyLiveLenderCreditValues, setIsDirty, handleLOChange, fhaUfmipSource, reviewedVolumeBonusForSave, manualAdjustments]);

  // Used by both confirmed discard and a successful save, for clear or switch.
  const finishPendingLoanAction = useCallback(() => {
    if (saveInFlightRef.current || deleteInFlightRef.current || finalizeInFlightRef.current) {
      toast({
        title: 'Operation in progress',
        description: 'Please wait for the current operation to finish before discarding.'
      });
      return;
    }
    if (pendingLoanAction) {
      clearLoanInternal();
      if (pendingLoanAction.type === 'switch') {
        void loadLoanInternal(pendingLoanAction.loan);
      }
      setPendingLoanAction(null);
    }
  }, [pendingLoanAction, clearLoanInternal, loadLoanInternal, toast]);

  const handleSaveAndContinue = useCallback(async () => {
    const editRevision = editRevisionRef.current;
    const saved = await handleSaveDraft();
    if (!saved) {
      return;
    }
    if (editRevisionRef.current !== editRevision) {
      toast({
        title: 'New changes need saving',
        description: 'The draft was saved, but newer changes are still on screen. Save again before leaving.'
      });
      return;
    }
    finishPendingLoanAction();
  }, [handleSaveDraft, finishPendingLoanAction, toast]);

  const handleCancelLoanAction = useCallback(() => {
    if (saveInFlightRef.current || deleteInFlightRef.current || finalizeInFlightRef.current) {
      return;
    }
    setPendingLoanAction(null);
  }, []);

  /**
   * Print summary to PDF
   */
  const handlePrintToPDF = useCallback(() => {
    if (!selectedLoan || loanLoadFailedRef.current) {
      return;
    }

    const isDraftPdf = status === 'draft';
    const pdfCosts = isDraftPdf
      ? normalizeBuydownPassThroughCostsForDraft(costs, getBuydownPassThroughAmountFromLoan(selectedLoan))
      : costs;
    const pdfRevenue = isDraftPdf
      ? normalizeRevenueStateForDraft(revenue, retainedClosingFeeBasis)
      : revenue;
    const pdfLineItems = isDraftPdf
      ? buildLineItemsFromState(pdfCosts, pdfRevenue, compensation, extractedLineTypesRef.current, docOrderLineTypesRef.current, findConventionalMiPremiumFee(selectedLoan?.le_fees, selectedLoan?.trans_details_loan_type)?.payee ?? null, manualAdjustments)
      : builtLineItems;
    const pdfTotalWarehouseCosts = pdfCosts.warehouse.reduce((sum, item) => sum + item.amount, 0);
    const pdfTotalInvestorCosts = pdfCosts.investor.reduce((sum, item) => sum + item.amount, 0);
    const pdfProcessingFee = pdfRevenue.loan.find(r => r.id === 'processing_fee')?.amount || 0;
    const pdfUnderwritingFee = pdfRevenue.loan.find(r => r.id === 'underwriting_fee')?.amount || 0;
    const pdfReconResult = isDraftPdf
      ? computeBankedRecon({
          loanAmount: selectedLoan.trans_details_loan_amt || 0,
          actualPurchasePrice: pricing.actualPricePercent || 0,
          compensationType: compensation.type,
          compensationValue: compensation.value,
          volumeBonusAmount,
          overrides: getActiveOverrides(overrides).map(o => ({ bps: o.bps })),
          processingFee: pdfProcessingFee,
          underwritingFee: pdfUnderwritingFee,
          interestIncome: pdfRevenue.loan.find(r => r.id === 'interest_charged')?.amount || 0,
          servicingInterestIncome,
          flexcloseCredit: pdfCosts.flexcloseCredit,
          loanType: selectedLoan.trans_details_loan_type || null,
          fhaUfmip: fhaUfmipBridge,
          lineItems: pdfLineItems
        })
      : reconResult;
    const pdfTotalLoanRevenue = pdfRevenue.loan.reduce((sum, item) => sum + item.amount, 0);
    const pdfLoPayout = isDraftPdf ? pdfReconResult.loPayout : displayLoPayout;
    const pdfCompanyRevenue = isDraftPdf ? pdfReconResult.companyRevenue : displayCompanyRevenue;
    const pdfGosAmount = isDraftPdf ? pdfReconResult.gosAmount : displayGosAmount;
    const pdfActualPurchaseDollar = isDraftPdf ? pdfReconResult.actualPurchaseDollar : actualPurchaseDollar;
    const pdfNetProfit = isDraftPdf ? pdfReconResult.netProfit : displayNetProfit;
    const pdfTotalReimbursable = isDraftPdf ? pdfReconResult.totalReimbursable : totalReimbursable;
    const pdfTotalRevenueForDisplay = isDraftPdf ? pdfReconResult.totalRevenueForDisplay : displayTotalRevenueForPdf;
    const pdfTotalCostsForDisplay = isDraftPdf ? pdfReconResult.totalCostsForDisplay : displayTotalCostsForPdf;

    downloadBankerReconPDF({
      selectedLoan,
      status,
      keyDates,
      pricing,
      compensation,
      costs: pdfCosts,
      revenue: pdfRevenue,
      lineItems: pdfLineItems,
      fhaUfmipBridge,
      totalWarehouseCosts: pdfTotalWarehouseCosts,
      totalInvestorCosts: pdfTotalInvestorCosts,
      totalLoanRevenue: pdfTotalLoanRevenue,
      totalInvestorRevenue,
      totalRevenueForDisplay: pdfTotalRevenueForDisplay,
      totalCostsForDisplay: pdfTotalCostsForDisplay,
      loPayout: pdfLoPayout,
      volumeBonusAmount: pdfReconResult.volumeBonusAmount,
      volumeBonusInstallment: volumeBonus.preview?.applies && volumeBonus.preview.installment_number && volumeBonus.preview.loan_count
        ? { number: volumeBonus.preview.installment_number, total: volumeBonus.preview.loan_count, allocated: volumeBonus.preview.allocated }
        : undefined,
      companyRevenue: pdfCompanyRevenue,
      gosAmount: pdfGosAmount,
      actualPurchaseDollar: pdfActualPurchaseDollar,
      netProfit: pdfNetProfit,
      totalReimbursable: pdfTotalReimbursable,
      wireFromInvestor,
      wireFromWarehouse,
      servicingPrincipalCollected,
      lenderCreditTimingBasis,
      retainedClosingFeeBasis,
      uploadedDocuments,
      purchaseAdjustments,
      overrides: getActiveOverrides(overrides),
      totalOverrideAmount
    });

    toast({
      title: 'PDF Generated',
      description: 'The reconciliation summary has been downloaded'
    });
  }, [selectedLoan, status, keyDates, pricing, compensation, volumeBonusAmount, volumeBonus.preview, costs, revenue, builtLineItems, totalInvestorRevenue, displayLoPayout, displayCompanyRevenue, displayGosAmount, actualPurchaseDollar, displayNetProfit, totalReimbursable, displayTotalRevenueForPdf, displayTotalCostsForPdf, wireFromInvestor, wireFromWarehouse, servicingInterestIncome, servicingPrincipalCollected, lenderCreditTimingBasis, retainedClosingFeeBasis, uploadedDocuments, purchaseAdjustments, overrides, totalOverrideAmount, reconResult, toast, fhaUfmipBridge, manualAdjustments]);

  /**
   * Finalize the reconciliation and create payouts
   */
  const handleFinalize = useCallback(async () => {
    if (servicerSelectionRequired) {
      toast({ title: 'Cannot Finalize', description: 'Select the servicer on the Servicing tab', variant: 'destructive' });
      return;
    }
    // Reentrancy: one finalize at a time.
    if (finalizeInFlightRef.current) {
      return;
    }
    // Held for the ENTIRE workflow (save → checks → finalizeRecon → publish) —
    // released in the finally below. Loan switch/clear/delete and external
    // saves are blocked while this is set, and the VISIBLE lock (isFinalizing)
    // is raised here too so every draft-mutating surface inerts immediately,
    // not only after the preliminary save and preflight reads.
    finalizeInFlightRef.current = true;
    formWorkflowRef.current += 1;
    setIsFinalizing(true);
    // Loan-generation snapshot: if the screen context somehow changes mid-flow
    // (all UI paths are blocked, so this is defense-in-depth), finalization must
    // neither call finalizeRecon nor publish terminal state.
    const finalizeGeneration = loadRequestIdRef.current;
    const finalizeIsStale = () => loadRequestIdRef.current !== finalizeGeneration;
    // Accounting-input snapshot: any edit after this point is NOT in the
    // snapshot being frozen — finalize must abort before finalizeRecon.
    const editRevisionAtFinalizeStart = editRevisionRef.current;
    try {
    // Defense-in-depth: block finalization if override validation fails
    const currentOverrides = overridesRef.current;
    const activeOvrForGuard = getActiveOverrides(currentOverrides);
    const configuredOvrForGuard = getConfiguredOverrides(currentOverrides);
    const halfFilledGuard = currentOverrides.filter(o =>
      (o.recipientId && (o.bps === null || !Number.isFinite(o.bps)))
      || (!o.recipientId && typeof o.bps === 'number' && Number.isFinite(o.bps) && o.bps > 0)
    );
    if (currentOverrides.length > 3
        || halfFilledGuard.length > 0
        || activeOvrForGuard.reduce((s, o) => s + o.bps, 0) > 60
        || new Set(configuredOvrForGuard.map(o => o.recipientId)).size !== configuredOvrForGuard.length) {
      toast({ title: 'Cannot Finalize', description: 'Fix override validation errors first', variant: 'destructive' });
      return;
    }
    if ((revenue.lenderCredit > 0 && revenue.lenderCreditNetsToWarehouseAdvance === null)
        || (revenue.cureAppliedLenderCredit > 0 && revenue.cureAppliedLenderCreditNetsToWarehouseAdvance === null)) {
      toast({ title: 'Cannot Finalize', description: 'Resolve credit/cure warehouse timing first', variant: 'destructive' });
      return;
    }
    const finalizeCompensationCheck = normalizeLoCompReductionForRecon({
      compensation,
      metadata: loCompReductionImportRef.current,
      reconStatus: 'draft',
      isFreshNoComp: false
    });
    if (finalizeCompensationCheck.status === 'blocked') {
      toast({
        title: 'Cannot Finalize',
        description: 'The saved LO comp reduction conflicts with the compensation percentage. Review LO Compensation before finalizing.',
        variant: 'destructive'
      });
      return;
    }

    // Use existing reconId or save draft to get one
    // IMPORTANT: We capture the returned ID directly to avoid stale closure issues
    let currentReconId = reconId;
    let savedDraft: SavedDraftIdentity | null = null;
    {
      // Always save before finalize to persist all computed values (net_profit, gos_amount, etc.)
      savedDraft = await handleSaveDraft(FINALIZE_SAVE_CALLER);
      if (!savedDraft) {
        // handleSaveDraft already showed error toast
        return;
      }
      currentReconId = savedDraft.id;
    }

    if (savedDraft.fhaUfmipFinalizationErrors.length > 0) {
      toast({
        title: 'Cannot Finalize',
        description: savedDraft.fhaUfmipFinalizationErrors.join(' '),
        variant: 'destructive'
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'User not authenticated',
        variant: 'destructive'
      });
      return;
    }

    // Best-effort check: is the loan currently file-locked in Encompass?
    if (selectedLoan?.encompass_loan_id && user.organization_id) {
      const { data: lockCheck, error: lockError } = await supabase
        .from('encompass_loans')
        .select('is_file_locked, file_locked_by_name')
        .eq('encompass_loan_id', selectedLoan.encompass_loan_id)
        .eq('organization_id', user.organization_id)
        .eq('deleted_from_encompass', false)
        .maybeSingle();

      if (lockError) {
        toast({
          title: 'Unable to verify loan lock status',
          description: 'Could not check if the loan is locked in Encompass. Please try again.',
          variant: 'destructive'
        });
        return;
      }

      // lockCheck is null when no matching row exists (loan deleted/moved since selection).
      // Treat as unlocked — the milestone call will fail on its own if the loan is gone.
      if (lockCheck?.is_file_locked) {
        const lockedBy = lockCheck.file_locked_by_name || 'another user';
        toast({
          title: 'Loan is locked in Encompass',
          description: `${lockedBy} has this loan open. Please ask them to close it before finalizing.`,
          variant: 'destructive'
        });
        return;
      }
    }

    // Preflight source-of-truth read for the Delivery By Date field write at the
    // end of finalize: selectedLoan can be overlaid by delivery-lock fields. Same
    // strict posture as the Encompass fieldWrite it feeds: a query error aborts
    // before the spinner flips. The Investor Loan # copies (996, VEND.X276) are
    // not read here: the milestone function copies Encompass's own 352 when it
    // writes, so a number saved while finalize runs is never overwritten.
    let latestEncompassLockExpires = currentEncompassLockExpires;
    if (selectedLoan?.encompass_loan_id && user.organization_id) {
      const { data: latestLoanRow, error: lockLookupError } = await supabase
        .from('encompass_loans')
        .select('rate_lock_buy_side_lock_expires')
        .eq('encompass_loan_id', selectedLoan.encompass_loan_id)
        .eq('organization_id', user.organization_id)
        .eq('deleted_from_encompass', false)
        .maybeSingle();

      if (lockLookupError) {
        toast({
          title: 'Unable to prepare finalize',
          description: 'Could not read the lock expiration from the Encompass snapshot. Please try again.',
          variant: 'destructive'
        });
        return;
      }
      latestEncompassLockExpires = latestLoanRow?.rate_lock_buy_side_lock_expires || currentEncompassLockExpires;
    }

    log.info('Finalizing banker recon', { reconId: currentReconId });

    // Goodbye-letter outcome the user should see. Only one toast is visible at a
    // time, so an outcome known before the final finalize toast rides in it; a
    // later one gets its own loan-labelled notice.
    const letterLoanNumber = selectedLoan?.trans_details_loan_number ?? null;
    const letterState: { notice: LetterNotice | null; finalToastShown: boolean } = { notice: null, finalToastShown: false };
    const reportLetterOutcome = (outcome: GoodbyeOutcome) => {
      const notice: LetterNotice = { ...outcome, loanNumber: letterLoanNumber };
      // Kept after the final toast too, so an error toast that replaces it still carries it.
      letterState.notice = notice;
      if (!letterState.finalToastShown) {
        return;
      }
      toast({
        title: `Goodbye letter for ${letterLoanNumber || 'this loan'}`,
        description: `${letterNoticeHeadline(notice.state)}. ${notice.text}`,
        variant: 'destructive'
      });
    };
    let finalized = false;

    try {
      // NOTE: no direct lo_user_id write here. The preliminary save above already
      // persisted selectedLOId from this same closure; an id-only header write at
      // this point could mutate a recon another session just froze (and its
      // updated_at bump would defeat finalizeRecon's optimistic condition).

      // Revalidate the loan context before the irreversible step.
      if (finalizeIsStale()) {
        throw new Error('The selected loan changed during finalization. Nothing was finalized — please retry.');
      }

      // Revalidate the accounting inputs: an edit that landed after the
      // preliminary save is NOT in the snapshot about to be frozen.
      if (editRevisionRef.current !== editRevisionAtFinalizeStart) {
        throw new Error('Values changed during finalization. Nothing was finalized — review the changes, save, and finalize again.');
      }

      // A realtime lender-credit update arrived during this workflow: the saved
      // snapshot may predate it. Abort pre-freeze; the unlock below re-reads
      // the authoritative values so nothing is lost.
      if (pendingLiveCreditRefreshRef.current) {
        throw new Error('Lender credit changed in Encompass during finalization. Values were refreshed — review and finalize again.');
      }

      // Create payouts and finalize status (reversible DB operations FIRST)
      const result = await bankerReconService.finalizeRecon(
        currentReconId,
        savedDraft.updatedAt,
        savedDraft.fhaUfmipSignature,
        savedDraft.volumeBonusReviewToken
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to finalize');
      }
      finalized = true;

      // Publish terminal state only into the SAME loan context that finalized.
      if (finalizeIsStale()) {
        log.warn('Finalize succeeded but the loan context changed — skipping state publishes', { metadata: { reconId: currentReconId } });
        toast({
          title: `${letterLoanNumber || 'This loan'} was finalized, but follow-up steps were skipped`,
          description: 'The screen changed during Finalize, so the goodbye letter and the Encompass updates did not run. Send the letter from Servicing and update the loan in Encompass.',
          variant: 'destructive'
        });
        return;
      }

      setStatus('finalized');
      setStoredVolumeBonusAmount(toNumber(result.recon?.volume_bonus_amount));
      void refreshVolumeBonus();
      setTerminalAccounting(lastSavedTerminalAccountingRef.current ?? {
        loPayout,
        companyRevenue,
        gosAmount,
        netProfit
      });

      // Send the RESPA Notice of Servicing Transfer (goodbye letter) to the
      // borrower. Fire-and-forget; the edge function resolves the loan data
      // server-side. Kicked off here (immediately after finalize success)
      // rather than after the LO/override compensation emails below so an
      // unrelated synchronous throw in compensation-email setup can't skip
      // the regulatory borrower notice. (Per plan §7.)
      if (result.servicingLoanId) {
        void supabase.functions.invoke('send-goodbye-letter', {
          body: {
            servicingLoanId: result.servicingLoanId,
            triggeredBy: 'banker_recon_finalize'
          }
        })
          .then(async ({ data: responseData, error }: { data?: GoodbyeSendResult | null; error?: unknown }) => {
            const data = await readGoodbyeSendResult(responseData, error);
            if (!shouldReportGoodbyeOutcome(data)) {
              if (data.skipped) {
                log.info('Goodbye letter skipped', { metadata: { reason: data.reason } });
              }
              return;
            }
            // Raw sender values stay in the log; the user sees plain copy.
            log.warn('Goodbye letter not delivered on finalize', {
              metadata: {
                error: data.error,
                reason: data.reason,
                code: data.code,
                missingFields: data.missingFields,
                transportFailure: data.transportFailure
              }
            });
            const outcome = describeGoodbyeSendResult(data);
            reportLetterOutcome({ ...outcome, text: goodbyeFailureDescription(outcome.text, data) });
          })
          .catch((err: unknown) => {
            // A thrown invocation proves nothing about delivery.
            log.warn('Goodbye letter invoke error', {}, err as Error);
            reportLetterOutcome(describeGoodbyeSendResult({ success: false, transportFailure: true }));
          });
      } else {
        log.warn('Goodbye letter auto-send skipped: no servicingLoanId from finalizeRecon', {
          metadata: { reconId: currentReconId, reason: result.servicingError }
        });
        reportLetterOutcome({ state: 'not_sent', text: result.servicingError ?? 'The servicing record could not be set up.' });
      }

      let persistedOverrideNotifications = activeOvrForGuard;
      const persistedRecon = await bankerReconService.getBankerRecon(currentReconId);
      if (persistedRecon) {
        const persistedOverrides = normalizeDraftOverrides(persistedRecon.overrides);
        persistedOverrideNotifications = getActiveOverrides(
          persistedOverrides.length > 0
            ? persistedOverrides
            : persistedRecon.override_recipient_id && persistedRecon.override_bps && persistedRecon.override_bps > 0
              ? [{
                  id: 'legacy-override',
                  recipientId: persistedRecon.override_recipient_id,
                  recipientName: persistedRecon.override_recipient
                    ? `${persistedRecon.override_recipient.first_name} ${persistedRecon.override_recipient.last_name}`
                    : null,
                  bps: toNumber(persistedRecon.override_bps)
                }]
              : []
        );
      }
      if (selectedLoan?.encompass_loan_id) {
        setFinalizedLoanIds(prev => [...prev, selectedLoan.encompass_loan_id]);
      }
      refreshFinalizedLoanIds();
      log.info('Finalized banker recon', { id: currentReconId });

      // STEP 3: Update Encompass milestones (AFTER successful finalization)
      // These are irreversible external API calls — if they fail, the recon is still
      // finalized and the user gets a warning to update milestones manually.
      let milestonesUpdated = true;
      let folderMovedSuccessfully = false;
      let folderMoveQueued = false;
      let folderMoveError: string | null = null;
      let capturedMilestoneError: string | null = null;
      if (selectedLoan?.encompass_loan_id) {
        try {
          // HMDA/Closing field writes (per Jennifer's mapping request, 2026-04-15).
          // Attached to the first (Funded) milestone step under strict mode so a
          // write failure aborts before milestones advance or the folder moves.
          // Blank sources are skipped — never clobber existing Encompass values.
          const fieldWrites = buildBankerReconFinalizationFieldWrites({
            fundsToEscrow: keyDates.fundsToEscrow,
            fundsReceived: keyDates.fundsReceived,
            investorPurchaseDate,
            firstPaymentDate: selectedLoan.first_payment_date,
            firstPaymentToInvestor,
            deliveryLockSelection,
            currentEncompassLockExpires: latestEncompassLockExpires
          });

          const milestoneSteps: MilestoneStep[] = [
            {
              milestoneName: 'Funded',
              milestoneDate: keyDates.fundsToEscrow ? `${keyDates.fundsToEscrow}T00:00:00Z` : undefined,
              fieldWrites,
              requireFieldWritesSuccess: true,
              syncInvestorLoanCopies: true
            },
            {
              milestoneName: 'Purchased',
              milestoneDate: keyDates.fundsReceived ? `${keyDates.fundsReceived}T00:00:00Z` : undefined
            },
            {
              milestoneName: 'Completion',
              milestoneDate: keyDates.fundsReceived ? `${keyDates.fundsReceived}T00:00:00Z` : undefined,
              moveToFolder: 'Completed Loans',
              sourceType: 'banker_recon',
              sourceId: currentReconId
            }
          ];

          for (const step of milestoneSteps) {
            log.info(`Updating milestone: ${step.milestoneName}`, { date: step.milestoneDate });

            const { data: milestoneData, error: milestoneError } = await supabase.functions.invoke('update-encompass-milestone', {
              body: {
                encompassLoanId: selectedLoan.encompass_loan_id,
                milestoneName: step.milestoneName,
                milestoneDate: step.milestoneDate,
                moveToFolder: step.moveToFolder,
                sourceType: step.sourceType,
                sourceId: step.sourceId,
                fieldWrites: step.fieldWrites,
                requireFieldWritesSuccess: step.requireFieldWritesSuccess,
                syncInvestorLoanCopies: step.syncInvestorLoanCopies
              }
            });

            if (milestoneError) {
              let detail = milestoneError.message;
              try {
                const body = await (milestoneError as { context?: { json?: () => Promise<{ error?: string }> } }).context?.json?.();
                if (body?.error) {
detail = body.error;
}
              } catch { /* response body unavailable */ }
              throw new Error(`Failed to update milestone "${step.milestoneName}": ${detail}`);
            }

            if (milestoneData?.error) {
              throw new Error(milestoneData.error);
            }

            // The milestone function reports the Investor Loan # copies it made.
            // A deployment that predates them would skip the copies silently,
            // so a missing confirmation stops the remaining steps.
            if (step.syncInvestorLoanCopies && !['copied', 'blank'].includes(milestoneData?.investorLoanCopies)) {
              throw new Error('Investor Loan # copies (996, VEND.X276) were not confirmed. Update them in Encompass.');
            }

            // Consume folderMoveStatus from edge function (it now writes loan_folder directly)
            if (milestoneData?.folderMoveStatus === 'moved') {
              folderMovedSuccessfully = true;
              log.info('Loan moved to folder', { folder: step.moveToFolder });
            } else if (milestoneData?.folderMoveStatus === 'queued_retry') {
              folderMoveQueued = true;
              log.info('Folder move queued for retry', { jobId: milestoneData.folderMoveJobId });
            } else if (milestoneData?.folderMoveStatus === 'failed_terminal') {
              folderMoveError = milestoneData.folderError || 'Permanent folder move failure';
              log.warn('Folder move terminal failure', { error: folderMoveError });
            }
          }

          log.info('All milestones updated successfully');
        } catch (milestoneError) {
          milestonesUpdated = false;
          capturedMilestoneError = milestoneError instanceof Error ? milestoneError.message : String(milestoneError);
          log.warn('Milestone update failed after finalization', { reconId: currentReconId }, milestoneError);
        }
      }

      toast(buildFinalizationToast({
        milestonesUpdated,
        folderMovedSuccessfully,
        folderMoveQueued,
        folderMoveError,
        milestoneError: capturedMilestoneError,
        letterNotice: letterState.notice
      }));
      letterState.finalToastShown = true;

      // Send LO compensation breakdown email (fire-and-forget)
      if (selectedLOId && selectedLoan) {
        const lo = loanOfficers.find(o => o.id === selectedLOId);
        const borrowerName = [selectedLoan.borrower_last_name, selectedLoan.borrower_first_name]
          .filter(Boolean).join(', ') || 'N/A';
        const reimbItems = revenue.loan.filter(
          item => !standardFeeIds.includes(item.id) && item.reimbursable
        );
        const emailCompensationResult = normalizeLoCompReductionForRecon({
          compensation: lastSavedCompensationRef.current ?? compensation,
          metadata: loCompReductionImportRef.current,
          reconStatus: 'draft',
          isFreshNoComp: false
        });
        // Route-aware (same as the save path): normalizeLoCompReductionForRecon
        // can re-add the LOCR base-comp deduction; strip it for the email ledger
        // when LOCR is profit-routed so the breakdown email doesn't show a payout
        // deduction the LO never took.
        const emailCompensation = stripProfitabilityRoutedLoCompReduction(
          emailCompensationResult.status === 'blocked'
            ? (lastSavedCompensationRef.current ?? compensation)
            : emailCompensationResult.compensation,
          profitabilityRouting
        );
        const emailBaseCompensation = emailCompensation.type === 'flat_fee'
          ? toNumber(emailCompensation.value)
          : (toNumber(selectedLoan.trans_details_loan_amt) * toNumber(emailCompensation.value)) / 100;
        const ledger = buildLoPayoutLedger({
          loanAmount: selectedLoan.trans_details_loan_amt || 0,
          baseCompensation: emailBaseCompensation,
          deductions: emailCompensation.deductions.map(d => ({ label: d.label, amount: d.deductionAmount })),
          reimbursables: reimbItems.map(r => ({ label: r.description, amount: getLoanRevenueReimbursementBasis(r) })),
          eAndODeduction: 50,
          // The review token was checked by the finalizer. Keep the exact
          // reviewed installment alongside the draft, not a later live preview.
          volumeBonusAmount: savedDraft.volumeBonusInstallment?.amount ?? 0,
          volumeBonusLabel: savedDraft.volumeBonusInstallment
            ? `Volume Bonus — ${savedDraft.volumeBonusInstallment.number} of ${savedDraft.volumeBonusInstallment.total}`
            : undefined
        });

        void supabase.functions.invoke('send-notification', {
          body: {
            userId: selectedLOId,
            notificationType: 'lo_compensation_breakdown',
            encompassLoanId: selectedLoan.encompass_loan_id,
            data: {
              firstName: lo?.firstName,
              loanNumber: selectedLoan.trans_details_loan_number,
              borrowerName,
              loanAmount: selectedLoan.trans_details_loan_amt,
              closingDate: keyDates.closingDate,
              compensationType: emailCompensation.type,
              compensationValue: emailCompensation.value,
              ledger: ledger.lines,
              subtotal: ledger.subtotal,
              subtotalPercent: ledger.subtotalPercent,
              netPayout: ledger.netPayout,
              totalPriceLocked: selectedLoan.rate_lock_total_buy_price || null,
              // LO Profitability scorecard (report-only; NOT part of the payout above).
              // Keep lines with a real effect; plus the zero-effect allocation rows worth
              // explaining: a profit-routed credit the LO kept, and the base-comp LOCR (its
              // release is folded into Gross, so it's $0 here). Base-comp lender credit /
              // cure are omitted — they already appear as payout deductions in the ledger.
              loProfitability: lastSavedProfitabilityRef.current
                ? {
                    gross: lastSavedProfitabilityRef.current.gross,
                    net: lastSavedProfitabilityRef.current.net,
                    components: lastSavedProfitabilityRef.current.components
                      .filter(c => c.effectAmount !== 0 || (c.amount > 0 && (c.target === 'lo_profitability' || c.type === 'lo_comp_reduction')))
                      .map(c => ({
                        type: c.type,
                        label: c.label,
                        target: c.target,
                        amount: c.amount,
                        sourceAmount: c.sourceAmount,
                        points: c.points,
                        effectAmount: c.effectAmount,
                        varianceBasis: c.varianceBasis
                      }))
                  }
                : null
            }
          }
        })
          .then(({ error }: { error?: unknown }) => {
 if (error) {
log.warn('Compensation breakdown email failed', { error });
}
})
          .catch((err: unknown) => log.warn('Compensation breakdown email send error', {}, err));

      }

      // Send override recipient emails (one per active override)
      if (selectedLoan) {
        const overrideBorrowerName = [selectedLoan.borrower_last_name, selectedLoan.borrower_first_name]
          .filter(Boolean).join(', ') || 'N/A';
        const loForOverride = selectedLOId ? loanOfficers.find(o => o.id === selectedLOId) : null;

        for (const override of persistedOverrideNotifications) {
          const ovrAmount = (selectedLoan.trans_details_loan_amt || 0) * override.bps / 10000;
          if (ovrAmount <= 0) {
continue;
}
          // Use recipientName from the override entry (set at selection time, works for LOs + LOAs + admins)
          const recipientFirstName = override.recipientName?.split(' ')[0] || undefined;

          void supabase.functions.invoke('send-notification', {
            body: {
              userId: override.recipientId,
              notificationType: 'override_compensation_breakdown',
              encompassLoanId: selectedLoan.encompass_loan_id,
              data: {
                firstName: recipientFirstName,
                loanNumber: selectedLoan.trans_details_loan_number,
                borrowerName: overrideBorrowerName,
                loanAmount: selectedLoan.trans_details_loan_amt,
                closingDate: keyDates.closingDate,
                totalPriceLocked: selectedLoan.rate_lock_total_buy_price || null,
                overrideBps: override.bps,
                overrideAmount: ovrAmount,
                loanOfficerName: loForOverride ? `${loForOverride.firstName} ${loForOverride.lastName}` : 'N/A'
              }
            }
          })
            .then(({ error }: { error?: unknown }) => {
              if (error) {
                log.warn('Override compensation email failed', { error });
              }
            })
            .catch((err: unknown) => log.warn('Override compensation email send error', {}, err));
        }
      }
    } catch (error) {
      log.error('Failed to finalize', { reconId: currentReconId }, error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      // A step after a successful finalize threw: keep any known letter outcome visible,
      // even if the completion toast already showed it (this toast replaces that one).
      const knownLetterNotice = finalized ? letterState.notice : null;
      toast({
        title: 'Finalize Failed',
        description: knownLetterNotice ? `${message} ${formatLetterNotice(knownLetterNotice)}` : message,
        variant: 'destructive'
      });
      letterState.finalToastShown = true;
    }
    } finally {
      // Release the whole-workflow locks (acquired at entry): the ref that
      // gates other mutations and the visible state that inerts the UI.
      finalizeInFlightRef.current = false;
      setIsFinalizing(false);
      // Settle any realtime lender-credit update deferred during the workflow.
      if (pendingLiveCreditRefreshRef.current) {
        pendingLiveCreditRefreshRef.current = false;
        void rereadLiveCreditsRef.current?.();
      }
    }
  }, [
    servicerSelectionRequired,
    reconId,
    user,
    handleSaveDraft,
    toast,
    selectedLoan,
    keyDates,
    selectedLOId,
    loanOfficers,
    revenue,
    compensation,
    profitabilityRouting,
    loPayout,
    companyRevenue,
    gosAmount,
    netProfit,
    refreshFinalizedLoanIds,
    investorPurchaseDate,
    standardFeeIds,
    firstPaymentToInvestor,
    deliveryLockSelection,
    currentEncompassLockExpires,
    refreshVolumeBonus
  ]);

  // The live encompass_loans row — the same source handleViewFinalizedRecon loads.
  const fetchLatestEncompassLoan = useCallback(async (encompassLoanId: string): Promise<EncompassLoan> => {
    const { data, error } = await supabase
      .from('encompass_loans')
      .select('*')
      .eq('encompass_loan_id', encompassLoanId)
      .single();
    if (error || !data) {
      throw new Error(error?.message || 'Loan data could not be loaded.');
    }
    return data as EncompassLoan;
  }, []);

  /**
   * Reopen a finalized recon and reload it in place — no page refresh. The reload
   * runs the same loader as any loan load (fresh Encompass row, strict reads, the
   * reopened draft confirmed first), so the save timestamp and every draft value
   * are current. Mutations stay locked until it succeeds; on failure they stay
   * locked and the user is told to refresh.
   */
  const handleUnfinalize = useCallback(async () => {
    if (!reconId || !user?.id || !selectedLoan || finalizeInFlightRef.current) {
      return;
    }
    const reopenedReconId = reconId;
    const reopenedLoanId = selectedLoan.encompass_loan_id;
    const reopenGeneration = loadRequestIdRef.current;

    // The same lock Finalize holds: blocks loan switch/clear, draft delete, outside
    // saves and Finalize, and defers realtime credit updates until the reload settles.
    finalizeInFlightRef.current = true;
    formWorkflowRef.current += 1;
    setIsFinalizing(true);

    try {
      const result = await bankerReconService.unfinalizeRecon(reopenedReconId, user.id);
      if (!result.success) {
        throw new Error(result.error || 'Failed to reopen');
      }
    } catch (error) {
      log.error('Failed to un-finalize', { metadata: { reconId: reopenedReconId } }, error as Error);
      toast({
        title: 'Reopen Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
      finalizeInFlightRef.current = false;
      setIsFinalizing(false);
      return;
    }

    setFinalizedLoanIds(prev => prev.filter(id => id !== reopenedLoanId));
    refreshFinalizedLoanIds();

    let reloaded = false;
    try {
      if (loadRequestIdRef.current !== reopenGeneration) {
        throw new Error('The loan changed during Reopen.');
      }
      const latestLoan = await fetchLatestEncompassLoan(reopenedLoanId);
      if (loadRequestIdRef.current !== reopenGeneration) {
        throw new Error('The loan changed during Reopen.');
      }
      const outcome = await loadLoanInternal(latestLoan, false, {
        reloadAfterReopen: { expectedReconId: reopenedReconId }
      });
      reloaded = outcome === 'loaded'
        && loadRequestIdRef.current === reopenGeneration + 1
        && feeReconUpdatedAtRef.current !== null;
      if (!reloaded) {
        throw new Error('The reopened reconciliation did not finish loading.');
      }
    } catch (error) {
      log.error('Reopened, but the reload failed', { metadata: { reconId: reopenedReconId } }, error as Error);
    }

    if (!reloaded) {
      // Keep every mutation locked: the screen may show reset or stale values.
      toast({
        title: "Reopened, but the latest data didn't load",
        description: 'Refresh the page to continue.',
        variant: 'destructive'
      });
      return;
    }

    // A fee capture that changed while the screen was locked gets the normal draft warning.
    const deferredCapture = deferredFeeCaptureRef.current;
    deferredFeeCaptureRef.current = null;
    const reloadedFeeContext = feeContextRef.current;
    if (deferredCapture && reloadedFeeContext && (
      deferredCapture.status !== 'complete'
      || deferredCapture.signature !== reloadedFeeContext.accounting.capture.signature
    )) {
      setFeeCaptureError('Fee sources changed. Save Draft to refresh and review the updated amounts.');
    }

    finalizeInFlightRef.current = false;
    setIsFinalizing(false);
    // Settle any realtime lender-credit update deferred during the reload (as Finalize does).
    if (pendingLiveCreditRefreshRef.current) {
      pendingLiveCreditRefreshRef.current = false;
      void rereadLiveCreditsRef.current?.();
    }
    toast({
      title: 'Reconciliation Reopened',
      description: 'Status reverted to draft. Ordinary payouts will be recreated; any allocated volume bonus and its payments remain attached.'
    });
  }, [reconId, user, selectedLoan, toast, refreshFinalizedLoanIds, fetchLatestEncompassLoan, loadLoanInternal]);

  // ============================================================================
  // FINALIZED RECORDS
  // ============================================================================

  // Fetch finalized recons when switching to finalized view
  useEffect(() => {
    if (viewMode !== 'finalized' || !user?.organization_id) {
      return;
    }

    const fetchFinalized = async () => {
      setFinalizedReconsLoading(true);
      try {
        const recons = await bankerReconService.listBankerRecons(user.organization_id, 'finalized');
        setFinalizedRecons(recons);
      } catch (error) {
        log.error('Failed to fetch finalized recons', {}, error as Error);
        toast({ title: 'Failed to load finalized records', variant: 'destructive' });
      } finally {
        setFinalizedReconsLoading(false);
      }
    };

    fetchFinalized();
  }, [viewMode, user?.organization_id, toast]);

  // View a finalized recon by loading it into the form
  const handleViewFinalizedRecon = useCallback(async (recon: BankerRecon) => {
    // Fetch the EncompassLoan record to pass to loadLoanInternal
    let encompassLoan: EncompassLoan;
    try {
      encompassLoan = await fetchLatestEncompassLoan(recon.encompass_loan_id);
    } catch (error) {
      toast({
        title: 'Failed to load loan data',
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive'
      });
      return;
    }

    setViewMode('new');
    // Small delay to ensure view mode switch renders before loading
    setTimeout(() => {
      handleLoanSelect(encompassLoan);
    }, 0);
  }, [toast, handleLoanSelect, fetchLatestEncompassLoan]);

  // ============================================================================
  // RENDER
  // ============================================================================

  // While a save or the finalize workflow persists a snapshot, EVERY
  // draft-mutating surface is inerted (native disable via fieldset + pointer
  // events off) so an operator can't edit accounting inputs that the in-flight
  // request would then silently exclude from the persisted/frozen snapshot.
  const draftMutationBusy = saveState === 'saving' || isFinalizing || loanLoadFailed;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Banker Reconciliation</h1>
            <div className="flex items-center rounded-lg border bg-muted p-1">
              <Button
                variant={viewMode === 'new' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-3 text-xs"
                onClick={() => setViewMode('new')}
              >
                New Reconciliation
              </Button>
              <Button
                variant={viewMode === 'finalized' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-3 text-xs"
                onClick={() => setViewMode('finalized')}
              >
                Finalized Records
              </Button>
            </div>
          </div>

          {/* Loan Search Bar - only in new recon mode */}
          {viewMode === 'new' && (
            <div className="mt-4 flex items-center gap-4">
              <div ref={loanSearchRef} className="flex-1 max-w-md">
                {user?.organization_id && finalizedIdsLoaded ? (
                  <LoanAutocomplete
                    organizationId={user.organization_id}
                    onSelect={handleLoanSelect}
                    selectedLoan={selectedLoan}
                    onClear={handleLoanClear}
                    channel="Banked - Retail"
                    includeCompleted={true}
                    requireLoanNumber={true}
                    excludeLoanIds={finalizedLoanIds}
                  />
                ) : user?.organization_id && finalizedIdsFailed ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    <span>Failed to load loan filter.</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-sm"
                      onClick={refreshFinalizedLoanIds}
                    >
                      Retry
                    </Button>
                  </div>
                ) : user?.organization_id ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading...</span>
                  </div>
                ) : null}
              </div>
              {/* Draft indicator, delete button, and print PDF */}
              <div className="flex items-center gap-2">
                {reconId && status === 'draft' && (
                  <>
                    <Badge variant="secondary" className="text-xs">
                      Draft loaded
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-destructive hover:text-destructive"
                      onClick={handleDeleteDraft}
                      disabled={loanLoadFailed || volumeBonus.preview?.allocated || storedVolumeBonusAmount > 0}
                      title={volumeBonus.preview?.allocated || storedVolumeBonusAmount > 0 ? 'This loan has an allocated volume bonus obligation and cannot be deleted.' : undefined}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete Draft
                    </Button>
                  </>
                )}
                {selectedLoan && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs gap-1"
                    onClick={handlePrintToPDF}
                    disabled={loanLoadFailed}
                  >
                    <FileDown className="h-3 w-3" />
                    Print PDF
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">

        {/* Finalized Records Table */}
        {viewMode === 'finalized' && (
          <FinalizedReconsTable
            recons={finalizedRecons}
            loading={finalizedReconsLoading}
            onViewRecon={handleViewFinalizedRecon}
          />
        )}

        {/* Reconciliation Form - only in new recon mode */}
        {viewMode === 'new' && <>

        {loanLoadFailed && (
          <div role="alert" className="mb-6 rounded-lg border border-destructive bg-card p-4 text-sm text-destructive">
            Reconciliation could not be loaded. Refresh the page before editing or saving.
            {' '}Saved entries have not been changed.
          </div>
        )}

        {/* Validation Summary */}
        {validationIssues.length > 0 && (
          <Card className={cn(
            "border-2 mb-6",
            validationIssues.some(i => i.type === 'error') ? "border-red-300 bg-red-50" :
            validationIssues.some(i => i.type === 'warning') ? "border-amber-300 bg-amber-50" :
            "border-blue-300 bg-blue-50"
          )}>
            <CardContent className="py-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className={cn(
                  "h-5 w-5 mt-0.5",
                  validationIssues.some(i => i.type === 'error') ? "text-red-500" :
                  validationIssues.some(i => i.type === 'warning') ? "text-amber-500" :
                  "text-blue-500"
                )} />
                <div className="space-y-1">
                  {validationIssues.map((issue, idx) => (
                    <p key={idx} className={cn(
                      "text-sm",
                      issue.type === 'error' ? "text-red-700" :
                      issue.type === 'warning' ? "text-amber-700" :
                      "text-blue-700"
                    )}>
                      {issue.message}
                    </p>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Banker Recon tabs */}
        <Tabs defaultValue="summary" className="w-full">
          <TabsList className="grid h-auto w-full grid-cols-4 gap-1 mb-6">
            <TabsTrigger value="summary" className="min-w-0 whitespace-normal px-1 py-2 text-xs sm:text-sm">Summary</TabsTrigger>
            <TabsTrigger value="warehouse" className="min-w-0 whitespace-normal px-1 py-2 text-xs sm:text-sm">Warehouse Reconciliation</TabsTrigger>
            <TabsTrigger value="investor" className="min-w-0 whitespace-normal px-1 py-2 text-xs sm:text-sm">Investor Purchase Advice</TabsTrigger>
            <TabsTrigger value="servicing" className="min-w-0 whitespace-normal px-1 py-2 text-xs sm:text-sm">Servicing</TabsTrigger>
          </TabsList>

          {/* Tab 1: Summary */}
          <TabsContent value="summary" className="space-y-6">
            <fieldset
              disabled={draftMutationBusy}
              className={cn('min-w-0 w-full', draftMutationBusy && 'pointer-events-none opacity-60')}
            >
            <BankerReconSummary
              volumeBonusPreview={volumeBonus.preview}
              volumeBonusLoading={volumeBonus.loading}
              volumeBonusError={volumeBonus.error}
              onRefreshVolumeBonus={() => void volumeBonus.refresh()}
              selectedLoan={selectedLoan}
              status={status}
              reconResult={reconResult}
              lineItems={builtLineItems}
              fhaUfmipBridge={fhaUfmipBridge}
              keyDates={keyDates}
              pricing={pricing}
              compensation={compensation}
              setCompensation={setCompensation}
              loCompReductionMetadata={loCompReductionMetadata}
              costs={costs}
              revenue={revenue}
              setRevenue={setRevenueForDraft}
              manualAdjustments={manualAdjustments}
              setManualAdjustments={setManualAdjustments}
              totalWarehouseCosts={totalWarehouseCosts}
              totalInvestorCosts={totalInvestorCosts}
              totalLoanRevenue={totalLoanRevenue}
              totalInvestorRevenue={totalInvestorRevenue}
              loPayout={displayLoPayout}
              companyRevenue={displayCompanyRevenue}
              gosAmount={displayGosAmount}
              actualPurchaseDollar={actualPurchaseDollar}
              netProfit={displayNetProfit}
              servicingInterestIncome={servicingInterestIncome}
              servicingPrincipalCollected={servicingPrincipalCollected}
              isSaving={isSaving}
              isFinalizing={isFinalizing}
              saveSuccess={saveSuccess}
              onSaveDraft={handleSaveDraft}
              onFinalize={handleFinalize}
              servicerSelectionRequired={servicerSelectionRequired}
              onUnfinalize={handleUnfinalize}
              loanOfficers={loanOfficers}
              loadingOfficers={loadingOfficers}
              selectedLOId={selectedLOId}
              onLOChange={handleLOChange}
              overrides={overrides}
              totalOverrideAmount={totalOverrideAmount}
              onOverridesChange={(newOverrides) => {
                setOverrides(newOverrides);
                setIsDirty(true);
              }}
              overrideRecipientOptions={allActiveUsers}
              validationIssues={validationIssues}
              setIsDirty={setIsDirty}
              purchaseAdjustments={purchaseAdjustments}
              uploadedDocuments={uploadedDocuments}
              wireFromInvestor={wireFromInvestor}
              setWireFromInvestor={setWireFromInvestor}
              wireFromWarehouse={wireFromWarehouse}
              setWireFromWarehouse={setWireFromWarehouse}
              totalReimbursable={totalReimbursable}
              lenderCreditTimingBasis={lenderCreditTimingBasis}
              retainedClosingFeeBasis={retainedClosingFeeBasis}
              feeAccounting={feeAccounting}
              loProfitability={loProfitability ?? undefined}
              concessionsUnavailable={!isTerminalRecon && concessionTruth === 'error'}
              profitabilityRouting={profitabilityRouting}
              onProfitabilityRoutingChange={handleProfitabilityRoutingChange}
              availableProfitability={availableProfitability}
            />
            </fieldset>
          </TabsContent>

          {/* Tab 2: Warehouse Reconciliation */}
          <TabsContent value="warehouse" className="space-y-6">
            <fieldset
              disabled={draftMutationBusy}
              className={draftMutationBusy ? 'pointer-events-none opacity-60' : undefined}
            >
            <WarehouseReconTab
              selectedLoan={selectedLoan}
              user={user}
              keyDates={keyDates}
              setKeyDates={setKeyDates}
              costs={costs}
              setCosts={setCosts}
              uploadedDocuments={uploadedDocuments}
              setUploadedDocuments={setUploadedDocuments}
              totalWarehouseCosts={totalWarehouseCosts}
              setIsDirty={setIsDirty}
              onSaveDraft={handleSaveDraft}
              isSaving={isSaving}
              isDirty={isDirty}
              saveSuccess={saveSuccess}
              onDocumentReady={handleDocumentReady}
            />
            </fieldset>
          </TabsContent>

          {/* Tab 3: Investor Purchase Advice */}
          <TabsContent value="investor" className="space-y-6">
            <fieldset
              disabled={draftMutationBusy}
              className={draftMutationBusy ? 'pointer-events-none opacity-60' : undefined}
            >
            <InvestorAdviceTab
              selectedLoan={selectedLoan}
              user={user}
              pricing={pricing}
              setPricing={setPricing}
              costs={costs}
              setCosts={setCosts}
              revenue={revenue}
              setRevenue={setRevenueForDraft}
              uploadedDocuments={uploadedDocuments}
              setUploadedDocuments={setUploadedDocuments}
              totalInvestorCosts={totalInvestorCosts}
              totalInvestorRevenue={totalInvestorRevenue}
              setIsDirty={setIsDirty}
              onSaveDraft={handleSaveDraft}
              isSaving={isSaving}
              isDirty={isDirty}
              saveSuccess={saveSuccess}
              investorPurchaseDate={investorPurchaseDate}
              setInvestorPurchaseDate={setInvestorPurchaseDate}
              firstPaymentToInvestor={firstPaymentToInvestor}
              setFirstPaymentToInvestor={setFirstPaymentToInvestor}
              purchaseAdjustments={purchaseAdjustments}
              setPurchaseAdjustments={setPurchaseAdjustments}
              onDocumentReady={handleDocumentReady}
            />
            </fieldset>
          </TabsContent>
          <TabsContent value="servicing" className="space-y-6">
            <fieldset disabled={draftMutationBusy} className={draftMutationBusy ? 'pointer-events-none opacity-60' : undefined}>
              {selectedLoan && <ServicingTab selectedLoan={selectedLoan} recon={{ id: reconId, investor_id: reconInvestorId, status, ...servicerSelection }} canEdit={status === 'draft'} setIsDirty={setIsDirty} setSelection={setServicerSelection} servicingLoanId={servicingRecordId} beforeCheck={async () => Boolean(await handleSaveDraft())} />}
            </fieldset>
          </TabsContent>
        </Tabs>
        </>}
      </div>

      {/* Document auto-fill review modal */}
      {autoFillReview && (
        <AutoFillReviewDialog
          open={!!autoFillReview}
          documentLabel={autoFillReview.documentLabel}
          classificationConfidence={autoFillReview.classificationConfidence}
          rows={autoFillReview.rows}
          onApply={handleAutoFillApply}
          onCancel={handleAutoFillCancel}
          onSkip={handleAutoFillSkip}
        />
      )}

      {/* Unsaved Changes Modal */}
      <AlertDialog open={pendingLoanAction !== null} onOpenChange={(open) => {
        if (!open) {
          handleCancelLoanAction();
        }
      }}>
        <AlertDialogContent onCloseAutoFocus={(event) => {
          event.preventDefault();
          loanSearchRef.current?.querySelector<HTMLInputElement>('input')?.focus();
        }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to the current reconciliation. Would you like to save before
              {pendingLoanAction?.type === 'clear' ? ' clearing the selection?' : ' switching loans?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelLoanAction} disabled={isSaving || isFinalizing}>
              Cancel
            </AlertDialogCancel>
            <Button variant="outline" onClick={finishPendingLoanAction} disabled={draftMutationBusy}>
              Discard Changes
            </Button>
            <Button onClick={handleSaveAndContinue} disabled={draftMutationBusy}>
              {isSaving ? 'Saving...' : pendingLoanAction?.type === 'clear' ? 'Save & Clear' : 'Save & Switch'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default BankerRecon;
